import { Link } from '@tanstack/react-router';
import {
    Bug,
    Check,
    ChevronDown,
    ChevronsRight,
    Clock,
    Hash,
    Link2,
    List,
    MoreVertical,
    Plus,
    RotateCcw,
    Wrench,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { AgentModelPicker } from '@apps/web/components/agent_model_picker';
import {
    AgentSidebarEmptyStage,
    AgentSidebarEmptyState,
    AgentSidebarStarterRows,
} from '@apps/web/components/agent_sidebar_empty_state';
import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { CaregiverShiftDetailsDialog } from '@apps/web/components/caregivers/caregiver_shift_details_dialog';
import { ClientDetailModal } from '@apps/web/components/clients/client_detail_modal';
import {
    MessageFeedbackControls,
    type MessageFeedbackLabel,
} from '@apps/web/components/message_feedback_controls';
import { RestartAnalysisConfirmModal } from '@apps/web/components/outreaches/restart_analysis_confirm_modal';
import { ActivityReferenceTray } from '@apps/web/components/phoebe_chat/activity_reference_tray';
import {
    type AssistantPromptAction,
    type ReferenceData,
    type ShiftActionPolicy,
    type ShiftTab,
} from '@apps/web/components/phoebe_chat/assistant_response_types';
import {
    ChatInput,
    type ChatInputHandle,
} from '@apps/web/components/phoebe_chat/chat_input';
import { PhoebeChatOrganizationScopeContext } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { ChatViewerPresence } from '@apps/web/components/phoebe_chat/chat_viewer_presence';
import {
    buildTranscriptFromPhoebeEvents,
    formatAgentTraceTextContent,
    getLatestLiveAssistantMessageId,
    getLatestLiveThoughtId,
    getOptionalString,
    getReferencesFromRawJson,
    getStringArray,
    hasPendingRecommendationBatch,
    isLegacySlackMessageXml,
    isOptimisticThinkingHandoffEvent,
    isQueuedUserMessageTimelineEntry,
    isSyntheticCalloutKickoffMessage,
    type LegacyOrderedItem,
    mergeLegacyTailAfterEventFeedStart,
    parseLegacySlackMessageInput,
    type PhoebeFooterStatus,
    projectCompactedLegacyItems,
} from '@apps/web/components/phoebe_chat/event_transcript';
import { ExtensionSurfaceProvider } from '@apps/web/components/phoebe_chat/extension_surface_context';
import {
    type MonitoringActivityControls,
    type MonitoringConfigPayload,
} from '@apps/web/components/phoebe_chat/monitoring_configuration_card';
import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import { ReferenceTray } from '@apps/web/components/phoebe_chat/reference_tray';
import { stagedReferenceToReferenceData } from '@apps/web/components/phoebe_chat/staged_reference_data';
import {
    type AgentConversationStreamData,
    type ConversationStreamState,
    type LiveRunStatus,
    type ProviderErrorBadgeState,
    type StreamRunNode,
    type StreamRunState,
    type SubagentDraftProgress,
} from '@apps/web/components/phoebe_chat/streaming_types';
import {
    getUserQuestions,
    type QuestionAnswerPayload,
} from '@apps/web/components/phoebe_chat/user_question_model';
import { PhoebeChatDebugPanelToggle } from '@apps/web/components/phoebe_chat_debug_sidebar';
import {
    buildPhoebeChatDebugSnapshot,
    type PhoebeChatDebugSnapshot,
} from '@apps/web/components/phoebe_chat_debug_snapshot';
import { SaveNoteArtifactCard } from '@apps/web/components/phoebe_chat_view/artifacts/save_note_artifact_card';
import { SavedNoteOutcomesProvider } from '@apps/web/components/phoebe_chat_view/artifacts/saved_note_outcomes_provider';
import { collectStructuredReportArtifactsFromRun } from '@apps/web/components/phoebe_chat_view/artifacts/structured_report_artifacts';
import {
    closeAllArtifactTabs,
    StructuredReportArtifactPill,
} from '@apps/web/components/phoebe_chat_view/artifacts/structured_report_artifacts_panel';
import { UpdatePlaybookArtifactCard } from '@apps/web/components/phoebe_chat_view/artifacts/update_playbook_artifact_card';
import {
    collectUpdatePlaybookArtifactsFromRun,
    isRuntimeContextRawJson,
} from '@apps/web/components/phoebe_chat_view/artifacts/update_playbook_artifacts';
import { EntityDetailClickProvider } from '@apps/web/components/phoebe_chat_view/assistant_responses/entity_detail_click_context';
import { buildShiftSuggestionTabs } from '@apps/web/components/phoebe_chat_view/assistant_responses/shift_suggestion_tabs';
import { ChangesPanel } from '@apps/web/components/phoebe_chat_view/changes/changes_panel';
import { CHAT_COLUMN_CLASS } from '@apps/web/components/phoebe_chat_view/chat_layout';
import {
    ChatModeSelector,
    type NewChatRunType,
    NewChatRunTypePicker,
} from '@apps/web/components/phoebe_chat_view/chat_mode_selector';
import { mapAdminTraceToConversation } from '@apps/web/components/phoebe_chat_view/conversation/admin_trace_adapter';
import {
    type ChatBackendKind,
    type ChatMessageOrigin,
    configureRunOutreachMonitoring,
    createChatBackendConversation,
    decideChatBackendToolCalls,
    ensureOutreachAnalysisRun,
    ensureOutreachRunnerRun,
    getEffectiveChatBackendKind,
    getInitialChatBackendKind,
    interruptChatBackendRun,
    isOutreachRunnerLinkChangedError,
    resetLegacyAgentConversation,
    resetOutreachRunnerRun,
    saveMessageFeedback,
    saveRunFeedback,
    sendChatBackendMessage,
    stopRunOutreachMonitoring,
    submitRunQuestionAnswer,
    updateChatBackendModel,
} from '@apps/web/components/phoebe_chat_view/conversation/chat_backend_adapter';
import {
    isDefaultChatTitle,
    visibleChatTitle,
} from '@apps/web/components/phoebe_chat_view/conversation/chat_title';
import { captureConversationMessageSent } from '@apps/web/components/phoebe_chat_view/conversation/message_send_analytics';
import { type DynamicWidgetActionRequest } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import {
    ASK_PHOEBE_DIAGNOSTIC_MESSAGE,
    type DynamicWidgetDiagnosticRequest,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_diagnostic';
import { dynamicWidgetInspectorStore } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_inspector_store';
import {
    type DynamicWidgetSupportActions,
    DynamicWidgetSupportActionsContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_support_context';
import { DynamicWidgetBuildContextContext } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widgets_context';
import { useTranscriptScroll } from '@apps/web/components/phoebe_chat_view/hooks/use_transcript_scroll';
import { useVoiceRecording } from '@apps/web/components/phoebe_chat_view/hooks/use_voice_recording';
import { InternalReadonlyBadge } from '@apps/web/components/phoebe_chat_view/internal_readonly_badge';
import {
    MessageBubble,
    PendingMessagePill,
} from '@apps/web/components/phoebe_chat_view/messages/message_bubble';
import {
    LiveStatusRow,
    PhoebeFooterStatusRow,
} from '@apps/web/components/phoebe_chat_view/messages/status_rows';
import {
    RunDebriefCard,
    type RunDebriefPromptContent,
    type RunDebriefSubmission,
} from '@apps/web/components/phoebe_chat_view/run_debrief_card';
import { parseTimestampMs } from '@apps/web/components/phoebe_chat_view/run_status/duration_format';
import {
    deriveLiveStatusFromRun,
    deriveManualLiveStatusFromRunState,
    derivePhoebeFooterStatus,
    hasRenderableLiveRunTreeContent,
    shouldRenderCompletedRunWrapper,
} from '@apps/web/components/phoebe_chat_view/run_status/live_status';
import {
    deriveChatThinkingTipIndex,
    derivePhoebeShimmerLabel,
    derivePhoebeThinkingTip,
    PhoebeShimmerStatus,
} from '@apps/web/components/phoebe_chat_view/run_status/shimmer_status';
import { buildStreamRunStateFromPersistedTurns } from '@apps/web/components/phoebe_chat_view/streaming/persisted_turn_run_state';
import {
    createStreamRunState,
    EMPTY_STREAM_STATE,
    getDisplayStreamRun,
    resolveStreamOverlay,
    upsertStreamRunNode,
} from '@apps/web/components/phoebe_chat_view/streaming/stream_run_state';
import {
    buildPhoebeAgentRenderedTimelineEntries,
    sortTimelineEntriesChronologically,
} from '@apps/web/components/phoebe_chat_view/timeline/build_timeline_entries';
import {
    GeneralOutreachProgressCard,
    type GeneralOutreachProgressSeed,
    getGeneralOutreachProgressSeedFromTool,
    getLatestMonitoringRun,
    LiveRunTree,
    MonitoringConfigurationCardForRun,
    PhoebeAgentTimelineEntries,
} from '@apps/web/components/phoebe_chat_view/timeline/transcript_rendering';
import { humanizeReportToolName } from '@apps/web/components/phoebe_chat_view/tool_calls/infrastructure_tool_ui';
import {
    isMonitoringToolEntry,
    shouldRenderToolEntryTopLevel,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_entry_predicates';
import { isUserInputToolName } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_name_constants';
import { adaptTranscriptForLegacy } from '@apps/web/components/phoebe_chat_view/transcript/adapt_transcript';
import {
    type AgentConversationTurnRead,
    buildOptimisticAgentConversationTurn,
    getOrganizationAgentMessageCreateResponseAction,
    type OrganizationAgentMessageCreateResponse,
} from '@apps/web/components/phoebe_chat_view/transcript/legacy_transcript';
import {
    buildStreamingTranscript,
    buildToolExecutionEntry,
    extractReasoning,
} from '@apps/web/components/phoebe_chat_view/transcript/streaming_transcript';
import {
    type MessageEntry,
    type OptimisticThinkingMarker,
    type PhoebeChatMode,
    type PhoebeChatTarget,
    type PhoebeConversationSource,
    type RenderTimelineEntry,
    type RunDebriefAcknowledgementEntry,
    type SaveNoteArtifactEntry,
    type StructuredReportArtifactEntry,
    type ThoughtEntry,
    type ToolApprovalDecision,
    type ToolApprovalDecisionTarget,
    type ToolApprovalScope,
    type ToolAwareItem,
    type ToolExecutionEntry,
    type TranscriptEntry,
    type UpdatePlaybookArtifactEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { shouldPollPhoebeRunEvents } from '@apps/web/components/phoebe_chat_view_polling';
import { resolveSelectedConversationId } from '@apps/web/components/phoebe_chat_view_selection';
import { VOICE_INPUT_PERMISSION_ERROR_MESSAGE } from '@apps/web/components/shared/messages/voice_input_utils';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from '@apps/web/components/ui/context_menu';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { ScrollToBottomButton } from '@apps/web/components/ui/scroll_to_bottom_button';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';
import { DisplayNamesLastFirstContext } from '@apps/web/context/display_names_last_first_context';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useUI } from '@apps/web/context/ui_provider';
import {
    ReviewMyRunsCard,
    TrainingModeBadge,
} from '@apps/web/features/teach_phoebe';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { useOnModeChange } from '@apps/web/hooks/use_on_mode_change';
import { useOrchestratorProductChatEnabled } from '@apps/web/hooks/use_orchestrator_product_chat';
import { type OrganizationEvent } from '@apps/web/hooks/use_organization_events';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { usePhoebeAgentRunEventStream } from '@apps/web/hooks/use_phoebe_agent_run_event_stream';
import { usePhoebeChatPresence } from '@apps/web/hooks/use_phoebe_chat_presence';
import {
    formatChatTypingLabel,
    usePhoebeChatTyping,
} from '@apps/web/hooks/use_phoebe_chat_typing';
import { useRunUploadAttachments } from '@apps/web/hooks/use_run_upload_attachments';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { useTrainingMode } from '@apps/web/hooks/use_training_mode';
import { setActiveAgentConversation } from '@apps/web/lib/active_agent_conversation';
import {
    activityHandoffStore,
    buildActivityHandoffContextKey,
} from '@apps/web/lib/activity_handoff_store';
import { toast } from '@apps/web/lib/app_notifications';
import {
    buildChatReferenceContextKey,
    chatReferenceStore,
} from '@apps/web/lib/chat_reference_store';
import {
    type ChatPanelMode,
    type GeneralChatSelection,
    readGeneralChatSelection,
    writeGeneralChatSelection,
} from '@apps/web/lib/chat_ui_persistence';
import { EVENTS } from '@apps/web/lib/events';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { isTransparentRateLimitError } from '@apps/web/lib/known_api_errors';
import { useMode } from '@apps/web/lib/mode_store';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminAgentTrace,
    useAgentConversation,
    useAgentConversations,
    useAgentModelOptions,
    useJoinedOutreach,
    useOrganization,
    useOrganizationRun,
    useOrganizationRunDebrief,
    useOrganizationRunFeedback,
    useOrganizationRunMessageFeedback,
    useOrganizationRuns,
    useOutreachAnalysisConversationByOutreach,
    useOutreachAnalysisRunByOutreach,
    useOutreachDetail,
    useOutreachRunnerRunByOutreach,
    useShift,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type AgentConversationRead,
    type AgentTraceDetail,
    ApiError,
    type ApiResponseType,
} from '@libraries/typescript/api_client';
import { generateUuid } from '@libraries/typescript/utilities';

const DEFAULT_PHOEBE_CHAT_TARGET: PhoebeChatTarget = { kind: 'general' };
// "Learned" rather than "Changes": the tab is what Phoebe now knows, and the
// short word keeps the segmented control off the header's other controls.
const CHAT_TAB_LABELS = { chat: 'Chat', changes: 'Learned' } as const;
const PHOEBE_THINKING_TIPS_FEATURE_NAME = 'phoebe_thinking_tips_enabled';
const OUTREACH_EVENT_REFRESH_THROTTLE_MS = 5_000;
// How often to re-read the conversation over HTTP while a run is believed to
// be in flight. Slow on purpose: the websocket is the real progress channel
// and this only has to bound how long a dropped one can hold the composer
// disabled, so it should be well under a user's patience and well over the
// cost of a poll.
const RUN_IN_FLIGHT_CONVERSATION_POLL_INTERVAL_MS = 10_000;
type OrganizationConversationSummary =
    | ApiResponseType<
          '/organizations/{organization_id}/agent-conversations',
          'get'
      >['conversations'][number]
    | ApiResponseType<
          '/organizations/{organization_id}/runs',
          'get'
      >['runs'][number];
type PhoebeAgentRunStatus = ApiResponseType<
    '/organizations/{organization_id}/runs/{run_id}',
    'get'
>['status'];
type StreamOverlayInlineEntry =
    | MessageEntry
    | SaveNoteArtifactEntry
    | StructuredReportArtifactEntry
    | UpdatePlaybookArtifactEntry;
type RunDebriefAcknowledgementState = {
    readonly createdAt: string;
    readonly notes: ReadonlyArray<string>;
};

const CREATE_CONVERSATION_ERROR_MESSAGE = 'Failed to create conversation.';
const SEND_MESSAGE_ERROR_MESSAGE = 'Failed to send message.';
const NETWORK_ERROR_MESSAGE =
    'Connection interrupted. Check your network and try again.';
const PENDING_APPROVAL_INPUT_BLOCKED_MESSAGE =
    'Approve or deny the pending action before sending another message.';
const DRAFT_AGENT_OUTREACH_TOOL_NAME = 'draft_agent_outreach';
const ADD_OUTREACH_SUGGESTIONS_TOOL_NAME = 'add_outreach_suggestions';
const OUTREACH_RECOMMENDATIONS_READY_REASON = 'outreach_recommendations_ready';

// A dropped connection surfaces as the browser's own "Failed to fetch" /
// "Load failed" text, which reads like an app bug and hides the fact that
// retrying works.
function getSendErrorMessage(error: unknown, fallback: string): string | null {
    if (isTransparentRateLimitError(error)) {
        return null;
    }
    if (error instanceof ApiError && error.isNetworkError) {
        return NETWORK_ERROR_MESSAGE;
    }
    return error instanceof Error ? error.message : fallback;
}

type AgentModelUpdate = {
    readonly active_model: string | null;
    readonly resolved_model: string | null;
};

function readAgentModelUpdate(response: unknown): AgentModelUpdate | null {
    if (!isRecord(response) || !('active_model' in response)) {
        return null;
    }
    const activeModel = response['active_model'];
    const resolvedModel = response['resolved_model'];
    return {
        active_model: typeof activeModel === 'string' ? activeModel : null,
        resolved_model:
            typeof resolvedModel === 'string' ? resolvedModel : null,
    };
}

function isTerminalPhoebeAgentRunStatus(status: PhoebeAgentRunStatus): boolean {
    switch (status) {
        case 'completed':
        case 'failed':
        case 'cancelled':
            return true;
        case 'idle':
        case 'running':
            return false;
        default: {
            const exhaustiveStatus: never = status;
            return exhaustiveStatus;
        }
    }
}

function getRunDebriefSubmissionNotes(
    submission: RunDebriefSubmission,
): ReadonlyArray<string> {
    const notes = submission.decisionVerdicts
        .map((verdict) => verdict.note?.trim() ?? '')
        .filter((note) => note.length > 0);
    const overallComment = submission.comment?.trim() ?? '';
    return overallComment ? [...notes, overallComment] : notes;
}

function getSuggestionCountFromStatus(status: string | null): number | null {
    if (status === null) {
        return null;
    }
    const match = /added\s+(\d+)\s+suggestion/i.exec(status);
    if (!match) {
        return null;
    }
    const count = Number.parseInt(match[1] ?? '', 10);
    return Number.isFinite(count) ? count : null;
}

function buildRecommendationReviewPromptContent({
    assistantText,
    suggestionStatus,
}: {
    readonly assistantText: string;
    readonly suggestionStatus: string | null;
}): RunDebriefPromptContent {
    const suggestionCount = getSuggestionCountFromStatus(suggestionStatus);
    const normalizedAssistantText = assistantText.toLowerCase();
    const reasoningPoints = [
        suggestionCount === null
            ? 'Phoebe found caregivers for this open shift.'
            : `Phoebe found ${suggestionCount} caregiver${
                  suggestionCount === 1 ? '' : 's'
              } for this open shift.`,
    ];

    if (
        normalizedAssistantText.includes('care plan') ||
        normalizedAssistantText.includes('familiar caregiver') ||
        normalizedAssistantText.includes('prior visit')
    ) {
        reasoningPoints.push(
            'Phoebe called out the continuity preference and explained how it affected the ranking.',
        );
    }
    if (normalizedAssistantText.includes('nothing has been sent')) {
        reasoningPoints.push(
            'Nothing has been sent yet, so this list is still safe to adjust.',
        );
    }

    return {
        title: 'How are these recommendations?',
        decisionIntro:
            'Here’s the thinking behind these recommendations — can you tell me if I got it right?',
        reasoningPoints,
    };
}

function getTimeOfDayGreeting(date: Date): string {
    const hour = date.getHours();
    if (hour < 5) {
        return 'Working late';
    }
    if (hour < 12) {
        return 'Morning';
    }
    if (hour < 17) {
        return 'Afternoon';
    }
    if (hour < 21) {
        return 'Evening';
    }
    return 'Working late';
}

// Starter prompts surface in the empty-state hero. Tapping a prompt
// auto-sends it (handleSubmit triggers the deferred conversation
// create + first message), so the prompts double as one-click examples
// of what Phoebe can do today.
const STARTER_PROMPTS: ReadonlyArray<string> = [
    'Show me open shifts this week',
    'Pull a 7-day outreach report',
    'Summarize recent callouts',
];

const MODEL_PICKABLE_NEW_CHAT_RUN_TYPES: ReadonlyArray<NewChatRunType> = [
    'general',
];

export function PhoebeChatView({
    mode = 'general',
    target = DEFAULT_PHOEBE_CHAT_TARGET,
    onHide,
    readOnly = false,
    conversationSource = 'organization',
    fixedConversationId = null,
    onSelectedConversationIdChange,
    onAgentTraceConversationIdChange,
    onDebugSnapshotChange,
    debugPanelOpen = false,
    onDebugPanelToggle,
    isExpanded = false,
    chatMode,
    onChangeChatMode,
    mobileKeyboardOpen = false,
    clientSurface = null,
    organizationIdOverride,
    automationId,
    onManageAutomationSuccess,
    isVisible = true,
    conversationId: conversationIdProp,
    onOpenPhoebePathInNewTab,
    onRequestMicrophonePermission,
}: {
    readonly mode?: PhoebeChatMode;
    readonly target?: PhoebeChatTarget;
    readonly onHide?: () => void;
    readonly readOnly?: boolean;
    readonly conversationSource?: PhoebeConversationSource;
    readonly fixedConversationId?: string | null;
    // Omit to use Home session persistence. Pass a string or null when this
    // surface owns selection (null is a new draft).
    readonly conversationId?: string | null;
    readonly onSelectedConversationIdChange?: (id: string | null) => void;
    readonly onAgentTraceConversationIdChange?: (id: string | null) => void;
    readonly onDebugSnapshotChange?: (
        snapshot: PhoebeChatDebugSnapshot | null,
    ) => void;
    readonly debugPanelOpen?: boolean;
    readonly onDebugPanelToggle?: () => void;
    readonly isExpanded?: boolean;
    readonly chatMode?: ChatPanelMode;
    readonly onChangeChatMode?: (mode: ChatPanelMode) => void;
    readonly mobileKeyboardOpen?: boolean;
    readonly clientSurface?: string | null;
    readonly organizationIdOverride?: string | null;
    readonly automationId?: string | null;
    /**
     * Temporary page seam until the backend publishes automation updates for
     * interactive manage_automation calls.
     */
    readonly onManageAutomationSuccess?: () => void;
    readonly isVisible?: boolean;
    // In the extension surface, opens an app path in a full web-app browser
    // tab (via the sidepanel's EXTENSION_OPEN_PHOEBE_PATH bridge) instead of
    // navigating the sidepanel iframe. Undefined on the web surface.
    readonly onOpenPhoebePathInNewTab?: (path: string) => void;
    // In the extension surface, opens the extension's one-time microphone
    // grant page (via the sidepanel's EXTENSION_MIC_PERMISSION_REQUEST
    // bridge) — the side panel cannot reliably render the getUserMedia
    // prompt itself. Undefined on the web surface, where the browser
    // prompts inline.
    readonly onRequestMicrophonePermission?: () => void;
}) {
    const onManageAutomationSuccessRef = useRef(onManageAutomationSuccess);
    const handledManageAutomationCallIdsRef = useRef<Set<string>>(new Set());
    const posthog = usePostHog();
    useEffect(() => {
        onManageAutomationSuccessRef.current = onManageAutomationSuccess;
    }, [onManageAutomationSuccess]);
    const contextOrganizationId = useOrganizationId();
    const organizationId = organizationIdOverride ?? contextOrganizationId;
    const { timezone: contextTimezone } = useOrganizationTimezone();
    const { data: chatOrganization, isLoading: chatOrganizationIsLoading } =
        useOrganization(organizationId, {
            reason: 'phoebe-chat-view',
        });
    // The chat may be scoped to an organization other than the app-wide
    // selection, so names inside it follow this organization's setting.
    const displayNamesLastFirst =
        chatOrganization?.general_settings
            ?.display_caregiver_names_last_first ?? false;
    const timezone = chatOrganization?.timezone ?? contextTimezone;
    const chatOrganizationScope = useMemo(
        () => ({
            organizationId,
            timezone,
        }),
        [organizationId, timezone],
    );
    // Changes is a persistent tab, not an artifact: artifacts open on demand
    // and close, whereas what Phoebe has changed is always there to look at.
    const [activeChatTab, setActiveChatTab] = useState<'chat' | 'changes'>(
        'chat',
    );
    const trainingMode = useTrainingMode({
        organizationId,
        reason: 'phoebe-chat-view',
    });
    const showTrainingModeBadge = trainingMode.isInTrainingMode;
    // The question-card variant is a rule (compact layout, arrow-key
    // selection, focus stealing), not just decoration. Note that the
    // `isReady &&` here doesn't gate anything by itself: isReady and
    // isInTrainingMode both come from the same organization fetch
    // (useOrganization has no keepPreviousData), so isReady is false only
    // while isInTrainingMode is already false too. The real fix for "a
    // training org's pending question briefly renders the non-training
    // variant and then snaps over" is trainingModeContextReady below, which
    // defers UserQuestionCard's first render until readiness is known so it
    // never paints the wrong variant in the first place.
    const showTrainingModeQuestionContext =
        trainingMode.isReady && trainingMode.isInTrainingMode;
    // Threaded to UserQuestionCard (see PhoebeAgentTimelineEntries below):
    // while false, the question card renders nothing instead of rendering
    // once now and again once training-mode readiness resolves. Ready means
    // the org query has SETTLED (data, error, or disabled by a null org id),
    // not that data is present like trainingMode.isReady: an errored fetch
    // (useApiQuery never retries) or a missing org id would leave isReady
    // false forever, permanently hiding every question card in the
    // transcript. When the query settles without data,
    // showTrainingModeQuestionContext above stays false, so those cards
    // render the safe plain variant instead.
    const trainingModeContextReady =
        organizationId === null || !chatOrganizationIsLoading;
    const { data: user } = useUser({ reason: 'phoebe-chat-view' });
    const userId = user?.id ?? null;
    const userIsAdmin = user?.admin === true;
    const { data: agentModelOptions } = useAgentModelOptions(userIsAdmin);
    const orchestratorProductChatEnabled =
        useOrchestratorProductChatEnabled(userIsAdmin);
    const thinkingTipsEnabled = useFeatureFlag(
        PHOEBE_THINKING_TIPS_FEATURE_NAME,
        chatOrganization?.features,
    );
    const agentV3Enabled = useFeatureFlag(
        'agent_v3_enabled',
        chatOrganization?.features,
    );

    const canOpenAgentTrace = user?.email?.endsWith('@phoebe.work') === true;
    const {
        requestedChatId: requestedAgentConversationIdFromUi,
        requestedChatOrganizationId,
        clearRequestedChatId: clearRequestedAgentConversationId,
        requestedComposerPrefill,
        clearRequestedComposerPrefill,
        requestedComposerDraft,
        clearRequestedComposerDraft,
        newChatRequested,
        clearNewChatRequest,
        isChatVisible: isDockedChatVisible,
    } = useUI();
    const shouldRequireRequestedChatOrganizationScope =
        organizationIdOverride !== undefined;
    const requestedAgentConversationId =
        requestedChatOrganizationId === organizationId ||
        (!shouldRequireRequestedChatOrganizationScope &&
            requestedChatOrganizationId === null)
            ? requestedAgentConversationIdFromUi
            : null;
    const isOutreachTarget = target.kind === 'outreach';
    const isCalloutMode = mode === 'callout' || isOutreachTarget;
    const isFixedCalloutMode = mode === 'callout' && !isOutreachTarget;
    const usesAgentConversationEndpoints =
        isFixedCalloutMode || fixedConversationId !== null;
    const initialChatBackendKind = getInitialChatBackendKind({
        conversationSource,
        usesAgentConversationEndpoints,
    });
    const isAdminConversationSource =
        initialChatBackendKind === 'admin_trace_compat';
    const isRunNativeProductChat = initialChatBackendKind === 'run_native';
    const shouldLoadRunList = isRunNativeProductChat && !isOutreachTarget;
    const orgConversationOrganizationId = isAdminConversationSource
        ? null
        : organizationId;
    const voiceInputFlow = isCalloutMode ? 'callout_chat' : 'general_chat';
    const ownsConversationSelection = conversationIdProp !== undefined;
    const isGeneralProductChat =
        mode === 'general' &&
        conversationSource === 'organization' &&
        fixedConversationId === null &&
        !isOutreachTarget;
    // Persist the selected conversation id across page refreshes for the
    // general-mode product chat. Callout/admin/fixed flows are driven by
    // routing or props. Surfaces that pass conversationId own selection
    // themselves (for example /agent via the URL) and must not touch Home's
    // session storage.
    const shouldPersistConversationId =
        !ownsConversationSelection && isGeneralProductChat;
    // The general chat is scoped to the browser session: a new session opens a
    // new conversation (so the user picks up the current agent version, which a
    // conversation pins on its first run), while a reload resumes the chat this
    // session was on. Captured once at mount; the async-userId path (Point B
    // below) resolves it again once the scope is complete.
    const initialGeneralChatSelectionRef = useRef<GeneralChatSelection | null>(
        null,
    );
    if (initialGeneralChatSelectionRef.current === null) {
        if (ownsConversationSelection) {
            initialGeneralChatSelectionRef.current = {
                conversationId: conversationIdProp,
                isNewChat: conversationIdProp === null,
            };
        } else {
            initialGeneralChatSelectionRef.current = shouldPersistConversationId
                ? readGeneralChatSelection({ organizationId, userId })
                : { conversationId: null, isNewChat: false };
        }
    }
    const [selectedConversationId, setSelectedConversationId] = useState<
        string | null
    >(() => initialGeneralChatSelectionRef.current?.conversationId ?? null);
    const selectedConversationPersistenceScopeRef = useRef({
        organizationId,
        userId,
    });
    useEffect(() => {
        if (!shouldPersistConversationId) {
            return;
        }
        const currentScope = selectedConversationPersistenceScopeRef.current;
        if (
            currentScope.organizationId !== organizationId ||
            currentScope.userId !== userId
        ) {
            return;
        }
        writeGeneralChatSelection(
            {
                organizationId,
                userId,
            },
            selectedConversationId,
        );
    }, [
        organizationId,
        shouldPersistConversationId,
        selectedConversationId,
        userId,
    ]);
    // Mirror the on-screen general conversation into the module registry so
    // approval toasts for the currently-viewed chat can be suppressed.
    useEffect(() => {
        if (!isGeneralProductChat || !isVisible) {
            return;
        }
        setActiveAgentConversation(selectedConversationId, organizationId);
        return () => {
            setActiveAgentConversation(null);
        };
    }, [
        isVisible,
        organizationId,
        isGeneralProductChat,
        selectedConversationId,
    ]);
    useEffect(() => {
        onSelectedConversationIdChange?.(selectedConversationId);
    }, [selectedConversationId, onSelectedConversationIdChange]);
    // Report artifacts are conversation-scoped — wipe the shared store
    // whenever the user switches conversations and on unmount, so a
    // report opened in conversation A doesn't survive into B.
    useEffect(() => {
        closeAllArtifactTabs();
        return () => {
            closeAllArtifactTabs();
        };
    }, [selectedConversationId]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const fetchErrorMessageRef = useRef<string | null>(null);
    // Conversation ids whose detail fetch 404'd ("run not found"); the
    // selection resolver skips them so recovery never re-selects a dead row.
    const notFoundConversationIdsRef = useRef<Set<string>>(new Set());
    const [hasRunError, setHasRunError] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isUpdatingModel, setIsUpdatingModel] = useState(false);
    const isUpdatingModelRef = useRef(false);
    const heldPinRef = useRef<{
        readonly runId: string;
        readonly promise: Promise<void>;
    } | null>(null);
    const [pendingAgentModel, setPendingAgentModel] = useState<string | null>(
        null,
    );
    const [pendingRunType, setPendingRunType] =
        useState<NewChatRunType>('general');
    const [isStopping, setIsStopping] = useState(false);
    const [isConfiguringMonitoring, setIsConfiguringMonitoring] =
        useState(false);
    const [decidingCallIds, setDecidingCallIds] = useState<
        Record<string, boolean>
    >({});
    const [answeringQuestionCallIds, setAnsweringQuestionCallIds] = useState<
        Record<string, boolean>
    >({});

    const [selectedCaregiverId, setSelectedCaregiverId] = useState<
        string | null
    >(null);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(
        null,
    );
    const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
    // Let deeply-rendered reference badges open the same in-panel detail
    // modals that tool outputs do, instead of navigating out of the chat.
    const entityDetailClickHandlers = useMemo(
        () => ({
            onCaregiverClick: setSelectedCaregiverId,
            onClientClick: setSelectedClientId,
            onShiftClick: setSelectedShiftId,
        }),
        [],
    );
    const {
        data: selectedClient,
        isLoading: selectedClientIsLoading,
        mutate: mutateSelectedClient,
    } = useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}',
        method: 'get',
        pathParams:
            organizationId && selectedClientId
                ? {
                      organization_id: organizationId,
                      client_id: selectedClientId,
                  }
                : undefined,
        queryParams: {
            include_inactive: true,
        },
        enabled: organizationId !== null && selectedClientId !== null,
        select: (client) => client,
        reason: 'phoebe-chat-client-detail-modal',
    });
    const {
        data: selectedShift,
        isLoading: selectedShiftIsLoading,
        error: selectedShiftError,
        mutate: mutateSelectedShift,
    } = useShift(organizationId, selectedShiftId, {
        reason: 'phoebe-chat-shift-details-dialog',
    });

    const [isAwaitingRunCompletion, setIsAwaitingRunCompletion] =
        useState(false);
    const [hasRunCompletionSignal, setHasRunCompletionSignal] = useState(false);
    const [awaitingQuestionAnswerCallId, setAwaitingQuestionAnswerCallId] =
        useState<string | null>(null);
    const [optimisticThinkingMarker, setOptimisticThinkingMarker] =
        useState<OptimisticThinkingMarker | null>(null);
    const [optimisticThinkingTipIndex, setOptimisticThinkingTipIndex] =
        useState<number | null>(null);
    const [expandedLiveRunId, setExpandedLiveRunId] = useState<string | null>(
        null,
    );
    const [autoExpandDismissedRunId, setAutoExpandDismissedRunId] = useState<
        string | null
    >(null);
    const [streamState, setStreamState] =
        useState<ConversationStreamState>(EMPTY_STREAM_STATE);
    const [retainedStreamRun, setRetainedStreamRun] =
        useState<StreamRunState | null>(null);
    const [subagentDraftProgress, setSubagentDraftProgress] =
        useState<SubagentDraftProgress | null>(null);
    const chatInputRef = useRef<ChatInputHandle | null>(null);
    const composerFormRef = useRef<HTMLFormElement | null>(null);
    const sentMentionReferencesRef = useRef(
        new Map<string, Array<ReferenceData>>(),
    );
    // When the user clicks "new chat", we clear selectedConversationId so
    // the empty-state hero renders. Without a gate, the auto-resolve
    // effect would immediately snap selection to the most recent
    // conversation. This ref keeps the draft state until they actually
    // send (which creates the conversation) or pick a different chat.
    const isDraftingNewChatRef = useRef(
        initialGeneralChatSelectionRef.current?.isNewChat === true,
    );
    // The create response arrives before the refreshed run list and detail.
    // Preserve its backend generation marker so saved-run resolution and file
    // uploads do not wait for those follow-up reads.
    const createdRunRendererVersionRef = useRef<{
        readonly runId: string;
        readonly isAgentV3: boolean;
    } | null>(null);
    useEffect(() => {
        createdRunRendererVersionRef.current = null;
    }, [organizationId]);
    const pendingMessageOperationIdRef = useRef<string | null>(null);
    const pendingMessageOperationTextRef = useRef<string | null>(null);
    const pendingActivityEventIdsRef = useRef<ReadonlyArray<string>>([]);
    const pendingStopOperationIdRef = useRef<string | null>(null);
    const pendingToolDecisionOperationIdsRef = useRef<Record<string, string>>(
        {},
    );
    const pendingQuestionAnswerOperationIdsRef = useRef<Record<string, string>>(
        {},
    );
    const previousIsCalloutModeRef = useRef(isCalloutMode);
    const {
        data: targetOutreach,
        error: targetOutreachError,
        isLoading: isTargetOutreachLoading,
        mutate: mutateTargetOutreach,
    } = useOutreachDetail(
        orgConversationOrganizationId,
        isOutreachTarget ? target.outreachId : null,
        { reason: 'phoebe-chat-monitoring-status' },
    );
    // A v3-born outreach is steered by its runner run, not by the legacy
    // analysis run, so the two lookups are mutually exclusive.
    const isV3BornOutreachTarget =
        isOutreachTarget && targetOutreach?.agent_generation === 'v3';
    const {
        data: outreachAnalysisRun,
        isLoading: isOutreachAnalysisRunLoading,
        error: outreachAnalysisRunError,
        mutate: mutateOutreachAnalysisRun,
    } = useOutreachAnalysisRunByOutreach(
        orgConversationOrganizationId,
        isOutreachTarget && isRunNativeProductChat && !isV3BornOutreachTarget
            ? target.outreachId
            : null,
    );
    const {
        data: outreachRunnerRun,
        isLoading: isOutreachRunnerRunLoading,
        error: outreachRunnerRunError,
        mutate: mutateOutreachRunnerRun,
    } = useOutreachRunnerRunByOutreach(
        orgConversationOrganizationId,
        isRunNativeProductChat && isV3BornOutreachTarget
            ? target.outreachId
            : null,
    );
    const outreachTargetRun = isV3BornOutreachTarget
        ? outreachRunnerRun
        : outreachAnalysisRun;
    const isOutreachTargetRunLoading = isV3BornOutreachTarget
        ? isOutreachRunnerRunLoading
        : isOutreachAnalysisRunLoading;
    const outreachTargetRunError = isV3BornOutreachTarget
        ? outreachRunnerRunError
        : outreachAnalysisRunError;
    const mutateOutreachTargetRun = isV3BornOutreachTarget
        ? mutateOutreachRunnerRun
        : mutateOutreachAnalysisRun;
    // Once this session has resolved the outreach's run, keep using it
    // through transient lookup gaps (a revalidation 404, a cache wipe).
    // Without the latch, one bad lookup nulls the run id, resets the event
    // stream, and downgrades the chat to the legacy mirror mid-session.
    const [lastKnownOutreachRun, setLastKnownOutreachRun] = useState<{
        readonly outreachId: string;
        readonly runId: string;
    } | null>(null);
    const currentOutreachTargetId = isOutreachTarget ? target.outreachId : null;
    useEffect(() => {
        if (currentOutreachTargetId !== null && outreachTargetRun?.id) {
            setLastKnownOutreachRun({
                outreachId: currentOutreachTargetId,
                runId: outreachTargetRun.id,
            });
        }
    }, [currentOutreachTargetId, outreachTargetRun?.id]);
    const effectiveOutreachTargetRunId =
        outreachTargetRun?.id ??
        (currentOutreachTargetId !== null &&
        lastKnownOutreachRun?.outreachId === currentOutreachTargetId
            ? lastKnownOutreachRun.runId
            : null);
    const requestedOutreachRunIdForSelection =
        isOutreachTarget &&
        isRunNativeProductChat &&
        requestedAgentConversationId !== null &&
        requestedAgentConversationId === effectiveOutreachTargetRunId
            ? requestedAgentConversationId
            : null;
    const effectiveRequestedConversationId = isOutreachTarget
        ? isRunNativeProductChat
            ? requestedOutreachRunIdForSelection
            : null
        : requestedAgentConversationId;
    const chatTargetRequestKey = isOutreachTarget
        ? `outreach:${target.outreachId}`
        : `mode:${mode}:fixed:${fixedConversationId ?? ''}`;
    const [appliedCalloutRequest, setAppliedCalloutRequest] = useState<{
        readonly targetKey: string;
        readonly conversationId: string;
    } | null>(null);
    const appliedCalloutRequestedConversationId =
        appliedCalloutRequest?.targetKey === chatTargetRequestKey
            ? appliedCalloutRequest.conversationId
            : null;
    const requestedConversationIdForSelection = isCalloutMode
        ? (effectiveRequestedConversationId ??
          appliedCalloutRequestedConversationId)
        : ownsConversationSelection
          ? conversationIdProp
          : effectiveRequestedConversationId;
    const requestedConversationIdRef = useRef<string | null>(
        requestedConversationIdForSelection,
    );
    const explicitRequestedSelectionRef = useRef<{
        readonly conversationId: string;
        readonly organizationId: string | null;
    } | null>(
        ownsConversationSelection && conversationIdProp !== null
            ? {
                  conversationId: conversationIdProp,
                  organizationId,
              }
            : null,
    );
    const modePreference = useMode();
    const buildPanelChatLink = useCallback(
        (chatId: string): string => {
            const params = new URLSearchParams({ chatId });
            if (organizationId !== null) {
                params.set('organizationId', organizationId);
            }
            if (modePreference !== undefined) {
                params.set('mode', modePreference);
            }
            return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
        },
        [organizationId, modePreference],
    );
    const didLeaveCalloutMode =
        previousIsCalloutModeRef.current && !isCalloutMode;

    function resetStreamState() {
        setStreamState(EMPTY_STREAM_STATE);
        setExpandedLiveRunId(null);
        setAutoExpandDismissedRunId(null);
        setSubagentDraftProgress(null);
    }

    useEffect(() => {
        if (
            requestedAgentConversationIdFromUi !== null &&
            ((shouldRequireRequestedChatOrganizationScope &&
                requestedChatOrganizationId === null) ||
                (requestedChatOrganizationId !== null &&
                    organizationId !== null &&
                    requestedChatOrganizationId !== organizationId))
        ) {
            requestedConversationIdRef.current = null;
            explicitRequestedSelectionRef.current = null;
            clearRequestedAgentConversationId();
            return;
        }
        requestedConversationIdRef.current =
            requestedConversationIdForSelection;
        if (!ownsConversationSelection) {
            return;
        }
        // /agent owns selection via the URL. Leaving ?chatId=A for a bare
        // /agent must unpin A and draft a new chat; otherwise the previous
        // id stays selected even though the URL no longer asks for it.
        if (requestedConversationIdForSelection === null) {
            explicitRequestedSelectionRef.current = null;
            isDraftingNewChatRef.current = true;
            setSelectedConversationId(null);
            return;
        }
        isDraftingNewChatRef.current = false;
        explicitRequestedSelectionRef.current = {
            conversationId: requestedConversationIdForSelection,
            organizationId,
        };
    }, [
        clearRequestedAgentConversationId,
        organizationId,
        ownsConversationSelection,
        requestedAgentConversationIdFromUi,
        requestedChatOrganizationId,
        requestedConversationIdForSelection,
        shouldRequireRequestedChatOrganizationScope,
    ]);

    useEffect(() => {
        if (!isCalloutMode || effectiveRequestedConversationId === null) {
            return;
        }
        setAppliedCalloutRequest({
            targetKey: chatTargetRequestKey,
            conversationId: effectiveRequestedConversationId,
        });
    }, [chatTargetRequestKey, effectiveRequestedConversationId, isCalloutMode]);

    useEffect(() => {
        if (
            !isOutreachTarget ||
            !isRunNativeProductChat ||
            requestedAgentConversationId === null ||
            isOutreachTargetRunLoading
        ) {
            return;
        }

        if (requestedOutreachRunIdForSelection !== null) {
            return;
        }

        requestedConversationIdRef.current = null;
        setAppliedCalloutRequest(null);
        clearRequestedAgentConversationId();
    }, [
        clearRequestedAgentConversationId,
        isOutreachTarget,
        isOutreachTargetRunLoading,
        isRunNativeProductChat,
        requestedAgentConversationId,
        requestedOutreachRunIdForSelection,
    ]);

    useEffect(() => {
        previousIsCalloutModeRef.current = isCalloutMode;
    }, [isCalloutMode]);

    const {
        data: runConversations,
        isLoading: isRunsLoading,
        error: runsError,
        mutate: mutateRuns,
    } = useOrganizationRuns(
        shouldLoadRunList ? orgConversationOrganizationId : null,
        automationId ?? null,
    );
    const {
        data: legacyConversations,
        isLoading: isLegacyConversationsLoading,
        error: legacyConversationsError,
        mutate: mutateLegacyConversations,
    } = useAgentConversations(
        !isRunNativeProductChat && !isCalloutMode
            ? orgConversationOrganizationId
            : null,
    );
    const conversations =
        isRunNativeProductChat && isOutreachTarget
            ? null
            : isRunNativeProductChat
              ? runConversations
              : legacyConversations;
    const isConversationsLoading = isRunNativeProductChat
        ? isRunsLoading
        : isLegacyConversationsLoading;
    const conversationsError = isRunNativeProductChat
        ? runsError
        : legacyConversationsError;
    const mutateConversations = isRunNativeProductChat
        ? mutateRuns
        : mutateLegacyConversations;
    useEffect(() => {
        if (!shouldPersistConversationId) {
            selectedConversationPersistenceScopeRef.current = {
                organizationId,
                userId,
            };
            return;
        }
        const currentScope = selectedConversationPersistenceScopeRef.current;
        if (
            currentScope.organizationId === organizationId &&
            currentScope.userId === userId
        ) {
            return;
        }

        selectedConversationPersistenceScopeRef.current = {
            organizationId,
            userId,
        };
        const shouldPreserveExplicitSelection =
            currentScope.organizationId === organizationId &&
            currentScope.userId === null &&
            userId !== null &&
            explicitRequestedSelectionRef.current?.organizationId ===
                organizationId &&
            explicitRequestedSelectionRef.current.conversationId ===
                selectedConversationId;
        if (shouldPreserveExplicitSelection) {
            writeGeneralChatSelection(
                {
                    organizationId,
                    userId,
                },
                selectedConversationId,
            );
            void mutateConversations();
            return;
        }

        explicitRequestedSelectionRef.current = null;
        requestedConversationIdRef.current = null;
        setAppliedCalloutRequest(null);
        // A new browser session opens a new chat; a reload resumes the chat
        // this session was on. Keep the draft flag when there is no
        // conversation to resume, so the resolver below does not fall back to
        // the user's most recent (version-pinned) conversation.
        const sessionSelection = readGeneralChatSelection({
            organizationId,
            userId,
        });
        isDraftingNewChatRef.current = sessionSelection.isNewChat;
        setSelectedConversationId(sessionSelection.conversationId);
        setErrorMessage(null);
        fetchErrorMessageRef.current = null;
        setStreamState(EMPTY_STREAM_STATE);
        setExpandedLiveRunId(null);
        setAutoExpandDismissedRunId(null);
        setSubagentDraftProgress(null);
        void mutateConversations();
    }, [
        mutateConversations,
        organizationId,
        selectedConversationId,
        shouldPersistConversationId,
        userId,
    ]);
    // Record the session's new-chat state once the scope resolves. The
    // write-back effect above only fires when the selection changes, so on a
    // cold load a new chat would never reach storage — and the next reload
    // would resume the user's previous (version-pinned) conversation instead of
    // keeping the new one. Declared after the restore effect so it observes the
    // resolved selection.
    useEffect(() => {
        if (!shouldPersistConversationId || !organizationId || !userId) {
            return;
        }
        if (selectedConversationId !== null) {
            return;
        }
        writeGeneralChatSelection({ organizationId, userId }, null);
    }, [
        organizationId,
        selectedConversationId,
        shouldPersistConversationId,
        userId,
    ]);
    // The joined read fans out to shifts/attempts/responses on every fetch,
    // so it serves only the shift-suggestion tabs and refreshes on run
    // settles — the hot monitoring loop above polls the plain read.
    const { data: targetOutreachJoined, mutate: mutateTargetOutreachJoined } =
        useJoinedOutreach(
            orgConversationOrganizationId,
            isOutreachTarget ? target.outreachId : null,
            { reason: 'phoebe-chat-shift-tabs' },
        );
    // Agent-managed outreaches are marked analyzing before their queued
    // kickoff creates and links the run. Keep looking up the native run during
    // that gap instead of settling permanently on the initial 404 result.
    const isOutreachAnalysisActive =
        isOutreachTarget && targetOutreach?.agent_status === 'analyzing';
    const isOutreachAgentRunPending =
        isOutreachAnalysisActive && outreachTargetRun === undefined;
    // useApiQuery has an initial render where data is unresolved before SWR's
    // loading flag turns true. Do not classify that unknown outreach as a
    // manual one and flash the empty sidebar before the request starts.
    const isTargetOutreachUnresolved =
        isOutreachTarget &&
        targetOutreach === undefined &&
        !targetOutreachError;
    const outreachTargetId = isOutreachTarget ? target.outreachId : null;
    // The agent runtime publishes outreach_suggestion_added on every run
    // state sync and outreach_updated when the run settles, so organization
    // events are the fast path for keeping the analyzing outreach fresh.
    // Bursts (e.g. a swarm batch staging many suggestions) collapse into one
    // leading and one trailing refresh per throttle window. Only the rare
    // settle event refreshes the joined read; the frequent suggestion syncs
    // touch just the plain status read.
    const refreshOutreachTarget = useCallback(() => {
        void mutateTargetOutreach();
        void mutateOutreachTargetRun();
    }, [mutateOutreachTargetRun, mutateTargetOutreach]);
    const throttledRefreshOutreachTarget = useThrottledCallback(
        refreshOutreachTarget,
        OUTREACH_EVENT_REFRESH_THROTTLE_MS,
    );
    // A separate throttle so a burst of suggestion syncs cannot swallow the
    // settle event's joined refresh.
    const throttledRefreshJoinedOutreachTarget = useThrottledCallback(
        () => void mutateTargetOutreachJoined(),
        OUTREACH_EVENT_REFRESH_THROTTLE_MS,
    );
    const refreshOutreachTargetForEvent = useCallback(
        (event: OrganizationEvent) => {
            if (
                outreachTargetId === null ||
                event.resource.type !== 'outreach' ||
                event.resource.id !== outreachTargetId
            ) {
                return;
            }
            if (event.type === 'outreach_updated') {
                throttledRefreshJoinedOutreachTarget();
            }
            throttledRefreshOutreachTarget();
        },
        [
            outreachTargetId,
            throttledRefreshJoinedOutreachTarget,
            throttledRefreshOutreachTarget,
        ],
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_added',
        refreshOutreachTargetForEvent,
        {
            organizationId: orgConversationOrganizationId,
        },
    );
    useListenForOrganizationEvent(
        'outreach_updated',
        refreshOutreachTargetForEvent,
        {
            organizationId: orgConversationOrganizationId,
        },
    );
    // Safety net while analysis is active: a dropped websocket or missed
    // event must not strand the chat on "analyzing", so keep a slow refresh
    // of the outreach and its run link alongside the event-driven fast path.
    useEffect(() => {
        if (!isOutreachAnalysisActive) {
            return;
        }
        void mutateTargetOutreach();
        void mutateOutreachTargetRun();
        const intervalId = window.setInterval(() => {
            void mutateTargetOutreach();
            void mutateOutreachTargetRun();
        }, 30_000);
        return () => window.clearInterval(intervalId);
    }, [
        isOutreachAnalysisActive,
        mutateOutreachTargetRun,
        mutateTargetOutreach,
    ]);
    // When analysis settles on the outreach being viewed, pull the joined
    // read once so the shift-suggestion tabs catch up even when the settle
    // event was missed on a dropped websocket. Tracking the analyzing
    // outreach id keeps unmounts and target switches from revalidating a
    // joined key nothing is consuming.
    const analyzingOutreachIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (isOutreachAnalysisActive) {
            analyzingOutreachIdRef.current = outreachTargetId;
            return;
        }
        if (
            analyzingOutreachIdRef.current !== null &&
            analyzingOutreachIdRef.current === outreachTargetId
        ) {
            void mutateTargetOutreachJoined();
        }
        analyzingOutreachIdRef.current = null;
    }, [
        isOutreachAnalysisActive,
        mutateTargetOutreachJoined,
        outreachTargetId,
    ]);
    // Temporary bridge for pre-resource-link outreach chats: prefer the native
    // run lookup, but still render old legacy-only callout conversations.
    // Once a run has been seen this session the chat never downgrades to the
    // legacy mirror — a transient lookup 404 must not flip the backend.
    const shouldLookupLegacyOutreachFallback =
        isOutreachTarget &&
        isRunNativeProductChat &&
        !isV3BornOutreachTarget &&
        !isOutreachAgentRunPending &&
        !isOutreachTargetRunLoading &&
        outreachTargetRun === undefined &&
        effectiveOutreachTargetRunId === null &&
        !outreachTargetRunError;
    const {
        data: outreachTargetConversation,
        isLoading: isOutreachTargetConversationLoading,
        error: outreachTargetConversationError,
    } = useOutreachAnalysisConversationByOutreach(
        orgConversationOrganizationId,
        isOutreachTarget &&
            (!isRunNativeProductChat || shouldLookupLegacyOutreachFallback)
            ? target.outreachId
            : null,
    );
    const shouldUseLegacyOutreachFallback =
        shouldLookupLegacyOutreachFallback &&
        (isOutreachTargetConversationLoading ||
            outreachTargetConversation !== undefined);
    const effectiveChatBackendKind = getEffectiveChatBackendKind({
        initialBackendKind: initialChatBackendKind,
        shouldUseLegacyOutreachFallback,
    });
    useEffect(() => {
        setPendingRunType('general');
    }, [
        effectiveChatBackendKind,
        isCalloutMode,
        modePreference,
        organizationId,
        selectedConversationId,
        userId,
        userIsAdmin,
        orchestratorProductChatEnabled,
    ]);
    const shouldUseRunNativeConversation =
        effectiveChatBackendKind === 'run_native';
    const canCreateOutreachRunOnSubmit =
        isOutreachTarget && shouldUseRunNativeConversation;
    const shiftActionPolicy: ShiftActionPolicy = useMemo(
        () => ({
            // Outreach is "started" once it has left the pending draft state.
            outreachStarted:
                isOutreachTarget &&
                targetOutreach?.status !== undefined &&
                targetOutreach?.status !== null &&
                targetOutreach.status !== 'pending',
            // Only offer "create outreach" when not already scoped to one.
            showCreateOutreachAction: !isOutreachTarget,
            // An outreach detail chat already covers its own shifts, so the
            // assistant suppresses the "Create outreach" affordance for those
            // (it can still offer it for other referenced shifts).
            currentOutreachShiftIds: isOutreachTarget
                ? (targetOutreach?.shift_ids ?? [])
                : [],
        }),
        [isOutreachTarget, targetOutreach?.status, targetOutreach?.shift_ids],
    );
    const fixedConversationIdForSelection = isOutreachTarget
        ? isOutreachTargetRunLoading
            ? null
            : shouldUseRunNativeConversation
              ? (requestedConversationIdForSelection ??
                effectiveOutreachTargetRunId ??
                null)
              : (outreachTargetConversation?.id ?? null)
        : fixedConversationId;
    const selectedRunNativeConversationId =
        shouldUseRunNativeConversation &&
        (!isOutreachTarget ||
            selectedConversationId === fixedConversationIdForSelection)
            ? selectedConversationId
            : null;

    // Ensure we always have a selected conversation when data is available.
    useEffect(() => {
        // Leaving callout mode retires the request, so the pin it produced
        // has to go with it — a general chat must not stay stuck on the
        // callout conversation it is no longer scoped to. Clear the ref
        // rather than skipping it for this pass only: the fallback cannot
        // run while the list is still loading, and a surviving ref would
        // re-pin the callout conversation on the next pass.
        if (didLeaveCalloutMode) {
            explicitRequestedSelectionRef.current = null;
        }
        const requestedConversationId = didLeaveCalloutMode
            ? null
            : requestedConversationIdRef.current;
        // The drafting flag stays true through both "user clicked + but
        // hasn't sent yet" (selectedConversationId === null) and "first
        // message sent, awaiting SWR to refetch the new conversation" (id
        // already set but conversations list still stale). Skip the resolver
        // in both cases so it doesn't snap back to the previously-newest row.
        // An explicit open request (sidebar session, deep link) overrides
        // the draft state — the user asked for a specific conversation.
        if (isDraftingNewChatRef.current) {
            if (requestedConversationId === null) {
                return;
            }
            isDraftingNewChatRef.current = false;
        }
        const selection = resolveSelectedConversationId({
            fixedConversationId: fixedConversationIdForSelection,
            isCalloutMode,
            requestedConversationId,
            selectedConversationId,
            pinnedConversationId:
                explicitRequestedSelectionRef.current?.conversationId ?? null,
            notFoundConversationIds: notFoundConversationIdsRef.current,
            conversations,
        });
        if (!selection) {
            return;
        }

        setSelectedConversationId(selection.nextSelectedConversationId);
        if (
            requestedConversationId !== null &&
            selection.nextSelectedConversationId === requestedConversationId
        ) {
            explicitRequestedSelectionRef.current = {
                conversationId: requestedConversationId,
                organizationId,
            };
        }
        if (selection.shouldClearRequestedConversationId) {
            requestedConversationIdRef.current = null;
            clearRequestedAgentConversationId();
        }
    }, [
        conversations,
        fixedConversationIdForSelection,
        isCalloutMode,
        organizationId,
        selectedConversationId,
        didLeaveCalloutMode,
        requestedConversationIdForSelection,
        clearRequestedAgentConversationId,
    ]);

    // Clear the drafting flag once the just-created conversation is observed
    // in the SWR list. Until then, the resolver above stays suppressed.
    useEffect(() => {
        if (!isDraftingNewChatRef.current) {
            return;
        }
        if (selectedConversationId === null) {
            return;
        }
        // /agent new-draft: the previous chat is still in the list, but that
        // is not the conversation we just created. Leaving the flag set is
        // what keeps the resolver from snapping back onto it. Same race the
        // mode-flip handler avoids by declaring after this effect.
        if (
            ownsConversationSelection &&
            requestedConversationIdForSelection === null
        ) {
            return;
        }
        const conversationLanded = conversations?.some(
            (conversation) => conversation.id === selectedConversationId,
        );
        if (conversationLanded) {
            isDraftingNewChatRef.current = false;
        }
    }, [
        conversations,
        ownsConversationSelection,
        requestedConversationIdForSelection,
        selectedConversationId,
    ]);

    // Runs are mode-scoped server-side, so a conversation selected in one
    // mode cannot be fetched in the other — keeping it selected across a
    // flip just produces "run not found" stream errors. Drop the selection
    // into the draft-new-chat state (the drafting flag keeps the resolver
    // from snapping back to a stale row) and refetch the list under the
    // new mode. Declared after the flag-clearing effect above on purpose:
    // in the flip's effect flush that effect still sees the old selection
    // and would otherwise clear the flag we just set.
    useOnModeChange(() => {
        resetStreamState();
        setAppliedCalloutRequest(null);
        if (isGeneralProductChat) {
            isDraftingNewChatRef.current = true;
            explicitRequestedSelectionRef.current = null;
            requestedConversationIdRef.current = null;
            if (shouldPersistConversationId) {
                writeGeneralChatSelection(
                    {
                        organizationId,
                        userId,
                    },
                    null,
                );
            }
            setSelectedConversationId(null);
            onSelectedConversationIdChange?.(null);
            setErrorMessage(null);
            fetchErrorMessageRef.current = null;
        }
        void mutateConversations();
    });

    useEffect(() => {
        if (
            !isCalloutMode ||
            effectiveRequestedConversationId === null ||
            selectedConversationId !== effectiveRequestedConversationId
        ) {
            return;
        }

        requestedConversationIdRef.current = null;
        clearRequestedAgentConversationId();
    }, [
        isCalloutMode,
        effectiveRequestedConversationId,
        selectedConversationId,
        clearRequestedAgentConversationId,
    ]);

    useEffect(() => {
        if (!isCalloutMode) {
            return;
        }

        return () => {
            if (requestedConversationIdRef.current === null) {
                return;
            }

            requestedConversationIdRef.current = null;
            clearRequestedAgentConversationId();
        };
    }, [isCalloutMode, clearRequestedAgentConversationId]);

    useEffect(() => {
        if (
            !isCalloutMode ||
            fixedConversationIdForSelection ||
            effectiveRequestedConversationId !== null
        ) {
            return;
        }

        // Callout targets are not in the general-chat list. If a previous
        // general selection survives the route switch, clear it and wait for
        // the callout target lookup to select the right chat.
        if (isRunNativeProductChat && isOutreachTarget) {
            // App-config hydration can switch an outreach from the legacy
            // conversation path to the run-native path after the first render.
            // In that window, do not treat a legacy conversation id as a run id.
            setSelectedConversationId(null);
            return;
        }

        setSelectedConversationId((current) => {
            const selectedGeneralConversation = conversations?.some(
                (conversation) => conversation.id === current,
            );
            return selectedGeneralConversation ? null : current;
        });
    }, [
        conversations,
        fixedConversationIdForSelection,
        isCalloutMode,
        isOutreachTarget,
        isRunNativeProductChat,
        effectiveRequestedConversationId,
    ]);

    const {
        data: organizationRun,
        isLoading: isOrganizationRunLoading,
        error: organizationRunError,
        mutate: mutateOrganizationRun,
    } = useOrganizationRun(
        shouldUseRunNativeConversation ? orgConversationOrganizationId : null,
        selectedRunNativeConversationId,
    );
    // The run detail is authoritative, but while it is in flight fall back
    // to the run-list summary so orchestrator-only controls (e.g. the model
    // picker) don't flash before the detail loads.
    const selectedRunSummary = runConversations?.find(
        (run) => run.id === selectedRunNativeConversationId,
    );
    const selectedRunIsOrchestrator =
        (organizationRun ?? selectedRunSummary)?.run_type === 'orchestrator';
    // Run-native runs report whether the viewer may act; legacy
    // conversations and still-loading runs default to sendable. The
    // by-resource run covers callout mode before the selected run loads.
    const canSendInSelectedConversation =
        !shouldUseRunNativeConversation ||
        (organizationRun?.can_send ??
            (outreachTargetRun?.id === selectedRunNativeConversationId
                ? outreachTargetRun.can_send
                : undefined) ??
            true);
    // An expired chat is only reachable by deep link — it has already aged
    // out of the list and the sidebar rail. Sending a message revives it,
    // and the post-send refetch clears this on its own.
    const isSelectedConversationExpired = organizationRun?.is_expired === true;
    const isSelectedConversationCostCapped =
        organizationRun?.is_cost_capped === true;
    const {
        data: organizationRunMessageFeedback,
        mutate: mutateOrganizationRunMessageFeedback,
    } = useOrganizationRunMessageFeedback(
        shouldUseRunNativeConversation ? orgConversationOrganizationId : null,
        selectedRunNativeConversationId,
        !readOnly &&
            organizationRun !== undefined &&
            !selectedRunIsOrchestrator,
    );
    const {
        data: legacyOrganizationConversation,
        isLoading: isLegacyOrganizationConversationLoading,
        error: legacyOrganizationConversationError,
        mutate: mutateLegacyOrganizationConversation,
    } = useAgentConversation(
        shouldUseRunNativeConversation ? null : orgConversationOrganizationId,
        isAdminConversationSource ? null : selectedConversationId,
    );
    const organizationConversation = shouldUseRunNativeConversation
        ? organizationRun
        : legacyOrganizationConversation;
    const isOrganizationConversationLoading = shouldUseRunNativeConversation
        ? isOrganizationRunLoading || isOutreachTargetRunLoading
        : isLegacyOrganizationConversationLoading ||
          isOutreachTargetConversationLoading;
    const organizationConversationError = shouldUseRunNativeConversation
        ? (organizationRunError ?? outreachTargetRunError)
        : (legacyOrganizationConversationError ??
          outreachTargetConversationError);
    const mutateOrganizationConversation = shouldUseRunNativeConversation
        ? mutateOrganizationRun
        : mutateLegacyOrganizationConversation;
    const {
        data: adminTraceConversation,
        isLoading: isAdminConversationLoading,
        error: adminConversationError,
        mutate: mutateAdminConversation,
    } = useAdminAgentTrace(
        isAdminConversationSource ? selectedConversationId : null,
    );
    const [phoebeRunIdOverride, setPhoebeRunIdOverride] = useState<
        string | null
    >(null);
    const [suppressedPhoebeRunId, setSuppressedPhoebeRunId] = useState<
        string | null
    >(null);
    const [isRestartModalOpen, setIsRestartModalOpen] = useState(false);
    const [isRunnerResetModalOpen, setIsRunnerResetModalOpen] = useState(false);
    const organizationConversationPhoebeRunId =
        !shouldUseRunNativeConversation &&
        organizationConversation &&
        'phoebe_run_id' in organizationConversation
            ? (organizationConversation.phoebe_run_id ?? null)
            : null;
    const organizationAgentExecutionBackend =
        !shouldUseRunNativeConversation &&
        organizationConversation &&
        'execution_backend' in organizationConversation
            ? organizationConversation.execution_backend
            : null;
    const selectedPhoebeRunId = shouldUseRunNativeConversation
        ? selectedRunNativeConversationId
        : organizationConversationPhoebeRunId;
    const selectedRunIsAgentV3 =
        organizationRun?.is_agent_v3 ??
        (outreachTargetRun?.id === selectedRunNativeConversationId
            ? outreachTargetRun.is_agent_v3
            : undefined) ??
        selectedRunSummary?.is_agent_v3 ??
        (createdRunRendererVersionRef.current?.runId ===
        selectedRunNativeConversationId
            ? createdRunRendererVersionRef.current.isAgentV3
            : undefined);
    // Coordinator chat uploads: files land under this run's workspace via
    // the /user-uploads endpoint. The composer stays disabled for admin
    // trace surfaces (they still use the admin image-upload path) and
    // read-only outreach conversations.
    const uploadAttachments = useRunUploadAttachments({
        organizationId,
        runId: selectedPhoebeRunId ?? null,
        mode: modePreference ?? 'live',
        enabled:
            !isAdminConversationSource &&
            organizationId !== null &&
            selectedPhoebeRunId !== null &&
            selectedRunIsAgentV3 === true,
    });
    const canUploadRunAttachments =
        !isAdminConversationSource &&
        organizationId !== null &&
        selectedPhoebeRunId !== null &&
        selectedRunIsAgentV3 === true;
    const selectedCompatAgentConversationId =
        shouldUseRunNativeConversation &&
        organizationConversation &&
        'agent_conversation_id' in organizationConversation
            ? (organizationConversation.agent_conversation_id ?? null)
            : null;
    const requestedPhoebeRunId =
        phoebeRunIdOverride ??
        (selectedPhoebeRunId === suppressedPhoebeRunId
            ? null
            : selectedPhoebeRunId);
    const shouldUseEventReadForAgentConversation =
        organizationAgentExecutionBackend === 'event' &&
        organizationConversationPhoebeRunId !== null;
    const shouldUsePhoebeEventRead =
        !isAdminConversationSource &&
        (shouldUseRunNativeConversation ||
            shouldUseEventReadForAgentConversation);
    useEffect(() => {
        setPhoebeRunIdOverride(null);
        setSuppressedPhoebeRunId(null);
    }, [selectedConversationId, shouldUsePhoebeEventRead]);
    useEffect(() => {
        if (
            suppressedPhoebeRunId !== null &&
            selectedPhoebeRunId !== suppressedPhoebeRunId
        ) {
            setSuppressedPhoebeRunId(null);
        }
    }, [selectedPhoebeRunId, suppressedPhoebeRunId]);
    const phoebeAgentRunId = shouldUsePhoebeEventRead
        ? requestedPhoebeRunId
        : null;
    const websocketStreamRun =
        streamState.conversationId === selectedConversationId &&
        streamState.currentRunId
            ? (streamState.runsById[streamState.currentRunId] ?? null)
            : null;
    const phoebeRunEventStream = usePhoebeAgentRunEventStream({
        organizationId: orgConversationOrganizationId,
        phoebeAgentRunId,
        enabled:
            shouldUsePhoebeEventRead &&
            !!orgConversationOrganizationId &&
            !!phoebeAgentRunId,
    });
    // Multiplayer presence only applies to shared chats (outreach chats, which
    // any org member can open). General chats are owner-scoped, so there is
    // never a second participant to attribute or to see typing.
    const { typingUsers: chatTypingUsers, notifyTyping: notifyChatTyping } =
        usePhoebeChatTyping({
            organizationId: orgConversationOrganizationId,
            runId: phoebeAgentRunId,
            currentUserId: user?.id ?? null,
            enabled: isOutreachTarget && canSendInSelectedConversation,
        });
    const { viewers: chatViewers } = usePhoebeChatPresence({
        organizationId: orgConversationOrganizationId,
        // Presence belongs to the selected run regardless of which renderer
        // reads its transcript. Some client organizations still use the
        // legacy conversation renderer even though that conversation already
        // has a Phoebe run id.
        runId: requestedPhoebeRunId,
        currentUserId: user?.id ?? null,
        enabled: isOutreachTarget && !isAdminConversationSource,
        visible: isVisible,
    });
    const firstTypingUser = chatTypingUsers[0] ?? null;
    const phoebeRunEventFeed = useMemo(
        () =>
            shouldUsePhoebeEventRead && phoebeAgentRunId
                ? {
                      events: phoebeRunEventStream.events,
                      pending_events: phoebeRunEventStream.pendingEvents,
                  }
                : null,
        [
            phoebeAgentRunId,
            phoebeRunEventStream.events,
            phoebeRunEventStream.pendingEvents,
            shouldUsePhoebeEventRead,
        ],
    );
    const providerErrorBadge = useMemo<ProviderErrorBadgeState | null>(() => {
        if (!shouldUsePhoebeEventRead) {
            return null;
        }

        const latestLifecycleEvent = [...phoebeRunEventStream.events]
            .reverse()
            .find(
                (event) =>
                    event.type === 'run_started' ||
                    event.type === 'run_completed' ||
                    event.type === 'run_interrupted' ||
                    event.type === 'run_cancelled' ||
                    event.type === 'run_failed',
            );
        if (latestLifecycleEvent?.type === 'run_failed') {
            const payload = latestLifecycleEvent.payload;
            const recoverability =
                typeof payload['error_recoverability'] === 'string'
                    ? payload['error_recoverability']
                    : null;
            const errorType =
                typeof payload['error_type'] === 'string'
                    ? payload['error_type']
                    : null;
            if (
                recoverability !== 'irrecoverable' &&
                !errorType?.startsWith('provider_http_')
            ) {
                return null;
            }

            const cause =
                typeof payload['error_message'] === 'string'
                    ? payload['error_message']
                    : null;
            return {
                kind: 'irrecoverable',
                label: 'Error',
                detail: cause,
            };
        }

        return null;
    }, [phoebeRunEventStream.events, shouldUsePhoebeEventRead]);
    const refreshPhoebeRunEvents = phoebeRunEventStream.refresh;
    const eventStreamRunStateMatchesCurrentRun =
        shouldUsePhoebeEventRead &&
        phoebeAgentRunId !== null &&
        phoebeRunEventStream.runState?.id === phoebeAgentRunId;
    const currentRunEventStreamIsBusy =
        eventStreamRunStateMatchesCurrentRun && phoebeRunEventStream.isBusy;
    const currentRunEventStreamIsGenerating =
        eventStreamRunStateMatchesCurrentRun &&
        phoebeRunEventStream.isGenerating;
    // After an outreach is drafted from this chat, surface the existing
    // GeneralOutreachProgressCard (streams the real outreach-analysis run,
    // showing "Finding caregivers" gated on the true agent_status and the real
    // suggested_caregivers when done). Build its seed from the latest
    // `draft_agent_outreach` output, cleared once the coordinator sends another
    // message. Extension-only (see the render gate) so the general chat is
    // untouched. Replaces the removed auto-surface poke.
    const pendingOutreachProgressSeed =
        useMemo<GeneralOutreachProgressSeed | null>(() => {
            let seed: GeneralOutreachProgressSeed | null = null;
            for (const event of phoebeRunEventStream.events) {
                if (event.type === 'user_message') {
                    seed = null;
                } else if (event.type === 'tool_output') {
                    const output = event.payload['output_json'];
                    if (
                        typeof event.payload['tool_name'] === 'string' &&
                        event.payload['tool_name'] === 'draft_agent_outreach' &&
                        isRecord(output) &&
                        output['outreach_agent_state'] === 'queued'
                    ) {
                        seed = getGeneralOutreachProgressSeedFromTool({
                            toolName: 'draft_agent_outreach',
                            isComplete: true,
                            output,
                        });
                    }
                }
            }
            return seed;
        }, [phoebeRunEventStream.events]);
    const currentRunEventStreamIsWaitingForApproval =
        eventStreamRunStateMatchesCurrentRun &&
        phoebeRunEventStream.isWaitingForApproval;
    const currentRunEventStreamIsWaitingForUserInput =
        eventStreamRunStateMatchesCurrentRun &&
        phoebeRunEventStream.isWaitingForUserInput;
    const isInputBlockedByPendingApproval =
        eventStreamRunStateMatchesCurrentRun &&
        phoebeRunEventStream.runState?.has_pending_approval === true;
    const currentRunDurablyActive =
        eventStreamRunStateMatchesCurrentRun &&
        phoebeRunEventStream.runState !== null &&
        (phoebeRunEventStream.runState.status === 'running' ||
            phoebeRunEventStream.runState.has_pending_work ||
            phoebeRunEventStream.runState.has_active_lease ||
            phoebeRunEventStream.runState.is_generating ||
            phoebeRunEventStream.runState.interrupt_requested);
    const currentRunEventStreamIsAwaitingInstructions =
        eventStreamRunStateMatchesCurrentRun &&
        phoebeRunEventStream.runState !== null &&
        !phoebeRunEventStream.runState.has_pending_work &&
        !phoebeRunEventStream.runState.has_active_lease &&
        !phoebeRunEventStream.runState.is_generating &&
        !phoebeRunEventStream.runState.interrupt_requested &&
        !phoebeRunEventStream.runState.has_pending_approval;
    const shouldHonorLiveRunState =
        !shouldUsePhoebeEventRead ||
        !eventStreamRunStateMatchesCurrentRun ||
        currentRunDurablyActive;
    const currentStreamRun = shouldHonorLiveRunState
        ? websocketStreamRun
        : null;
    const latestPhoebeRunEventOffset = useMemo(
        () =>
            Math.max(
                -1,
                ...phoebeRunEventStream.events.map(
                    (event) => event.event_offset,
                ),
            ),
        [phoebeRunEventStream.events],
    );
    const hasNewRunContentAfterOptimisticThinking = useMemo(() => {
        if (!optimisticThinkingMarker) {
            return false;
        }
        if (
            optimisticThinkingMarker.runId !== null &&
            optimisticThinkingMarker.runId !== phoebeAgentRunId
        ) {
            return false;
        }

        return phoebeRunEventStream.events.some(
            (event) =>
                event.event_offset >
                    optimisticThinkingMarker.afterEventOffset &&
                isOptimisticThinkingHandoffEvent(event),
        );
    }, [
        optimisticThinkingMarker,
        phoebeAgentRunId,
        phoebeRunEventStream.events,
    ]);
    const hasEventStreamUserMessageAfterOptimisticThinking = useMemo(() => {
        if (!optimisticThinkingMarker) {
            return false;
        }
        if (
            optimisticThinkingMarker.runId !== null &&
            optimisticThinkingMarker.runId !== phoebeAgentRunId
        ) {
            return false;
        }

        const optimisticText = optimisticThinkingMarker.text.trim();
        const optimisticExternalRef =
            optimisticThinkingMarker.clientOperationId === null
                ? null
                : `api_chat_send:${optimisticThinkingMarker.clientOperationId}`;
        const hasCommittedUserMessage = phoebeRunEventStream.events.some(
            (event) => {
                if (
                    event.event_offset <=
                        optimisticThinkingMarker.afterEventOffset ||
                    event.type !== 'user_message'
                ) {
                    return false;
                }

                return (
                    String(event.payload['text'] ?? '').trim() ===
                    optimisticText
                );
            },
        );
        if (hasCommittedUserMessage) {
            return true;
        }

        return phoebeRunEventStream.pendingEvents.some((pendingEvent) => {
            if (
                pendingEvent.status !== 'pending' ||
                pendingEvent.type !== 'user_message'
            ) {
                return false;
            }

            if (optimisticExternalRef !== null) {
                return pendingEvent.external_ref === optimisticExternalRef;
            }

            return (
                String(pendingEvent.payload['text'] ?? '').trim() ===
                optimisticText
            );
        });
    }, [
        optimisticThinkingMarker,
        phoebeAgentRunId,
        phoebeRunEventStream.events,
        phoebeRunEventStream.pendingEvents,
    ]);
    const hasQuestionAnswerCompletionEvent = useMemo(() => {
        let answerResolvedOffset: number | null = null;
        if (awaitingQuestionAnswerCallId !== null) {
            const answerOutputEvent = [...phoebeRunEventStream.events]
                .reverse()
                .find(
                    (event) =>
                        event.type === 'tool_output' &&
                        event.payload['call_id'] ===
                            awaitingQuestionAnswerCallId,
                );
            answerResolvedOffset = answerOutputEvent?.event_offset ?? null;
        }

        if (answerResolvedOffset === null) {
            const latestQuestionAnswerEvent = [...phoebeRunEventStream.events]
                .reverse()
                .find(
                    (event) =>
                        event.type === 'user_question_answered' ||
                        event.type === 'user_question_cancelled',
                );
            if (!latestQuestionAnswerEvent) {
                return false;
            }
            const hasNewUserMessageAfterQuestionAnswer =
                phoebeRunEventStream.events.some(
                    (event) =>
                        event.type === 'user_message' &&
                        event.event_offset >
                            latestQuestionAnswerEvent.event_offset,
                );
            if (hasNewUserMessageAfterQuestionAnswer) {
                return false;
            }
            answerResolvedOffset = latestQuestionAnswerEvent.event_offset;
        }

        if (answerResolvedOffset === null) {
            return false;
        }

        return phoebeRunEventStream.events.some(
            (event) =>
                event.event_offset > answerResolvedOffset &&
                (event.type === 'assistant_text' ||
                    event.type === 'run_completed' ||
                    event.type === 'run_failed' ||
                    event.type === 'run_interrupted' ||
                    event.type === 'run_cancelled'),
        );
    }, [awaitingQuestionAnswerCallId, phoebeRunEventStream.events]);
    const eventFeedLatestLifecycleIsTerminal = useMemo(() => {
        const latestLifecycleEvent = [...phoebeRunEventStream.events]
            .reverse()
            .find(
                (event) =>
                    event.type === 'run_started' ||
                    event.type === 'run_completed' ||
                    event.type === 'run_failed' ||
                    event.type === 'run_interrupted' ||
                    event.type === 'run_cancelled',
            );
        return (
            latestLifecycleEvent?.type === 'run_completed' ||
            latestLifecycleEvent?.type === 'run_failed' ||
            latestLifecycleEvent?.type === 'run_interrupted' ||
            latestLifecycleEvent?.type === 'run_cancelled'
        );
    }, [phoebeRunEventStream.events]);
    const eventFeedHasAssistantResponseAfterLatestRunStart = useMemo(() => {
        let latestRunStartedOffset: number | null = null;
        let hasAssistantTextAfterRunStart = false;

        for (const event of phoebeRunEventStream.events) {
            if (event.type === 'run_started') {
                latestRunStartedOffset = event.event_offset;
                hasAssistantTextAfterRunStart = false;
            } else if (
                latestRunStartedOffset !== null &&
                event.event_offset > latestRunStartedOffset &&
                event.type === 'assistant_text'
            ) {
                hasAssistantTextAfterRunStart = true;
            }
        }

        return hasAssistantTextAfterRunStart;
    }, [phoebeRunEventStream.events]);
    const effectiveIsAwaitingRunCompletion =
        isAwaitingRunCompletion &&
        !hasQuestionAnswerCompletionEvent &&
        !(
            optimisticThinkingMarker === null &&
            eventFeedLatestLifecycleIsTerminal
        );
    // End-of-run debrief ("Review this chat"): a training-mode card at the
    // bottom of a terminal general/outreach-analysis run whose submission is
    // the run's review. Hidden once the viewer has any feedback row for the
    // run.
    const runDebriefRunTypeEligible =
        organizationRun?.run_type === 'general' ||
        organizationRun?.run_type === 'outreach_analysis';
    // Strictly training-mode-only — no dev-environment escape, so local
    // behavior matches production exactly.
    const runDebriefReviewModeEnabled = trainingMode.isInTrainingMode;
    const runDebriefBaseEnabled =
        !readOnly &&
        trainingMode.isReady &&
        runDebriefReviewModeEnabled &&
        runDebriefRunTypeEligible &&
        !!orgConversationOrganizationId &&
        !!selectedRunNativeConversationId;
    // The live event stream only carries events observed since this
    // subscription connected, so it never reports terminal for a run that
    // already finished before the viewer opened (or reopened) the chat.
    // Fall back to the polled run status, which reflects persisted state
    // regardless of when the stream connected.
    const runDebriefRunIsFeedbackEligible =
        eventFeedLatestLifecycleIsTerminal ||
        (eventFeedHasAssistantResponseAfterLatestRunStart &&
            currentRunEventStreamIsAwaitingInstructions) ||
        (organizationRun !== undefined &&
            organizationRun !== null &&
            isTerminalPhoebeAgentRunStatus(organizationRun.status));
    const runDebriefWaitingForOutreachRecommendations = useMemo(() => {
        let queuedOutreachOffset: number | null = null;
        let recommendationsReadyOffset: number | null = null;
        let recommendationsReplyArrived = false;

        for (const event of phoebeRunEventStream.events) {
            if (event.type === 'tool_output') {
                const output = event.payload['output_json'];
                if (
                    event.payload['tool_name'] ===
                        DRAFT_AGENT_OUTREACH_TOOL_NAME &&
                    isRecord(output) &&
                    output['outreach_agent_state'] === 'queued'
                ) {
                    queuedOutreachOffset = event.event_offset;
                    recommendationsReadyOffset = null;
                    recommendationsReplyArrived = false;
                }
            }

            if (
                queuedOutreachOffset !== null &&
                event.event_offset > queuedOutreachOffset &&
                event.type === 'poke' &&
                event.payload['reason'] ===
                    OUTREACH_RECOMMENDATIONS_READY_REASON
            ) {
                recommendationsReadyOffset = event.event_offset;
                recommendationsReplyArrived = false;
            }

            if (
                recommendationsReadyOffset !== null &&
                event.event_offset > recommendationsReadyOffset &&
                (event.type === 'assistant_text' ||
                    event.type === 'run_completed')
            ) {
                recommendationsReplyArrived = true;
            }
        }

        return queuedOutreachOffset !== null && !recommendationsReplyArrived;
    }, [phoebeRunEventStream.events]);
    // Feedback comes strictly at the end of a chat. When Phoebe's latest
    // reply is itself an open ask — it ends with a question ("Want me to
    // start an outreach?") or references shifts (which render action chips
    // like "Create outreach") — the scheduler still has a move to make, so
    // the debrief stays hidden until they respond or the offer goes stale.
    const runDebriefBlockedByOpenOffer = useMemo(() => {
        for (
            let index = phoebeRunEventStream.events.length - 1;
            index >= 0;
            index--
        ) {
            const event = phoebeRunEventStream.events[index];
            if (event === undefined) {
                continue;
            }
            // A newer scheduler message answers the offer; older assistant
            // questions no longer block.
            if (event.type === 'user_message') {
                return false;
            }
            if (event.type === 'assistant_text') {
                const text = event.payload['text'];
                if (typeof text !== 'string') {
                    return false;
                }
                return text.trimEnd().endsWith('?') || /\[\[shift:/i.test(text);
            }
        }
        return false;
    }, [phoebeRunEventStream.events]);
    const recommendationReviewPromptContent =
        useMemo<RunDebriefPromptContent | null>(() => {
            let latestInteractionStartOffset: number | null = null;
            let suggestionStatus: string | null = null;
            let assistantTextAfterSuggestions = '';

            for (const event of phoebeRunEventStream.events) {
                if (
                    event.type === 'user_message' ||
                    event.type === 'outreach_kickoff'
                ) {
                    latestInteractionStartOffset = event.event_offset;
                    suggestionStatus = null;
                    assistantTextAfterSuggestions = '';
                    continue;
                }

                if (
                    latestInteractionStartOffset !== null &&
                    event.event_offset <= latestInteractionStartOffset
                ) {
                    continue;
                }

                if (event.type === 'tool_output') {
                    const output = event.payload['output_json'];
                    if (
                        event.payload['tool_name'] ===
                            ADD_OUTREACH_SUGGESTIONS_TOOL_NAME &&
                        isRecord(output)
                    ) {
                        suggestionStatus =
                            typeof output['status'] === 'string'
                                ? output['status']
                                : null;
                        assistantTextAfterSuggestions = '';
                    }
                    continue;
                }

                if (
                    suggestionStatus !== null &&
                    event.type === 'assistant_text' &&
                    typeof event.payload['text'] === 'string'
                ) {
                    assistantTextAfterSuggestions = event.payload['text'];
                }
            }

            if (suggestionStatus === null || !assistantTextAfterSuggestions) {
                return null;
            }

            return buildRecommendationReviewPromptContent({
                assistantText: assistantTextAfterSuggestions,
                suggestionStatus,
            });
        }, [phoebeRunEventStream.events]);
    // A question is pending only until it is resolved. The durable feed
    // keeps the original pending_user_input tool_call forever, so a plain
    // `.some()` would read "pending" for the rest of the chat's life and,
    // among other things, permanently suppress the end-of-chat debrief.
    // Only one question card can be open at a time (it pauses the chat),
    // so an ordered scan is sufficient.
    const eventFeedHasPendingUserQuestion = useMemo(() => {
        let pending = false;
        for (const event of phoebeRunEventStream.events) {
            if (
                event.type === 'tool_call' &&
                isUserInputToolName(event.payload['tool_name']) &&
                event.payload['tool_status'] === 'pending_user_input'
            ) {
                pending = true;
            } else if (
                (event.type === 'tool_output' &&
                    isUserInputToolName(event.payload['tool_name'])) ||
                event.type === 'user_question_answered' ||
                event.type === 'user_question_cancelled'
            ) {
                pending = false;
            }
        }
        return pending;
    }, [phoebeRunEventStream.events]);
    const { data: runDebriefFeedback, mutate: mutateRunDebriefFeedback } =
        useOrganizationRunFeedback(
            orgConversationOrganizationId,
            selectedRunNativeConversationId,
            runDebriefBaseEnabled,
        );
    const { data: runDebrief } = useOrganizationRunDebrief(
        orgConversationOrganizationId,
        selectedRunNativeConversationId,
        runDebriefBaseEnabled && runDebriefRunIsFeedbackEligible,
    );
    // 'submitted' plays the "Thank you!" acknowledgment; 'dismissed' hides it.
    const [runDebriefOutcome, setRunDebriefOutcome] = useState<
        'submitted' | 'dismissed' | null
    >(null);
    const [runDebriefAcknowledgement, setRunDebriefAcknowledgement] =
        useState<RunDebriefAcknowledgementState | null>(null);
    const [runDebriefSaving, setRunDebriefSaving] = useState(false);
    useEffect(() => {
        setRunDebriefOutcome(null);
        setRunDebriefAcknowledgement(null);
    }, [selectedRunNativeConversationId]);
    const handleRunDebriefSubmit = useCallback(
        async (submission: RunDebriefSubmission) => {
            if (
                !orgConversationOrganizationId ||
                !selectedRunNativeConversationId
            ) {
                return;
            }
            setRunDebriefSaving(true);
            try {
                await saveRunFeedback({
                    organizationId: orgConversationOrganizationId,
                    runId: selectedRunNativeConversationId,
                    status: 'submitted',
                    label: submission.label,
                    comment: submission.comment,
                    decisionVerdicts: submission.decisionVerdicts,
                });
                setRunDebriefAcknowledgement({
                    createdAt: new Date().toISOString(),
                    notes: getRunDebriefSubmissionNotes(submission),
                });
                setRunDebriefOutcome('submitted');
                void mutateRunDebriefFeedback();
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error(
                        'Could not save your review. Please try again.',
                        {
                            persistNotification: false,
                        },
                    );
                }
            } finally {
                setRunDebriefSaving(false);
            }
        },
        [
            orgConversationOrganizationId,
            selectedRunNativeConversationId,
            mutateRunDebriefFeedback,
        ],
    );
    const handleRunDebriefSkip = useCallback(async () => {
        if (
            !orgConversationOrganizationId ||
            !selectedRunNativeConversationId
        ) {
            return;
        }
        setRunDebriefSaving(true);
        try {
            await saveRunFeedback({
                organizationId: orgConversationOrganizationId,
                runId: selectedRunNativeConversationId,
                status: 'dismissed',
            });
            setRunDebriefOutcome('dismissed');
            void mutateRunDebriefFeedback();
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error('Could not skip the review. Please try again.', {
                    persistNotification: false,
                });
            }
        } finally {
            setRunDebriefSaving(false);
        }
    }, [
        orgConversationOrganizationId,
        selectedRunNativeConversationId,
        mutateRunDebriefFeedback,
    ]);
    const showRunDebrief =
        runDebriefBaseEnabled &&
        runDebriefRunIsFeedbackEligible &&
        !runDebriefWaitingForOutreachRecommendations &&
        !runDebriefBlockedByOpenOffer &&
        !currentRunEventStreamIsWaitingForUserInput &&
        !eventFeedHasPendingUserQuestion &&
        runDebriefOutcome !== 'dismissed' &&
        runDebriefOutcome !== 'submitted' &&
        runDebriefFeedback === null;
    const startOptimisticThinking = useCallback(
        (
            text: string,
            clientOperationId: string | null,
            hiddenFromTranscript: boolean = false,
        ) => {
            if (!shouldUsePhoebeEventRead) {
                return;
            }
            if (currentRunEventStreamIsWaitingForApproval) {
                return;
            }
            if (currentRunEventStreamIsWaitingForUserInput) {
                return;
            }

            const displayMode: 'inline' | 'pill' =
                currentRunEventStreamIsGenerating || currentRunEventStreamIsBusy
                    ? 'pill'
                    : 'inline';
            setOptimisticThinkingMarker({
                runId: phoebeAgentRunId,
                afterEventOffset: latestPhoebeRunEventOffset,
                clientOperationId,
                text,
                createdAt: new Date().toISOString(),
                displayMode,
                hiddenFromTranscript,
            });
        },
        [
            currentRunEventStreamIsBusy,
            currentRunEventStreamIsGenerating,
            currentRunEventStreamIsWaitingForApproval,
            currentRunEventStreamIsWaitingForUserInput,
            latestPhoebeRunEventOffset,
            phoebeAgentRunId,
            shouldUsePhoebeEventRead,
        ],
    );
    const conversation = useMemo(
        () =>
            isAdminConversationSource
                ? mapAdminTraceToConversation(adminTraceConversation)
                : organizationConversation,
        [
            adminTraceConversation,
            isAdminConversationSource,
            organizationConversation,
        ],
    );
    // Persisted turns exist only on the legacy conversation read and the admin
    // trace adapter. Run-native chats render from the run event feed instead.
    const legacyTurns =
        conversation && 'turns' in conversation ? conversation.turns : null;
    const thinkingTipConversationTurnCount =
        legacyTurns !== null
            ? legacyTurns.length
            : (phoebeRunEventFeed?.events.filter(
                  (event) => event.type === 'user_message',
              ).length ?? 0);
    const thinkingTipIndex = deriveChatThinkingTipIndex({
        conversationTurnCount: thinkingTipConversationTurnCount,
        optimisticTipIndex: optimisticThinkingTipIndex,
    });
    const advanceThinkingTipIndex = useCallback(() => {
        setOptimisticThinkingTipIndex(
            (current) =>
                deriveChatThinkingTipIndex({
                    conversationTurnCount: thinkingTipConversationTurnCount,
                    optimisticTipIndex: current,
                }) + 1,
        );
    }, [thinkingTipConversationTurnCount]);
    useEffect(() => {
        if (
            optimisticThinkingTipIndex !== null &&
            thinkingTipConversationTurnCount >= optimisticThinkingTipIndex
        ) {
            setOptimisticThinkingTipIndex(null);
        }
    }, [optimisticThinkingTipIndex, thinkingTipConversationTurnCount]);
    const isConversationLoading = isAdminConversationSource
        ? isAdminConversationLoading
        : isOrganizationConversationLoading ||
          (shouldUsePhoebeEventRead &&
              !!phoebeAgentRunId &&
              phoebeRunEventStream.isLoading);
    const rawConversationError = isAdminConversationSource
        ? adminConversationError
        : (phoebeRunEventStream.error ?? organizationConversationError);
    const currentConversationIsReady =
        Boolean(conversation) &&
        (!shouldUsePhoebeEventRead ||
            phoebeRunEventStream.connectionStatus === 'open');
    const conversationError = currentConversationIsReady
        ? null
        : rawConversationError;
    const refreshConversation = useCallback(() => {
        if (isAdminConversationSource) {
            return mutateAdminConversation();
        }

        if (shouldUsePhoebeEventRead && phoebeAgentRunId) {
            refreshPhoebeRunEvents();
        }

        return mutateOrganizationConversation();
    }, [
        isAdminConversationSource,
        mutateAdminConversation,
        mutateOrganizationConversation,
        phoebeAgentRunId,
        refreshPhoebeRunEvents,
        shouldUsePhoebeEventRead,
    ]);
    // Held in a ref so the in-flight poll below keys its interval on whether a
    // run is running, not on this callback's identity — otherwise a dependency
    // changing mid-run tears the interval down and restarts the delay.
    const refreshConversationRef = useRef(refreshConversation);
    useEffect(() => {
        refreshConversationRef.current = refreshConversation;
    }, [refreshConversation]);

    // The pin the user chose; `null` means no pin (the server resolves).
    const pinnedAgentModel =
        conversation?.active_model ??
        (selectedConversationId !== null ? null : pendingAgentModel);
    const resolvedAgentModel = conversation?.resolved_model ?? null;
    const modelUpdateRunId =
        shouldUseRunNativeConversation && isOutreachTarget
            ? effectiveOutreachTargetRunId
            : shouldUseRunNativeConversation
              ? selectedConversationId
              : null;
    const modelUpdateBackendKind: ChatBackendKind =
        !isAdminConversationSource && modelUpdateRunId !== null
            ? 'run_native'
            : effectiveChatBackendKind;
    const modelUpdateAgentConversationId =
        !isAdminConversationSource &&
        modelUpdateBackendKind === 'legacy_agent_conversation'
            ? selectedConversationId
            : null;
    // Fold the PATCH response's {pin, resolved, source} triple into the cached
    // conversation so the picker settles before the refetch lands.
    const applyAgentModelUpdate = useCallback(
        (response: unknown) => {
            const update = readAgentModelUpdate(response);
            if (update === null) {
                return;
            }
            if (isAdminConversationSource) {
                void mutateAdminConversation(
                    (current) =>
                        current ? { ...current, ...update } : current,
                    {
                        revalidate: false,
                    },
                );
                return;
            }
            if (shouldUseRunNativeConversation) {
                void mutateOrganizationRun(
                    (current) =>
                        current ? { ...current, ...update } : current,
                    {
                        revalidate: false,
                    },
                );
                return;
            }
            void mutateLegacyOrganizationConversation(
                (current: AgentConversationRead | undefined) =>
                    current ? { ...current, ...update } : current,
                { revalidate: false },
            );
        },
        [
            isAdminConversationSource,
            mutateAdminConversation,
            mutateLegacyOrganizationConversation,
            mutateOrganizationRun,
            shouldUseRunNativeConversation,
        ],
    );
    const updateActiveAgentModel = useCallback(
        async (activeModel: string | null) => {
            // Ref guard: two same-tick picks both read a stale
            // isUpdatingModel closure before React re-renders.
            if (isUpdatingModelRef.current) {
                return;
            }
            if (!selectedConversationId) {
                // An admin trace chat always has a conversation; a callout chat
                // may not yet, so the pick is held until its run is ensured.
                if (isAdminConversationSource) {
                    return;
                }
                setPendingAgentModel(activeModel);
                return;
            }
            if (!organizationId && !isAdminConversationSource) {
                return;
            }

            isUpdatingModelRef.current = true;
            setIsUpdatingModel(true);
            setErrorMessage(null);
            try {
                const response = await updateChatBackendModel({
                    backendKind: modelUpdateBackendKind,
                    organizationId,
                    runId: modelUpdateRunId,
                    agentConversationId: modelUpdateAgentConversationId,
                    adminTraceConversationId: isAdminConversationSource
                        ? selectedConversationId
                        : null,
                    activeModel,
                });
                applyAgentModelUpdate(response);
                if (!isAdminConversationSource) {
                    void mutateConversations();
                }
                await refreshConversation();
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    const message =
                        error instanceof Error
                            ? error.message
                            : 'Failed to update model.';
                    setErrorMessage(message);
                    toast.error(message, { persistNotification: false });
                }
            } finally {
                isUpdatingModelRef.current = false;
                setIsUpdatingModel(false);
            }
        },
        [
            applyAgentModelUpdate,
            isAdminConversationSource,
            modelUpdateAgentConversationId,
            modelUpdateBackendKind,
            modelUpdateRunId,
            mutateConversations,
            organizationId,
            refreshConversation,
            selectedConversationId,
        ],
    );

    // Applies a pick made before the chat had a run. The send path and the
    // run-appeared effect below can both call this; the latch keeps it to one
    // PATCH and hands the loser the same promise, so a send still waits for
    // the pin to land before its message is enqueued.
    const applyHeldAgentModel = useCallback(
        (
            run: NonNullable<typeof outreachTargetRun>,
            heldModel: string,
        ): Promise<void> => {
            if (!organizationId) {
                return Promise.resolve();
            }
            const inFlight = heldPinRef.current;
            if (inFlight?.runId === run.id) {
                return inFlight.promise;
            }
            const apply = async () => {
                // Show the pick straight away in both caches — once the run is
                // selected the picker reads the run detail, not the
                // by-outreach lookup. The PATCH fold replaces these with the
                // server's values, and a failure reverts them.
                const optimisticRun = {
                    ...run,
                    active_model: heldModel,
                    resolved_model: heldModel,
                };
                await mutateOutreachTargetRun(optimisticRun, {
                    revalidate: false,
                });
                void mutateOrganizationRun(
                    (current) => ({ ...(current ?? run), ...optimisticRun }),
                    {
                        revalidate: false,
                    },
                );
                try {
                    const response = await updateChatBackendModel({
                        backendKind: 'run_native',
                        organizationId,
                        runId: run.id,
                        activeModel: heldModel,
                    });
                    const update = readAgentModelUpdate(response);
                    if (update !== null) {
                        await mutateOutreachTargetRun(
                            (current) => ({ ...(current ?? run), ...update }),
                            {
                                revalidate: false,
                            },
                        );
                        void mutateOrganizationRun(
                            (current) => ({ ...(current ?? run), ...update }),
                            {
                                revalidate: false,
                            },
                        );
                    }
                } catch (error) {
                    // Drop the latch and the optimistic seed so the pin is not
                    // left half-applied and a later attempt can retry it.
                    heldPinRef.current = null;
                    await mutateOutreachTargetRun(run, { revalidate: false });
                    void mutateOrganizationRun(
                        (current) => ({ ...(current ?? run), ...run }),
                        {
                            revalidate: false,
                        },
                    );
                    if (!isTransparentRateLimitError(error)) {
                        toast.error(
                            error instanceof Error
                                ? error.message
                                : 'Failed to update model.',
                            {
                                persistNotification: false,
                            },
                        );
                    }
                }
                setPendingAgentModel(null);
            };
            const promise = apply();
            heldPinRef.current = { runId: run.id, promise };
            return promise;
        },
        [mutateOrganizationRun, mutateOutreachTargetRun, organizationId],
    );

    useEffect(() => {
        if (selectedConversationId === null) {
            return;
        }
        // The run this pick was made for can appear without a send (the
        // backend starts its own analysis run), so apply the pick instead of
        // dropping it. Any other selection retires the pick.
        if (
            pendingAgentModel !== null &&
            userIsAdmin &&
            isOutreachTarget &&
            shouldUseRunNativeConversation &&
            outreachTargetRun !== undefined &&
            outreachTargetRun.id === selectedConversationId
        ) {
            void applyHeldAgentModel(outreachTargetRun, pendingAgentModel);
            return;
        }
        setPendingAgentModel(null);
    }, [
        applyHeldAgentModel,
        isOutreachTarget,
        outreachTargetRun,
        pendingAgentModel,
        selectedConversationId,
        shouldUseRunNativeConversation,
        userIsAdmin,
    ]);

    useEffect(() => {
        setRetainedStreamRun(null);
    }, [selectedConversationId]);

    useEffect(() => {
        if (currentStreamRun?.status === 'running') {
            setRetainedStreamRun(currentStreamRun);
            return;
        }

        if (!conversation?.agent_thinking || !shouldHonorLiveRunState) {
            setRetainedStreamRun(null);
        }
    }, [
        conversation?.agent_thinking,
        currentStreamRun,
        shouldHonorLiveRunState,
    ]);

    const displayStreamRun = useMemo(
        () =>
            getDisplayStreamRun({
                currentStreamRun,
                retainedStreamRun,
                isAgentThinking: Boolean(
                    conversation?.agent_thinking && shouldHonorLiveRunState,
                ),
            }),
        [
            conversation?.agent_thinking,
            currentStreamRun,
            retainedStreamRun,
            shouldHonorLiveRunState,
        ],
    );

    const appendTurnOptimistically = useCallback(
        (result: unknown, userText?: string) => {
            if (!result) {
                return;
            }

            if (isAdminConversationSource) {
                const adminTurn = result as AgentTraceDetail['turns'][number];
                void mutateAdminConversation(
                    (current) =>
                        current
                            ? {
                                  ...current,
                                  turns: [...current.turns, adminTurn],
                                  agent_thinking: true,
                                  updated_at: adminTurn.updated_at,
                                  last_turn_at: adminTurn.created_at,
                              }
                            : current,
                    { revalidate: false },
                );
                return;
            }

            const action = getOrganizationAgentMessageCreateResponseAction(
                result as OrganizationAgentMessageCreateResponse,
            );

            if (action.kind === 'event-layer') {
                setPhoebeRunIdOverride(action.phoebeRunId);
                setOptimisticThinkingMarker((current) =>
                    current
                        ? {
                              ...current,
                              runId: action.phoebeRunId,
                          }
                        : current,
                );
                if (shouldUsePhoebeEventRead) {
                    refreshPhoebeRunEvents();
                }
                return;
            }

            if (action.kind === 'invalid') {
                setOptimisticThinkingMarker(null);
                setIsAwaitingRunCompletion(false);
                setHasRunCompletionSignal(false);
                setErrorMessage(SEND_MESSAGE_ERROR_MESSAGE);
                toast.error(SEND_MESSAGE_ERROR_MESSAGE, {
                    persistNotification: false,
                });
                return;
            }

            if (!userText) {
                return;
            }
            if (shouldUseRunNativeConversation) {
                return;
            }

            setOptimisticThinkingMarker(null);
            void mutateLegacyOrganizationConversation(
                (current: AgentConversationRead | undefined) =>
                    current
                        ? {
                              ...current,
                              turns: [
                                  ...current.turns,
                                  buildOptimisticAgentConversationTurn({
                                      conversation: current,
                                      turnId: action.turnId,
                                      text: userText,
                                  }),
                              ],
                          }
                        : current,
                { revalidate: false },
            );
        },
        [
            isAdminConversationSource,
            mutateAdminConversation,
            mutateLegacyOrganizationConversation,
            refreshPhoebeRunEvents,
            setErrorMessage,
            setHasRunCompletionSignal,
            setIsAwaitingRunCompletion,
            shouldUsePhoebeEventRead,
            shouldUseRunNativeConversation,
        ],
    );

    const throttledMutateConversation = useThrottledCallback(
        () => refreshConversation(),
        10_000,
    );

    useEffect(() => {
        setStreamState(EMPTY_STREAM_STATE);
        setIsAwaitingRunCompletion(false);
        setHasRunCompletionSignal(false);
        setAwaitingQuestionAnswerCallId(null);
        setOptimisticThinkingMarker(null);
        setOptimisticThinkingTipIndex(null);
        setExpandedLiveRunId(null);
        setAutoExpandDismissedRunId(null);
    }, [selectedConversationId]);

    const transcript = useMemo<Array<TranscriptEntry>>(() => {
        // Pending user messages and queued sends render as pills above the
        // composer (see pendingMessagePillItems). Idle-mode optimistic sends
        // append a synthesized inline bubble below — see appendInlineOptimisticUserMessage.
        const appendInlineOptimisticUserMessage = (
            entries: Array<TranscriptEntry>,
        ): Array<TranscriptEntry> => {
            if (
                !shouldUsePhoebeEventRead ||
                optimisticThinkingMarker === null ||
                optimisticThinkingMarker.displayMode !== 'inline' ||
                hasEventStreamUserMessageAfterOptimisticThinking
            ) {
                return entries;
            }
            return [
                ...entries,
                {
                    kind: 'message',
                    id: `optimistic-user-message-${optimisticThinkingMarker.createdAt}`,
                    role: 'user',
                    content: optimisticThinkingMarker.text,
                    createdAt: optimisticThinkingMarker.createdAt,
                    hiddenFromTranscript:
                        optimisticThinkingMarker.hiddenFromTranscript,
                },
            ];
        };

        const buildLegacyTranscript = (): Array<TranscriptEntry> => {
            if (legacyTurns === null) {
                return [];
            }

            const entries: Array<TranscriptEntry> = [];

            const orderedTurns = [...legacyTurns].sort(
                (a, b) => a.sequence - b.sequence,
            );
            const sortItems = (
                items: AgentConversationTurnRead['items'],
            ): Array<AgentConversationTurnRead['items'][number]> =>
                [...items].sort((a, b) => {
                    const createdAtCompare = a.created_at.localeCompare(
                        b.created_at,
                    );
                    if (createdAtCompare !== 0) {
                        return createdAtCompare;
                    }
                    const indexCompare = a.index_in_turn - b.index_in_turn;
                    if (indexCompare !== 0) {
                        return indexCompare;
                    }
                    return a.id.localeCompare(b.id);
                });
            const legacyProjection = debugPanelOpen
                ? projectCompactedLegacyItems(
                      orderedTurns.flatMap((turn) =>
                          sortItems(turn.items).map((item) => ({
                              item: item as ToolAwareItem,
                              turnSequence: turn.sequence,
                          })),
                      ),
                  )
                : {
                      filteredItemIds: new Set<string>(),
                      liveCompactionItemIds: new Set<string>(),
                      anchorItemIdToCompactions: new Map<
                          string,
                          ReadonlyArray<LegacyOrderedItem>
                      >(),
                      replacedItemCountByCompactionItemId: new Map<
                          string,
                          number
                      >(),
                  };
            const emittedLegacyCompactionItemIds = new Set<string>();
            const pushLegacyCompactionMarker = ({
                item,
                turnSequence,
            }: LegacyOrderedItem) => {
                if (emittedLegacyCompactionItemIds.has(item.id)) {
                    return;
                }
                entries.push({
                    kind: 'compaction-marker',
                    id: item.id,
                    createdAt: item.created_at,
                    replacedItemCount:
                        legacyProjection.replacedItemCountByCompactionItemId.get(
                            item.id,
                        ) ?? getStringArray(item.replaces_item_ids).length,
                    summaryText: getOptionalString(item.text_content),
                    turnSequence,
                });
                emittedLegacyCompactionItemIds.add(item.id);
            };

            orderedTurns.forEach((turn) => {
                const orderedItems = sortItems(turn.items);

                const toolExecutions = new Map<
                    string,
                    {
                        call: ToolAwareItem;
                        result?: ToolAwareItem;
                    }
                >();
                orderedItems
                    .filter(
                        (item) =>
                            !legacyProjection.filteredItemIds.has(item.id) &&
                            item.type !== 'compaction',
                    )
                    .forEach((item) => {
                        const toolItem = item as ToolAwareItem;
                        if (item.type === 'function_call' && toolItem.call_id) {
                            toolExecutions.set(toolItem.call_id, {
                                call: toolItem,
                                result: toolExecutions.get(toolItem.call_id)
                                    ?.result,
                            });
                            return;
                        }
                        if (
                            item.type === 'function_output' &&
                            toolItem.call_id
                        ) {
                            const existing = toolExecutions.get(
                                toolItem.call_id,
                            );
                            if (existing) {
                                toolExecutions.set(toolItem.call_id, {
                                    ...existing,
                                    result: toolItem,
                                });
                            } else {
                                toolExecutions.set(toolItem.call_id, {
                                    call: toolItem,
                                    result: toolItem,
                                });
                            }
                        }
                    });

                const callEntries = new Map<string, ToolExecutionEntry>();
                toolExecutions.forEach((execution, callId) => {
                    callEntries.set(
                        callId,
                        buildToolExecutionEntry(
                            execution.call,
                            execution.result,
                            callId,
                            turn.sequence,
                        ),
                    );
                });

                const attachedCallIds = new Set<string>();
                let currentThought: ThoughtEntry | null = null;
                let latestThoughtInTurn: ThoughtEntry | null = null;

                orderedItems.forEach((item) => {
                    const toolItem = item as ToolAwareItem;
                    const anchoredCompactions =
                        legacyProjection.anchorItemIdToCompactions.get(
                            item.id,
                        ) ?? [];
                    anchoredCompactions.forEach(pushLegacyCompactionMarker);
                    if (legacyProjection.filteredItemIds.has(item.id)) {
                        return;
                    }

                    switch (item.type) {
                        case 'text': {
                            if (isRuntimeContextRawJson(item.raw_json)) {
                                break;
                            }
                            const content = (item.text_content ?? '').trim();
                            if (content.length === 0) {
                                break;
                            }

                            const legacySlackMessage =
                                item.direction === 'input'
                                    ? parseLegacySlackMessageInput(content)
                                    : null;
                            if (legacySlackMessage !== null) {
                                entries.push({
                                    kind: 'message',
                                    id: item.id,
                                    role: 'user',
                                    content: legacySlackMessage.text,
                                    createdAt: item.created_at,
                                    turnSequence: turn.sequence,
                                    source: 'slack',
                                    authorLabel: legacySlackMessage.authorLabel,
                                });
                                currentThought = null;
                                break;
                            }
                            if (
                                item.direction === 'input' &&
                                isLegacySlackMessageXml(content)
                            ) {
                                currentThought = null;
                                break;
                            }

                            // Hide the internal kickoff message in conversations.
                            if (
                                item.direction === 'input' &&
                                isSyntheticCalloutKickoffMessage(content)
                            ) {
                                break;
                            }

                            if (
                                item.direction === 'input' &&
                                (content.startsWith('<poke>') ||
                                    content.startsWith('<watch_update>'))
                            ) {
                                break;
                            }

                            const references = getReferencesFromRawJson(
                                item.raw_json,
                            );
                            const isRunError =
                                item.raw_json !== null &&
                                typeof item.raw_json === 'object' &&
                                'type' in item.raw_json &&
                                (item.raw_json as { readonly type?: unknown })
                                    .type === 'run_error';

                            entries.push({
                                kind: 'message',
                                id: item.id,
                                role:
                                    item.direction === 'input'
                                        ? 'user'
                                        : 'assistant',
                                content: isRunError
                                    ? 'Run failed'
                                    : formatAgentTraceTextContent(content),
                                createdAt: item.created_at,
                                references,
                                turnSequence: turn.sequence,
                                ...(item.direction === 'output'
                                    ? {
                                          agentTurnId: turn.id,
                                          agentItemId: item.id,
                                      }
                                    : {}),
                                isRunError,
                            });
                            // Preserve assistant thought context across assistant text
                            // so tool calls that follow still nest under the latest
                            // thought in this turn.
                            if (item.direction === 'input') {
                                currentThought = null;
                            }
                            break;
                        }
                        case 'function_call': {
                            const callId = toolItem.call_id;
                            if (!callId || attachedCallIds.has(callId)) {
                                break;
                            }

                            const toolEntry = callEntries.get(callId);
                            if (!toolEntry) {
                                break;
                            }

                            attachedCallIds.add(callId);
                            const shouldRenderTopLevel =
                                shouldRenderToolEntryTopLevel(toolEntry);
                            const thoughtTarget =
                                currentThought ?? latestThoughtInTurn;
                            if (
                                !isMonitoringToolEntry(toolEntry) &&
                                thoughtTarget &&
                                !shouldRenderTopLevel
                            ) {
                                thoughtTarget.tools.push(toolEntry);
                            } else {
                                entries.push(toolEntry);
                            }
                            break;
                        }
                        case 'function_output':
                            break;
                        case 'compaction': {
                            if (
                                debugPanelOpen &&
                                legacyProjection.liveCompactionItemIds.has(
                                    item.id,
                                )
                            ) {
                                pushLegacyCompactionMarker({
                                    item: toolItem,
                                    turnSequence: turn.sequence,
                                });
                            }
                            currentThought = null;
                            break;
                        }
                        case 'reasoning': {
                            const content = extractReasoning(toolItem);
                            if (content) {
                                currentThought = {
                                    kind: 'thought',
                                    id: item.id,
                                    createdAt: item.created_at,
                                    content,
                                    tools: [],
                                    turnSequence: turn.sequence,
                                };
                                entries.push(currentThought);
                                latestThoughtInTurn = currentThought;
                            }
                            break;
                        }
                        default: {
                            break;
                        }
                    }
                });

                callEntries.forEach((toolEntry, callId) => {
                    if (!attachedCallIds.has(callId)) {
                        entries.push(toolEntry);
                    }
                });
            });

            return entries;
        };

        if (shouldUsePhoebeEventRead && phoebeRunEventFeed) {
            const eventTranscript = buildTranscriptFromPhoebeEvents(
                phoebeRunEventFeed.events,
                phoebeRunEventFeed.pending_events,
                {
                    showCompactionEvents: debugPanelOpen,
                    runId: phoebeAgentRunId,
                },
            );
            return appendInlineOptimisticUserMessage(
                mergeLegacyTailAfterEventFeedStart({
                    eventTranscript,
                    legacyTranscript: buildLegacyTranscript(),
                    events: phoebeRunEventFeed.events,
                    pendingEvents: phoebeRunEventFeed.pending_events,
                }),
            );
        }

        // Event-mode but the run feed hasn't loaded yet (first-message send
        // on a fresh conversation). Pin the optimistic user bubble now so it
        // lands above the shimmer instead of trailing it once the stream
        // connects.
        if (shouldUsePhoebeEventRead) {
            return appendInlineOptimisticUserMessage(buildLegacyTranscript());
        }

        return buildLegacyTranscript();
    }, [
        hasEventStreamUserMessageAfterOptimisticThinking,
        legacyTurns,
        optimisticThinkingMarker,
        phoebeAgentRunId,
        phoebeRunEventFeed,
        debugPanelOpen,
        shouldUsePhoebeEventRead,
    ]);

    const streamingTranscript = useMemo<Array<TranscriptEntry>>(
        () => buildStreamingTranscript(streamState),
        [streamState],
    );
    const currentRunRequestSequence = useMemo<number | null>(() => {
        if (
            shouldUsePhoebeEventRead ||
            !legacyTurns ||
            !displayStreamRun?.requestTurnId
        ) {
            return null;
        }

        return (
            legacyTurns.find(
                (turn) => turn.id === displayStreamRun.requestTurnId,
            )?.sequence ?? null
        );
    }, [
        displayStreamRun?.requestTurnId,
        legacyTurns,
        shouldUsePhoebeEventRead,
    ]);
    const latestRunStartedBoundary = useMemo<{
        readonly offset: number;
        readonly createdAtMs: number | null;
    } | null>(() => {
        if (!shouldUsePhoebeEventRead) {
            return null;
        }

        let boundary: {
            readonly offset: number;
            readonly createdAtMs: number | null;
        } | null = null;
        for (const event of phoebeRunEventStream.events) {
            if (event.type !== 'run_started') {
                continue;
            }
            boundary = {
                offset: event.event_offset,
                createdAtMs: parseTimestampMs(event.created_at),
            };
        }
        return boundary;
    }, [phoebeRunEventStream.events, shouldUsePhoebeEventRead]);
    // In event-read mode the committed feed is authoritative: an overlay with
    // no content for the latest committed pass is dropped so it cannot
    // suppress that pass's response from the transcript. Legacy mode keeps
    // the raw overlay untouched.
    const streamOverlayResolution = useMemo(() => {
        if (!shouldUsePhoebeEventRead) {
            return {
                overlayRun: displayStreamRun,
                filterBoundaryOffset: null,
            };
        }
        return resolveStreamOverlay({
            displayStreamRun,
            latestRunStartedOffset: latestRunStartedBoundary?.offset ?? null,
            latestRunStartedAtMs: latestRunStartedBoundary?.createdAtMs ?? null,
        });
    }, [displayStreamRun, latestRunStartedBoundary, shouldUsePhoebeEventRead]);
    const resolvedDisplayStreamRun = streamOverlayResolution.overlayRun;
    const currentEventStreamRunStartedOffset =
        streamOverlayResolution.filterBoundaryOffset;
    const transcriptBeforeCurrentRun = useMemo<Array<TranscriptEntry>>(() => {
        if (
            shouldUsePhoebeEventRead &&
            resolvedDisplayStreamRun &&
            currentEventStreamRunStartedOffset !== null
        ) {
            return transcript.filter(
                (entry) =>
                    entry.turnSequence === undefined ||
                    entry.turnSequence <= currentEventStreamRunStartedOffset ||
                    (entry.kind === 'message' && entry.role === 'user'),
            );
        }

        if (!displayStreamRun || currentRunRequestSequence === null) {
            return transcript;
        }

        return transcript.filter(
            (entry) =>
                entry.turnSequence === undefined ||
                entry.turnSequence <= currentRunRequestSequence,
        );
    }, [
        currentEventStreamRunStartedOffset,
        currentRunRequestSequence,
        displayStreamRun,
        resolvedDisplayStreamRun,
        shouldUsePhoebeEventRead,
        transcript,
    ]);
    const activeStreamingEntries = useMemo<Array<TranscriptEntry>>(() => {
        if (!currentStreamRun) {
            return [];
        }
        return streamingTranscript.filter(
            (entry) =>
                entry.kind === 'tool-execution' &&
                (entry.status === 'pending_approval' ||
                    entry.status === 'pending_user_input') &&
                !entry.isComplete,
        );
    }, [currentStreamRun, streamingTranscript]);
    const streamedAssistantMessages = useMemo<Array<MessageEntry>>(() => {
        if (!currentStreamRun) {
            return [];
        }

        // Only surface the assistant message as a standalone bubble when it is
        // the latest content node in the run. While the model continues with
        // more reasoning or tool calls after the message, treat it as interim
        // checkpoint text and let the in-progress tree own it, so it does not
        // get stranded between the tree and the live "Thinking" indicator.
        const contentNodes = currentStreamRun.orderedNodeIds
            .map((id) => currentStreamRun.nodesById[id])
            .filter(
                (node): node is StreamRunNode =>
                    node !== undefined &&
                    node.kind !== 'run' &&
                    node.kind !== 'activity',
            );
        const lastContentNode = contentNodes[contentNodes.length - 1];
        if (
            !lastContentNode ||
            lastContentNode.kind !== 'response' ||
            lastContentNode.responseKind !== 'final'
        ) {
            return [];
        }

        const lastAssistantMessage = [...streamingTranscript]
            .reverse()
            .find(
                (entry): entry is MessageEntry =>
                    entry.kind === 'message' && entry.role === 'assistant',
            );
        return lastAssistantMessage ? [lastAssistantMessage] : [];
    }, [currentStreamRun, streamingTranscript]);
    const liveArtifactEntries = useMemo<Array<UpdatePlaybookArtifactEntry>>(
        () => collectUpdatePlaybookArtifactsFromRun(resolvedDisplayStreamRun),
        [resolvedDisplayStreamRun],
    );
    const liveReportArtifactEntries = useMemo<
        Array<StructuredReportArtifactEntry>
    >(
        () => collectStructuredReportArtifactsFromRun(resolvedDisplayStreamRun),
        [resolvedDisplayStreamRun],
    );
    const shouldRenderStreamOverlay = useMemo(() => {
        if (!resolvedDisplayStreamRun) {
            return false;
        }

        return true;
    }, [resolvedDisplayStreamRun]);
    const streamOverlayRun = shouldRenderStreamOverlay
        ? resolvedDisplayStreamRun
        : null;
    const streamOverlayAssistantMessages = shouldRenderStreamOverlay
        ? streamedAssistantMessages
        : [];
    const streamOverlayArtifactEntries = shouldRenderStreamOverlay
        ? liveArtifactEntries
        : [];
    const streamOverlayReportArtifactEntries = shouldRenderStreamOverlay
        ? liveReportArtifactEntries
        : [];

    const displayedTranscriptEntries = useMemo<Array<TranscriptEntry>>(() => {
        return transcriptBeforeCurrentRun;
    }, [transcriptBeforeCurrentRun]);
    const displayedTranscript = useMemo<Array<TranscriptEntry>>(
        () => [...displayedTranscriptEntries, ...activeStreamingEntries],
        [activeStreamingEntries, displayedTranscriptEntries],
    );
    const pendingUserQuestionEntry = useMemo<ToolExecutionEntry | null>(() => {
        const questions = displayedTranscript.filter(
            (entry): entry is ToolExecutionEntry =>
                entry.kind === 'tool-execution' &&
                entry.status === 'pending_user_input' &&
                !entry.isComplete &&
                !!entry.callId &&
                getUserQuestions(entry).length === 1,
        );
        return questions.length === 1 ? (questions[0] ?? null) : null;
    }, [displayedTranscript]);
    const latestAnimatedAssistantMessageId = getLatestLiveAssistantMessageId({
        entries: displayedTranscript,
        liveEventIds: phoebeRunEventStream.liveEventIds,
        eventLayerReadEnabled: shouldUsePhoebeEventRead,
    });
    const latestAnimatedThoughtId = getLatestLiveThoughtId({
        entries: displayedTranscript,
        liveEventIds: phoebeRunEventStream.liveEventIds,
        eventLayerReadEnabled: shouldUsePhoebeEventRead,
    });
    const isActiveRunWithoutStream =
        !!conversation?.agent_thinking &&
        shouldHonorLiveRunState &&
        !resolvedDisplayStreamRun;
    const isLoading =
        (!isCalloutMode && isConversationsLoading) ||
        (selectedConversationId !== null && isConversationLoading) ||
        (isOutreachTarget &&
            (isTargetOutreachLoading ||
                isOutreachTargetRunLoading ||
                isOutreachTargetConversationLoading ||
                isTargetOutreachUnresolved));
    // Suppress the full-pane loader when an optimistic user message is in
    // flight. The first-turn send flow runs createConversation, sets
    // selectedConversationId, then triggers a SWR fetch — during that fetch
    // window the chat surface would otherwise blank out into a spinner and
    // hide the optimistic bubble we just queued, producing a perceived
    // "thinking before bubble" flash on the very first message.
    // Run-native chats render their history from the event feed, so a run
    // read alone is not enough to drop the loader: hold it until the feed's
    // first snapshot lands, or an idle run flashes the empty-chat state. A
    // legacy conversation still has persisted turns to show meanwhile.
    const isAwaitingFirstEventSnapshot =
        shouldUsePhoebeEventRead &&
        phoebeAgentRunId !== null &&
        phoebeRunEventStream.isLoading &&
        legacyTurns === null;
    const shouldShowConversationLoader =
        isLoading &&
        (!conversation || isAwaitingFirstEventSnapshot) &&
        optimisticThinkingMarker === null;
    const canQueueMessageWhileAgentBusy =
        shouldUsePhoebeEventRead && phoebeAgentRunId !== null;
    const isAgentBusy = shouldUsePhoebeEventRead
        ? isSending ||
          effectiveIsAwaitingRunCompletion ||
          isOutreachAnalysisActive ||
          currentStreamRun?.status === 'running' ||
          (phoebeAgentRunId !== null
              ? currentRunEventStreamIsBusy
              : !!conversation?.agent_thinking &&
                shouldHonorLiveRunState &&
                !hasRunCompletionSignal)
        : isSending ||
          effectiveIsAwaitingRunCompletion ||
          isOutreachAnalysisActive ||
          currentStreamRun?.status === 'running' ||
          (!!conversation?.agent_thinking && !hasRunCompletionSignal);
    const currentPhoebeRunState = eventStreamRunStateMatchesCurrentRun
        ? phoebeRunEventStream.runState
        : null;
    const shouldShowWorkflowActivitySurface =
        currentPhoebeRunState?.activity_type === 'workflow' ||
        (currentPhoebeRunState === null && isCalloutMode);
    const hasPendingFanInWork = useMemo(
        () => hasPendingRecommendationBatch(displayedTranscript),
        [displayedTranscript],
    );
    const [timelineNowMs, setTimelineNowMs] = useState(() => Date.now());
    useEffect(() => {
        // Keep the work clock running while hidden fork evaluators are
        // still deciding, even though the parent run itself is paused.
        if (!isAgentBusy && !hasPendingFanInWork) {
            return;
        }

        setTimelineNowMs(Date.now());
        const intervalId = window.setInterval(() => {
            setTimelineNowMs(Date.now());
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [hasPendingFanInWork, isAgentBusy]);
    const manualLiveStatus = useMemo<LiveRunStatus | null>(
        () => deriveManualLiveStatusFromRunState(currentPhoebeRunState),
        [currentPhoebeRunState],
    );
    const targetOutreachMetadata = isRecord(targetOutreach)
        ? targetOutreach['outreach_metadata']
        : null;
    const targetMonitoringScheduleMetadata = isRecord(targetOutreachMetadata)
        ? targetOutreachMetadata['monitoring_schedule']
        : null;
    const monitoringScheduleMetadata = isRecord(
        targetMonitoringScheduleMetadata,
    )
        ? targetMonitoringScheduleMetadata
        : null;
    const isMonitoringOutreach = monitoringScheduleMetadata !== null;
    // Outreach analysis runs on the same run-native agent as the general chat
    // (an outreach target never routes to the legacy conversation endpoints),
    // so both surfaces get the inline activity treatment: Phoebe-logo shimmer
    // instead of the LiveStatusRow spinner.
    const showInlineWorkflowActivitySurface = isRunNativeProductChat;
    const showGeneralOutreachProgressCards =
        isRunNativeProductChat && isOutreachTarget && isMonitoringOutreach;
    // The native surface no longer lives in this component, so there is no
    // other renderer to defer to while a run's version is unknown. An unknown
    // version is the normal steady state for a run-native conversation with no
    // compat conversation, and treating it as pending left this view spinning
    // forever. Only a real load failure may replace the transcript.
    const savedRunVersionFailedToLoad =
        isRunNativeProductChat &&
        selectedRunNativeConversationId !== null &&
        !isOutreachAgentRunPending &&
        selectedRunIsAgentV3 === undefined &&
        organizationConversationError !== undefined;
    const legacyDisplayedTranscript = useMemo(
        () => adaptTranscriptForLegacy(displayedTranscript),
        [displayedTranscript],
    );
    const monitoringControls: MonitoringActivityControls | null =
        showGeneralOutreachProgressCards &&
        isMonitoringOutreach &&
        shouldUseRunNativeConversation &&
        canSendInSelectedConversation
            ? {
                  isStopping,
                  isConfiguring: isConfiguringMonitoring,
                  onStop: () => {
                      void stopOutreachMonitoring();
                  },
                  onConfigure: (payload) =>
                      configureOutreachMonitoring(payload),
              }
            : null;
    const hasMonitoringControls = monitoringControls !== null;
    const phoebeFooterStatus = useMemo<PhoebeFooterStatus | null>(() => {
        if (!conversation || !shouldShowWorkflowActivitySurface) {
            return null;
        }
        return derivePhoebeFooterStatus({
            runState: currentPhoebeRunState,
            manualStatus: manualLiveStatus,
            isMonitoring: isMonitoringOutreach,
            isSending,
            isAwaitingRunCompletion: effectiveIsAwaitingRunCompletion,
            isStopping,
            isLiveStreamRunning: currentStreamRun?.status === 'running',
            legacyAgentThinking:
                !!conversation.agent_thinking && shouldHonorLiveRunState,
            providerErrorBadge,
            hasPendingSubagentWork: hasPendingFanInWork,
        });
    }, [
        conversation,
        currentStreamRun?.status,
        currentPhoebeRunState,
        hasPendingFanInWork,
        effectiveIsAwaitingRunCompletion,
        isMonitoringOutreach,
        isSending,
        isStopping,
        manualLiveStatus,
        providerErrorBadge,
        shouldShowWorkflowActivitySurface,
        shouldHonorLiveRunState,
    ]);
    const shouldShowPhoebeFooterStatus =
        phoebeFooterStatus !== null &&
        !(hasMonitoringControls && phoebeFooterStatus.canStopMonitoring);
    const renderedTimelineEntries = useMemo<Array<RenderTimelineEntry>>(() => {
        return buildPhoebeAgentRenderedTimelineEntries({
            displayedTranscript: legacyDisplayedTranscript,
            currentRunEventStreamIsGenerating,
            dynamicWidgetBuildContext: null,
            dynamicWidgetsEnabled: false,
            isAgentBusy,
            phoebeAgentRunId,
            shouldRenderStreamOverlay,
            shouldShowWorkflowActivitySurface,
            shouldUsePhoebeEventRead,
            showGeneralOutreachProgressCards,
            timelineNowMs,
        });
    }, [
        currentRunEventStreamIsGenerating,
        isAgentBusy,
        legacyDisplayedTranscript,
        phoebeAgentRunId,
        shouldRenderStreamOverlay,
        shouldShowWorkflowActivitySurface,
        shouldUsePhoebeEventRead,
        showGeneralOutreachProgressCards,
        timelineNowMs,
    ]);
    // Backfill mention display names captured at send time onto user
    // messages the server has not enriched yet (queued/optimistic), so
    // badges render named immediately instead of flashing generic labels
    // until the worker resolves references.
    const timelineEntriesWithLocalReferences = useMemo<
        Array<RenderTimelineEntry>
    >(
        () =>
            renderedTimelineEntries.map((entry) => {
                if (
                    entry.kind !== 'message' ||
                    entry.role !== 'user' ||
                    // An empty echoed array carries no labels; let the
                    // cached local references cover it until enrichment.
                    (entry.references !== undefined &&
                        entry.references.length > 0) ||
                    !entry.content.includes('[[')
                ) {
                    return entry;
                }
                const localReferences = sentMentionReferencesRef.current.get(
                    entry.content.trim(),
                );
                return localReferences
                    ? { ...entry, references: localReferences }
                    : entry;
            }),
        [renderedTimelineEntries],
    );
    // Pending user messages sent while the agent is busy/generating render as
    // pills above the composer (genuine queue). Pending user messages sent
    // while the agent is idle stay inline as a synthesized bubble — they will
    // be drained almost instantly so the pill flash is visual noise.
    const hasPriorRunStarted = useMemo(
        () =>
            phoebeRunEventStream.events.some(
                (event) => event.type === 'run_started',
            ),
        [phoebeRunEventStream.events],
    );
    const timelineEntriesBeforeQueuedUserMessages = useMemo(
        () =>
            timelineEntriesWithLocalReferences.filter(
                (entry) =>
                    !isQueuedUserMessageTimelineEntry({
                        entry,
                        optimisticThinkingMarker,
                        currentRunStartedOffset:
                            currentEventStreamRunStartedOffset,
                        hasPriorRunStarted,
                    }),
            ),
        [
            currentEventStreamRunStartedOffset,
            hasPriorRunStarted,
            optimisticThinkingMarker,
            timelineEntriesWithLocalReferences,
        ],
    );
    const timelineEntriesWithRunDebriefAcknowledgement = useMemo<
        Array<RenderTimelineEntry>
    >(() => {
        if (
            runDebriefAcknowledgement === null ||
            selectedRunNativeConversationId === null
        ) {
            return timelineEntriesBeforeQueuedUserMessages;
        }

        const acknowledgementEntry: RunDebriefAcknowledgementEntry = {
            kind: 'run-debrief-acknowledgement',
            id: `run-debrief-acknowledgement-${selectedRunNativeConversationId}`,
            createdAt: runDebriefAcknowledgement.createdAt,
            notes: runDebriefAcknowledgement.notes,
        };
        const acknowledgementTimestampMs = Date.parse(
            acknowledgementEntry.createdAt,
        );
        const insertionIndex =
            timelineEntriesBeforeQueuedUserMessages.findIndex((entry) => {
                if (!('createdAt' in entry)) {
                    return false;
                }
                const entryTimestampMs = Date.parse(entry.createdAt);
                return (
                    !Number.isNaN(entryTimestampMs) &&
                    entryTimestampMs > acknowledgementTimestampMs
                );
            });

        if (insertionIndex === -1) {
            return [
                ...timelineEntriesBeforeQueuedUserMessages,
                acknowledgementEntry,
            ];
        }
        return [
            ...timelineEntriesBeforeQueuedUserMessages.slice(0, insertionIndex),
            acknowledgementEntry,
            ...timelineEntriesBeforeQueuedUserMessages.slice(insertionIndex),
        ];
    }, [
        runDebriefAcknowledgement,
        selectedRunNativeConversationId,
        timelineEntriesBeforeQueuedUserMessages,
    ]);
    const queuedUserMessageTimelineEntries = useMemo(
        () =>
            timelineEntriesWithLocalReferences.filter(
                (entry): entry is MessageEntry =>
                    isQueuedUserMessageTimelineEntry({
                        entry,
                        optimisticThinkingMarker,
                        currentRunStartedOffset:
                            currentEventStreamRunStartedOffset,
                        hasPriorRunStarted,
                    }),
            ),
        [
            currentEventStreamRunStartedOffset,
            hasPriorRunStarted,
            optimisticThinkingMarker,
            timelineEntriesWithLocalReferences,
        ],
    );
    const pendingMessagePillItems = useMemo<
        Array<{
            readonly id: string;
            readonly content: string;
            readonly createdAt: string;
            readonly label?: string;
            readonly references?: Array<ReferenceData>;
        }>
    >(() => {
        const items: Array<{
            readonly id: string;
            readonly content: string;
            readonly createdAt: string;
            readonly label?: string;
            readonly references?: Array<ReferenceData>;
        }> = queuedUserMessageTimelineEntries
            .filter((entry) => entry.hiddenFromTranscript !== true)
            .map((entry) => ({
                id: entry.id,
                content: entry.content,
                createdAt: entry.createdAt,
                label:
                    entry.pendingDeliveryMode === 'queue'
                        ? 'Queued'
                        : entry.pendingDeliveryMode === 'steer'
                          ? 'Steering'
                          : undefined,
                references: entry.references,
            }));
        if (
            shouldUsePhoebeEventRead &&
            optimisticThinkingMarker &&
            optimisticThinkingMarker.displayMode === 'pill' &&
            !optimisticThinkingMarker.hiddenFromTranscript &&
            !hasEventStreamUserMessageAfterOptimisticThinking
        ) {
            items.push({
                id: `optimistic-${optimisticThinkingMarker.createdAt}`,
                content: optimisticThinkingMarker.text,
                createdAt: optimisticThinkingMarker.createdAt,
                label: undefined,
                references: sentMentionReferencesRef.current.get(
                    optimisticThinkingMarker.text.trim(),
                ),
            });
        }
        return items;
    }, [
        hasEventStreamUserMessageAfterOptimisticThinking,
        optimisticThinkingMarker,
        queuedUserMessageTimelineEntries,
        shouldUsePhoebeEventRead,
    ]);
    const activeMonitoringRun = useMemo(
        () =>
            !hasMonitoringControls
                ? null
                : getLatestMonitoringRun(
                      timelineEntriesBeforeQueuedUserMessages,
                      streamOverlayRun,
                  ),
        [
            hasMonitoringControls,
            streamOverlayRun,
            timelineEntriesBeforeQueuedUserMessages,
        ],
    );
    const messageFeedbackByItemId = useMemo(() => {
        const byItemId = new Map<
            string,
            {
                readonly label: MessageFeedbackLabel;
                readonly comment: string | null;
            }
        >();
        for (const entry of organizationRunMessageFeedback ?? []) {
            byItemId.set(entry.agent_item_id, {
                label: entry.label,
                comment: entry.comment,
            });
        }
        return byItemId;
    }, [organizationRunMessageFeedback]);

    // Wait for the org before offering feedback: training mode reads as off
    // while the org is loading, so rating early would show the optional-note
    // UI to a training org and then take a 422 the user cannot clear.
    //
    // Wait for the ratings too. In flight, every message reads as unrated, so a
    // click would overwrite a rating already given — and a label flip retires
    // the note that went with it.
    const canGiveMessageFeedback =
        !readOnly &&
        shouldUseRunNativeConversation &&
        orgConversationOrganizationId !== null &&
        selectedRunNativeConversationId !== null &&
        trainingMode.isReady &&
        organizationRunMessageFeedback !== undefined;
    const messageFeedbackRequiresComment = trainingMode.isInTrainingMode;
    const submitMessageFeedback = useCallback(
        async ({
            agentItemId,
            label,
            comment,
        }: {
            readonly agentItemId: string;
            readonly label: MessageFeedbackLabel;
            readonly comment?: string;
        }) => {
            if (
                orgConversationOrganizationId === null ||
                selectedRunNativeConversationId === null
            ) {
                return false;
            }
            let saved;
            try {
                saved = await saveMessageFeedback({
                    organizationId: orgConversationOrganizationId,
                    runId: selectedRunNativeConversationId,
                    agentItemId,
                    label,
                    comment,
                });
            } catch {
                // A transparent throttle already has a central notice, but the
                // caller still needs false to keep feedback retryable.
                return false;
            }
            // The rating is committed; a failed revalidation must not report it
            // as unsaved. Patch the row we already have instead of refetching.
            void mutateOrganizationRunMessageFeedback(
                (current) => ({
                    feedback: [
                        ...(current?.feedback ?? []).filter(
                            (entry) => entry.agent_item_id !== agentItemId,
                        ),
                        saved,
                    ],
                }),
                { revalidate: false },
            );
            return true;
        },
        [
            mutateOrganizationRunMessageFeedback,
            orgConversationOrganizationId,
            selectedRunNativeConversationId,
        ],
    );
    const renderMessageFeedback = useCallback(
        (message: MessageEntry) => {
            const agentItemId = message.agentItemId;
            // Only a persisted assistant answer can be rated — the rating is
            // keyed on the agent item, and a still-streaming answer has no row
            // to hang it on yet.
            if (
                !canGiveMessageFeedback ||
                agentItemId === undefined ||
                message.content.trim().length === 0
            ) {
                return null;
            }
            return (
                <MessageFeedbackControls
                    feedback={messageFeedbackByItemId.get(agentItemId) ?? null}
                    requiresComment={messageFeedbackRequiresComment}
                    onSubmit={({ label, comment }) =>
                        submitMessageFeedback({ agentItemId, label, comment })
                    }
                />
            );
        },
        [
            canGiveMessageFeedback,
            messageFeedbackByItemId,
            messageFeedbackRequiresComment,
            submitMessageFeedback,
        ],
    );

    const currentRunNodeCount = streamOverlayRun?.orderedNodeIds.length ?? 0;

    // Retroactively seed the current run from persisted data when
    // conversation loads after the run was already created without it
    // (race condition: websocket event can arrive before SWR).
    useEffect(() => {
        if (shouldUsePhoebeEventRead || legacyTurns === null) {
            return;
        }
        setStreamState((current) => {
            if (!current.currentRunId) {
                return current;
            }
            const run = current.runsById[current.currentRunId];
            if (!run || run.status !== 'running') {
                return current;
            }
            const isAlreadySeeded = run.orderedNodeIds.some((nodeId) => {
                const node = run.nodesById[nodeId];
                return node !== undefined && node.sequence < 0;
            });
            if (isAlreadySeeded) {
                return current;
            }
            const seeded = buildStreamRunStateFromPersistedTurns(
                run.runId,
                run.requestTurnId,
                { turns: legacyTurns },
                run.startedAt ?? new Date().toISOString(),
            );
            if (seeded.orderedNodeIds.length === 0) {
                return current;
            }
            const mergedNodesById = {
                ...seeded.nodesById,
                ...run.nodesById,
            };
            const allNodeIds = [
                ...new Set([...seeded.orderedNodeIds, ...run.orderedNodeIds]),
            ];
            allNodeIds.sort((a, b) => {
                const nodeA = mergedNodesById[a];
                const nodeB = mergedNodesById[b];
                if (!nodeA || !nodeB) {
                    return a.localeCompare(b);
                }
                if (nodeA.sequence !== nodeB.sequence) {
                    return nodeA.sequence - nodeB.sequence;
                }
                return nodeA.createdAt.localeCompare(nodeB.createdAt);
            });
            return {
                ...current,
                runsById: {
                    ...current.runsById,
                    [run.runId]: {
                        ...run,
                        nodesById: mergedNodesById,
                        orderedNodeIds: allNodeIds,
                    },
                },
            };
        });
    }, [legacyTurns, shouldUsePhoebeEventRead]);

    const shiftSuggestionTabs = useMemo<{
        readonly tabs: Array<ShiftTab>;
        readonly turnSequence: number;
    } | null>(() => {
        if (shouldUsePhoebeEventRead) {
            if (!phoebeRunEventFeed) {
                return null;
            }
            return buildShiftSuggestionTabs({
                toolOutputs: phoebeRunEventFeed.events
                    .filter((event) => event.type === 'tool_output')
                    .map((event) => ({
                        toolName:
                            typeof event.payload['tool_name'] === 'string'
                                ? event.payload['tool_name']
                                : null,
                        outputJson: event.payload['output_json'],
                    })),
                toolCalls: phoebeRunEventFeed.events
                    .filter((event) => event.type === 'tool_call')
                    .map((event) => ({
                        toolName:
                            typeof event.payload['tool_name'] === 'string'
                                ? event.payload['tool_name']
                                : null,
                        argumentsJson: event.payload['arguments_json'],
                        sequence: event.event_offset,
                    })),
                timelineMarkers: phoebeRunEventFeed.events
                    .filter(
                        (event) =>
                            event.type === 'user_message' ||
                            event.type === 'assistant_text',
                    )
                    .map((event) => ({
                        sequence: event.event_offset,
                        role:
                            event.type === 'user_message'
                                ? ('user' as const)
                                : ('assistant' as const),
                        hasText:
                            typeof event.payload['text'] === 'string' &&
                            event.payload['text'].trim().length > 0,
                    })),
                timezone,
                outreachShiftGroups: targetOutreachJoined?.shift_groups ?? [],
                outreachShifts: targetOutreachJoined?.shifts ?? [],
            });
        }

        if (legacyTurns === null) {
            return null;
        }

        return buildShiftSuggestionTabs({
            toolOutputs: legacyTurns.flatMap((turn) =>
                turn.items.map((item) => {
                    const toolItem = item as ToolAwareItem;
                    return {
                        toolName: toolItem.tool_name ?? null,
                        outputJson: toolItem.output_json,
                    };
                }),
            ),
            toolCalls: legacyTurns.flatMap((turn) =>
                turn.items.map((item) => {
                    const toolItem = item as ToolAwareItem;
                    return {
                        toolName: toolItem.tool_name ?? null,
                        argumentsJson: toolItem.arguments_json,
                        sequence: turn.sequence,
                    };
                }),
            ),
            timelineMarkers: legacyTurns.flatMap((turn) => [
                {
                    sequence: turn.sequence,
                    role: 'user' as const,
                    hasText: turn.items.some(
                        (item) =>
                            item.direction === 'input' &&
                            !isRuntimeContextRawJson(item.raw_json),
                    ),
                },
                {
                    sequence: turn.sequence,
                    role: 'assistant' as const,
                    hasText: turn.items.some(
                        (item) =>
                            item.direction === 'output' &&
                            item.type === 'text' &&
                            Boolean(item.text_content),
                    ),
                },
            ]),
            timezone,
            outreachShiftGroups: targetOutreachJoined?.shift_groups ?? [],
            outreachShifts: targetOutreachJoined?.shifts ?? [],
        });
    }, [
        legacyTurns,
        phoebeRunEventFeed,
        shouldUsePhoebeEventRead,
        targetOutreachJoined?.shift_groups,
        targetOutreachJoined?.shifts,
        timezone,
    ]);

    const tabsRenderedInTranscript = useMemo(() => {
        if (!shiftSuggestionTabs) {
            return false;
        }
        return transcriptBeforeCurrentRun.some(
            (entry) =>
                entry.kind === 'message' &&
                entry.role === 'assistant' &&
                entry.turnSequence === shiftSuggestionTabs.turnSequence,
        );
    }, [shiftSuggestionTabs, transcriptBeforeCurrentRun]);

    // The sender's own view jumps to the bottom on send; a teammate's visible
    // message does the same for everyone else so a shared chat stays in step.
    // Unknown until the viewer loads, since every message would look foreign.
    const latestTeammateMessageId = useMemo(() => {
        if (user === undefined) {
            return undefined;
        }
        // Scan the list the transcript renders, so a teammate's queued message
        // (shown only as a composer pill) counts once it reaches the
        // transcript. Walk backwards without copying: this reruns on every
        // streamed change, and the newest messages sit at the end.
        for (
            let index = timelineEntriesWithRunDebriefAcknowledgement.length - 1;
            index >= 0;
            index--
        ) {
            const entry = timelineEntriesWithRunDebriefAcknowledgement[index];
            if (
                entry?.kind === 'message' &&
                entry.role === 'user' &&
                !entry.hiddenFromTranscript &&
                entry.sentBy &&
                entry.sentBy.user_id !== user.id
            ) {
                return entry.id;
            }
        }
        return null;
    }, [timelineEntriesWithRunDebriefAcknowledgement, user]);
    const {
        transcriptContentRef,
        transcriptScrollAreaRef,
        stickTranscriptToBottomRef,
        isTranscriptAtBottom,
        isTranscriptScrollable,
        scrollTranscriptToBottom,
        handleTypewriterProgress,
    } = useTranscriptScroll({
        currentRunNodeCount,
        renderedTimelineEntryCount: renderedTimelineEntries.length,
        returnToBottomKey: latestTeammateMessageId,
        streamOverlayAssistantMessageCount:
            streamOverlayAssistantMessages.length,
        transcriptKey: `${chatTargetRequestKey}:${selectedConversationId ?? 'new'}`,
    });
    const showScrollToBottom = isTranscriptScrollable && !isTranscriptAtBottom;

    const fetchErrorMessage =
        conversationsError?.message ?? conversationError?.message ?? null;
    // A stale conversation id persisted from a previous session (e.g. after
    // a DB reseed or a deleted run) surfaces here as "Phoebe agent run not
    // found". Drop the selection and the persisted id silently so the user
    // sees the empty-state hero instead of a red error banner. Scope this
    // recovery to the SINGLE-conversation fetch error so a list-level 404
    // (e.g. an org-wide not-found) doesn't accidentally wipe the selection.
    const stalePersistedConversationId =
        typeof conversationError?.message === 'string' &&
        /not found/i.test(conversationError.message) &&
        shouldPersistConversationId
            ? selectedConversationId
            : null;

    useEffect(() => {
        if (stalePersistedConversationId !== null) {
            // Remember the dead id before clearing the selection: the run can
            // still sit in the cached runs list, and the selection resolver
            // re-picking it would replay the cached 404 into this effect in
            // an unbounded clear/re-select ping-pong (WEB-G8).
            notFoundConversationIdsRef.current.add(
                stalePersistedConversationId,
            );
            writeGeneralChatSelection(
                {
                    organizationId,
                    userId,
                },
                null,
            );
            setSelectedConversationId(null);
            setErrorMessage(null);
            fetchErrorMessageRef.current = null;
            return;
        }
        if (fetchErrorMessage) {
            fetchErrorMessageRef.current = fetchErrorMessage;
            setErrorMessage(fetchErrorMessage);
        } else {
            const previousFetchErrorMessage = fetchErrorMessageRef.current;
            setErrorMessage((current) =>
                current !== null && current === previousFetchErrorMessage
                    ? null
                    : current,
            );
            fetchErrorMessageRef.current = null;
        }
    }, [
        fetchErrorMessage,
        stalePersistedConversationId,
        organizationId,
        userId,
    ]);

    const handleAgentStreamEvent = useCallback(
        (event: OrganizationEvent) => {
            if (isAdminConversationSource) {
                return;
            }
            if (
                event.type !== 'agent_conversation_stream_event' &&
                event.type !== 'agent_run_stream_event'
            ) {
                return;
            }
            const streamData = event.data as AgentConversationStreamData;
            if (!selectedConversationId && phoebeAgentRunId === null) {
                return;
            }
            const matchesSelectedConversation =
                event.resource.type === 'agent_conversation' &&
                (event.resource.id === selectedConversationId ||
                    event.resource.id === selectedCompatAgentConversationId);
            const selectedRunIds = [
                phoebeAgentRunId,
                event.resource.type === 'agent_run'
                    ? selectedConversationId
                    : null,
            ].filter((runId): runId is string => runId !== null);
            const matchesSelectedRun = selectedRunIds.some(
                (runId) =>
                    streamData.run_id === runId ||
                    (event.resource.type === 'agent_run' &&
                        event.resource.id === runId),
            );
            if (!matchesSelectedConversation && !matchesSelectedRun) {
                return;
            }

            if (
                streamData.type === 'tool_call_complete' &&
                streamData.tool_name === 'manage_automation' &&
                (streamData.error === null || streamData.error === undefined) &&
                isRecord(streamData.result) &&
                (streamData.result['status'] === 'created' ||
                    streamData.result['status'] === 'updated')
            ) {
                const completionId =
                    streamData.tool_call_id ?? streamData.node_id;
                if (
                    !handledManageAutomationCallIdsRef.current.has(completionId)
                ) {
                    handledManageAutomationCallIdsRef.current.add(completionId);
                    onManageAutomationSuccessRef.current?.();
                }
            }

            const requestTurnId =
                event.relationships.find(
                    (relationship) => relationship.type === 'agent_turn',
                )?.id ?? null;
            if (streamData.type === 'run_error') {
                setErrorMessage(null);
                setHasRunError(true);
            }

            if (streamData.type === 'run_started') {
                setHasRunCompletionSignal(false);
                setHasRunError(false);
                setSubagentDraftProgress(null);
            } else if (
                streamData.type === 'tool_call_start' ||
                streamData.type === 'tool_call_complete' ||
                streamData.type === 'response_chunk' ||
                streamData.type === 'response_classified' ||
                streamData.type === 'reasoning_chunk'
            ) {
                setHasRunCompletionSignal(false);
            } else if (
                streamData.type === 'run_complete' ||
                streamData.type === 'run_error'
            ) {
                setHasRunCompletionSignal(true);
                setIsAwaitingRunCompletion(false);
                throttledMutateConversation();
                setSubagentDraftProgress(null);
            }

            if (streamData.type === 'subagent_drafts_started') {
                const result = streamData.result as {
                    readonly units?: ReadonlyArray<Record<string, unknown>>;
                } | null;
                const units = Array.isArray(result?.units) ? result.units : [];
                setSubagentDraftProgress({
                    units,
                    tracesByUnitId: new Map(),
                    completedByUnitId: new Map(),
                });
                return;
            }
            if (streamData.type === 'subagent_drafts_unit_trace') {
                const result = streamData.result as {
                    readonly unit_id?: string;
                    readonly trace?: string;
                } | null;
                const unitId = result?.unit_id;
                const trace = result?.trace;
                if (
                    typeof unitId !== 'string' ||
                    typeof trace !== 'string' ||
                    trace.trim().length === 0
                ) {
                    return;
                }
                setSubagentDraftProgress((current) => {
                    if (current === null) {
                        return current;
                    }
                    const trimmedTrace = trace.trim();
                    const nextTraces = new Map(current.tracesByUnitId);
                    const unitTraces = nextTraces.get(unitId) ?? [];
                    // Keep the full ordered trace (the expandable view shows all
                    // of it) and skip consecutive duplicates.
                    if (unitTraces[unitTraces.length - 1] === trimmedTrace) {
                        return current;
                    }
                    nextTraces.set(unitId, [...unitTraces, trimmedTrace]);
                    return {
                        units: current.units,
                        tracesByUnitId: nextTraces,
                        completedByUnitId: current.completedByUnitId,
                    };
                });
                return;
            }
            if (streamData.type === 'subagent_drafts_unit_complete') {
                const result = streamData.result as {
                    readonly unit_id?: string;
                    readonly unit_draft?: Record<string, unknown>;
                } | null;
                const unitId = result?.unit_id;
                const unitDraft = result?.unit_draft;
                if (
                    typeof unitId !== 'string' ||
                    !unitDraft ||
                    typeof unitDraft !== 'object'
                ) {
                    return;
                }
                setSubagentDraftProgress((current) => {
                    if (current === null) {
                        return current;
                    }
                    const nextCompleted = new Map(current.completedByUnitId);
                    nextCompleted.set(unitId, unitDraft);
                    return {
                        units: current.units,
                        tracesByUnitId: current.tracesByUnitId,
                        completedByUnitId: nextCompleted,
                    };
                });
                return;
            }

            setStreamState((current) => {
                const runId = streamData.run_id;
                const nodeId = streamData.node_id;
                const nextConversationState: ConversationStreamState =
                    current.conversationId === selectedConversationId
                        ? {
                              ...current,
                              runsById: { ...current.runsById },
                          }
                        : {
                              conversationId: selectedConversationId,
                              currentRunId: null,
                              runsById: {},
                          };

                const existingRun = nextConversationState.runsById[runId];
                let nextRun = existingRun;
                if (!nextRun) {
                    nextRun = legacyTurns
                        ? buildStreamRunStateFromPersistedTurns(
                              runId,
                              requestTurnId,
                              { turns: legacyTurns },
                              event.timestamp,
                          )
                        : createStreamRunState(
                              runId,
                              requestTurnId,
                              event.timestamp,
                          );
                }
                if (nextRun.requestTurnId === null && requestTurnId !== null) {
                    nextRun = { ...nextRun, requestTurnId };
                }

                const baseNode: StreamRunNode = {
                    id: nodeId,
                    parentNodeId: streamData.parent_node_id ?? null,
                    kind: 'run',
                    createdAt: event.timestamp,
                    sequence: streamData.sequence,
                    content: '',
                    phase: null,
                    label: null,
                    detail: null,
                    toolCallId: null,
                    toolName: null,
                    arguments: null,
                    result: null,
                    error: null,
                    status: null,
                    isComplete: false,
                    responseKind: null,
                    durationMs: null,
                };

                switch (streamData.type) {
                    case 'run_started': {
                        nextRun = upsertStreamRunNode(nextRun, baseNode);
                        nextRun = { ...nextRun, status: 'running' };
                        break;
                    }
                    case 'reasoning_chunk': {
                        nextRun = upsertStreamRunNode(nextRun, {
                            ...baseNode,
                            kind: 'reasoning',
                            content: streamData.content ?? '',
                            durationMs: streamData.duration_ms ?? null,
                        });
                        break;
                    }
                    case 'response_chunk': {
                        nextRun = upsertStreamRunNode(nextRun, {
                            ...baseNode,
                            kind: 'response',
                            content: streamData.content ?? '',
                            responseKind: streamData.response_kind ?? null,
                        });
                        break;
                    }
                    case 'response_classified': {
                        nextRun = upsertStreamRunNode(nextRun, {
                            ...baseNode,
                            kind: 'response',
                            responseKind: streamData.response_kind ?? null,
                        });
                        break;
                    }
                    case 'activity_update': {
                        nextRun = upsertStreamRunNode(nextRun, {
                            ...baseNode,
                            kind: 'activity',
                            phase:
                                streamData.phase === 'tool' ||
                                streamData.phase === 'responding'
                                    ? streamData.phase
                                    : 'thinking',
                            label: streamData.label ?? 'Thinking',
                            detail: streamData.detail ?? null,
                            toolName: streamData.tool_name ?? null,
                            status: 'completed',
                            isComplete: true,
                            durationMs: null,
                        });
                        break;
                    }
                    case 'tool_call_start': {
                        const argumentsValue =
                            streamData.arguments &&
                            typeof streamData.arguments === 'object'
                                ? (streamData.arguments as
                                      | Record<string, unknown>
                                      | Array<unknown>)
                                : null;
                        nextRun = upsertStreamRunNode(nextRun, {
                            ...baseNode,
                            kind: 'tool',
                            toolCallId: streamData.tool_call_id ?? null,
                            toolName: streamData.tool_name ?? null,
                            arguments: argumentsValue,
                            status: null,
                            isComplete: false,
                            durationMs: null,
                        });
                        break;
                    }
                    case 'tool_call_complete': {
                        const existingNode = nextRun.nodesById[nodeId];
                        nextRun = upsertStreamRunNode(nextRun, {
                            ...baseNode,
                            kind: 'tool',
                            toolCallId: streamData.tool_call_id ?? null,
                            toolName: streamData.tool_name ?? null,
                            arguments: existingNode?.arguments ?? null,
                            result: streamData.result ?? null,
                            error: streamData.error ?? null,
                            status: streamData.error
                                ? 'incomplete'
                                : 'completed',
                            isComplete: true,
                            durationMs: streamData.duration_ms ?? null,
                        });
                        break;
                    }
                    case 'run_complete': {
                        const resultObj =
                            streamData.result &&
                            typeof streamData.result === 'object' &&
                            !Array.isArray(streamData.result)
                                ? (streamData.result as Record<string, unknown>)
                                : null;
                        const references =
                            resultObj &&
                            'references' in resultObj &&
                            Array.isArray(resultObj['references'])
                                ? (resultObj[
                                      'references'
                                  ] as Array<ReferenceData>)
                                : undefined;

                        const nodesById: Record<string, StreamRunNode> = {};
                        for (const [key, node] of Object.entries(
                            nextRun.nodesById,
                        )) {
                            if (
                                node.kind === 'tool' &&
                                node.toolCallId &&
                                !node.isComplete
                            ) {
                                nodesById[key] = {
                                    ...node,
                                    status: 'pending_approval',
                                };
                            } else if (node.kind === 'response' && references) {
                                nodesById[key] = {
                                    ...node,
                                    references,
                                };
                            } else {
                                nodesById[key] = node;
                            }
                        }
                        nextRun = {
                            ...nextRun,
                            status: 'completed',
                            nodesById,
                        };
                        break;
                    }
                    case 'run_error': {
                        nextRun = {
                            ...nextRun,
                            status: 'errored',
                        };
                        break;
                    }
                    default:
                        break;
                }

                return {
                    ...nextConversationState,
                    currentRunId: runId,
                    runsById: {
                        ...nextConversationState.runsById,
                        [runId]: nextRun,
                    },
                };
            });
        },
        [
            isAdminConversationSource,
            legacyTurns,
            phoebeAgentRunId,
            selectedCompatAgentConversationId,
            selectedConversationId,
            throttledMutateConversation,
        ],
    );

    const streamEventListenerOptions = isAdminConversationSource
        ? undefined
        : { organizationId: orgConversationOrganizationId };
    useListenForOrganizationEvent(
        'agent_conversation_stream_event',
        handleAgentStreamEvent,
        streamEventListenerOptions,
    );
    useListenForOrganizationEvent(
        'agent_run_stream_event',
        handleAgentStreamEvent,
        streamEventListenerOptions,
    );

    useEffect(() => {
        if (!isAdminConversationSource || !selectedConversationId) {
            return;
        }
        if (!conversation?.agent_thinking && !isAwaitingRunCompletion) {
            return;
        }

        const interval = window.setInterval(() => {
            void refreshConversation();
        }, 3000);

        return () => window.clearInterval(interval);
    }, [
        conversation?.agent_thinking,
        isAdminConversationSource,
        isAwaitingRunCompletion,
        refreshConversation,
        selectedConversationId,
    ]);

    useEffect(() => {
        if (
            !shouldUsePhoebeEventRead ||
            !isAwaitingRunCompletion ||
            isSending ||
            (optimisticThinkingMarker &&
                !hasNewRunContentAfterOptimisticThinking) ||
            !eventStreamRunStateMatchesCurrentRun ||
            currentRunEventStreamIsBusy
        ) {
            return;
        }

        setIsAwaitingRunCompletion(false);
        setHasRunCompletionSignal(true);
    }, [
        currentRunEventStreamIsBusy,
        eventStreamRunStateMatchesCurrentRun,
        hasNewRunContentAfterOptimisticThinking,
        isAwaitingRunCompletion,
        isSending,
        optimisticThinkingMarker,
        shouldUsePhoebeEventRead,
    ]);

    useEffect(() => {
        if (
            !shouldUsePhoebeEventRead ||
            !isAwaitingRunCompletion ||
            !hasQuestionAnswerCompletionEvent
        ) {
            return;
        }

        setIsAwaitingRunCompletion(false);
        setHasRunCompletionSignal(true);
        setAwaitingQuestionAnswerCallId(null);
    }, [
        hasQuestionAnswerCompletionEvent,
        isAwaitingRunCompletion,
        shouldUsePhoebeEventRead,
    ]);

    useEffect(() => {
        if (!optimisticThinkingMarker) {
            return;
        }
        if (!hasEventStreamUserMessageAfterOptimisticThinking) {
            return;
        }
        // Only drop the marker once real post-user_message content lands or
        // the run goes to waiting-for-approval. Busy/generating alone can
        // briefly read false between the user_message commit and the first
        // assistant token, leaving no indicator visible.
        if (
            hasNewRunContentAfterOptimisticThinking ||
            currentRunEventStreamIsWaitingForApproval
        ) {
            setOptimisticThinkingMarker(null);
        }
    }, [
        currentRunEventStreamIsWaitingForApproval,
        hasEventStreamUserMessageAfterOptimisticThinking,
        hasNewRunContentAfterOptimisticThinking,
        optimisticThinkingMarker,
    ]);

    // The websocket is the fast path for run progress, not a guarantee of it.
    // A socket that drops mid-run never delivers the completion event, and on
    // this backend every signal that releases the composer's busy gate is
    // downstream of the conversation read — so without a second source the
    // composer stays disabled until the page is reloaded. Re-read over HTTP
    // for as long as a run is believed in flight. Deliberately unconditional
    // on socket health: a socket that stays OPEN while silently delivering
    // nothing produces the same symptom and no connection-state check sees it.
    //
    // Scoped to the legacy backend, which is the one with no other source of
    // truth. The event-read path long-polls its own authoritative run state,
    // and refreshing it here would bump the stream nonce and restart that poll
    // on a cadence; admin traces already have their own polling owner.
    const shouldPollConversationWhileRunInFlight =
        !shouldUsePhoebeEventRead &&
        !isAdminConversationSource &&
        shouldPollPhoebeRunEvents({
            currentRunStatus: currentStreamRun?.status ?? null,
            isAwaitingRunCompletion: effectiveIsAwaitingRunCompletion,
            isAgentThinking: !!conversation?.agent_thinking,
            hasRunCompletionSignal,
        });
    useEffect(() => {
        if (!shouldPollConversationWhileRunInFlight) {
            return;
        }

        let timeoutId: number | null = null;
        let isCancelled = false;
        // Scheduled after each read settles rather than on a fixed interval:
        // the outage that makes this fallback matter is also when a read takes
        // longer than the delay, and a plain interval would then stack
        // concurrent revalidations on an API that is already struggling.
        const scheduleNextRead = () => {
            timeoutId = window.setTimeout(() => {
                timeoutId = null;
                // A backgrounded chat has nobody waiting on the composer, and
                // polling it would put every hidden tab on the API for the
                // length of every run.
                if (document.visibilityState !== 'visible') {
                    scheduleNextRead();
                    return;
                }
                // Best effort by construction — SWR keeps the previous data
                // and owns the user-visible error state, so a rejected read
                // here must not become an unhandled rejection every tick.
                void Promise.resolve(refreshConversationRef.current())
                    .catch(() => undefined)
                    .finally(() => {
                        if (isCancelled) {
                            return;
                        }
                        scheduleNextRead();
                    });
            }, RUN_IN_FLIGHT_CONVERSATION_POLL_INTERVAL_MS);
        };
        scheduleNextRead();

        return () => {
            isCancelled = true;
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [shouldPollConversationWhileRunInFlight]);

    useEffect(() => {
        if (!selectedConversationId) {
            return;
        }
        if (streamState.conversationId !== selectedConversationId) {
            return;
        }
        const currentRunId = streamState.currentRunId;
        if (!currentRunId) {
            return;
        }
        const currentRun = streamState.runsById[currentRunId];
        if (!currentRun || !currentRun.startedAt) {
            return;
        }
        if (!conversation || conversation.agent_thinking) {
            return;
        }
        const persistedTurns = legacyTurns ?? [];
        const requestTurn =
            currentRun.requestTurnId === null
                ? undefined
                : persistedTurns.find(
                      (turn) => turn.id === currentRun.requestTurnId,
                  );
        // An answer this run produced, as the server recorded it.
        //
        // Both halves are load-bearing. A later turn is not itself an answer —
        // a second participant's user-only turn also sorts after the request —
        // and an assistant item that predates the run belongs to an earlier
        // one. Without the timestamp, a cleared `agent_thinking` would settle a
        // run in the window between the client starting it and the server
        // picking it up, tearing down a run that had only just begun.
        const runStartedAt = currentRun.startedAt;
        const hasAssistantAnswerForThisRun = persistedTurns.some(
            (turn) =>
                (requestTurn === undefined ||
                    turn.sequence > requestTurn.sequence) &&
                turn.items.some(
                    (item) =>
                        item.direction === 'output' &&
                        item.role === 'assistant' &&
                        item.created_at > runStartedAt,
                ),
        );
        // The websocket completion event is the fast path, not the only proof.
        // A socket that drops mid-run never delivers it, so on the legacy
        // backend a recorded answer settles the run by itself. Requiring the
        // socket's signal on top of it left `currentRunId` set for the page's
        // lifetime, and with it `isAgentBusy` and the disabled composer, until
        // the user signed out and back in.
        //
        // A request turn that is set but missing from the conversation is not a
        // reason to wait forever — compaction drops old turns — so it only
        // widens the search rather than blocking it.
        const hasServerSettledRun =
            !shouldUsePhoebeEventRead && hasAssistantAnswerForThisRun;
        if (!hasRunCompletionSignal && !hasServerSettledRun) {
            return;
        }
        if (
            !shouldUsePhoebeEventRead &&
            requestTurn !== undefined &&
            !hasAssistantAnswerForThisRun
        ) {
            return;
        }

        setStreamState((s) => ({ ...s, currentRunId: null }));
        setIsAwaitingRunCompletion(false);
        setHasRunCompletionSignal(false);
    }, [
        conversation,
        hasRunCompletionSignal,
        legacyTurns,
        selectedConversationId,
        shouldUsePhoebeEventRead,
        streamState.conversationId,
        streamState.currentRunId,
        streamState.runsById,
    ]);

    useEffect(() => {
        if (!selectedConversationId) {
            return;
        }
        if (!conversation) {
            return;
        }
        if (!isAwaitingRunCompletion) {
            return;
        }
        if (isAdminConversationSource && !conversation.agent_thinking) {
            setIsAwaitingRunCompletion(false);
            return;
        }
        if (!streamState.currentRunId) {
            return;
        }
        if (conversation.agent_thinking) {
            return;
        }

        // Fallback: stream completion events can be missed (for example worker
        // handler timeout/retry). If backend says thinking is done, unblock the
        // input even without a run_complete stream event.
        setStreamState((s) => ({ ...s, currentRunId: null }));
        setIsAwaitingRunCompletion(false);
    }, [
        conversation,
        isAdminConversationSource,
        isAwaitingRunCompletion,
        selectedConversationId,
        streamState.currentRunId,
    ]);

    async function createConversation() {
        if (!organizationId || isAdminConversationSource) {
            return;
        }
        if (isCreating) {
            return;
        }

        setIsCreating(true);
        try {
            const response = await createChatBackendConversation({
                backendKind: effectiveChatBackendKind,
                organizationId,
                activeModel:
                    userIsAdmin &&
                    MODEL_PICKABLE_NEW_CHAT_RUN_TYPES.includes(
                        pendingRunType,
                    ) &&
                    pendingAgentModel !== null
                        ? pendingAgentModel
                        : null,
                runType:
                    clientSurface !== 'extension' &&
                    userIsAdmin &&
                    orchestratorProductChatEnabled &&
                    pendingRunType === 'orchestrator'
                        ? 'orchestrator'
                        : null,
                clientSurface,
            });
            if (response !== undefined && 'is_agent_v3' in response) {
                createdRunRendererVersionRef.current = {
                    runId: response.id,
                    isAgentV3: response.is_agent_v3 ?? false,
                };
            }
            return response;
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : CREATE_CONVERSATION_ERROR_MESSAGE,
                );
            }
            throw error;
        } finally {
            setIsCreating(false);
        }
    }

    async function ensureOutreachRunConversation() {
        if (
            !organizationId ||
            target.kind !== 'outreach' ||
            !shouldUseRunNativeConversation ||
            isCreating
        ) {
            return null;
        }

        setIsCreating(true);
        try {
            const run = isV3BornOutreachTarget
                ? await ensureOutreachRunnerRun({
                      organizationId,
                      outreachId: target.outreachId,
                  })
                : await ensureOutreachAnalysisRun({
                      organizationId,
                      outreachId: target.outreachId,
                  });
            // Seed with the pending pick so the picker never flashes the
            // default while the pin PATCH below is in flight; the PATCH fold
            // corrects the cache if the pin is rejected.
            const modelSeededRun =
                userIsAdmin && pendingAgentModel !== null && run
                    ? {
                          ...run,
                          active_model: pendingAgentModel,
                          resolved_model: pendingAgentModel,
                      }
                    : run;
            await mutateOutreachTargetRun(modelSeededRun, {
                revalidate: false,
            });
            void mutateTargetOutreach();
            // A callout chat has no conversation to pin until now, so the
            // model the admin picked while drafting is applied here.
            if (userIsAdmin && pendingAgentModel !== null && run) {
                await applyHeldAgentModel(run, pendingAgentModel);
            }
            return run;
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : CREATE_CONVERSATION_ERROR_MESSAGE,
                );
            }
            throw error;
        } finally {
            setIsCreating(false);
        }
    }

    async function handleRetryAfterRunError() {
        if (!canSendInSelectedConversation || !selectedConversationId) {
            return;
        }
        setErrorMessage(null);
        setHasRunError(false);
        setIsAwaitingRunCompletion(false);
        setHasRunCompletionSignal(false);
        setOptimisticThinkingMarker(null);
        resetStreamState();
        if (isAdminConversationSource) {
            return;
        }
        const resetAgentConversationId =
            shouldUseRunNativeConversation && isOutreachTarget
                ? selectedCompatAgentConversationId
                : selectedConversationId;
        if (!(isCalloutMode || fixedConversationIdForSelection)) {
            return;
        }
        if (!resetAgentConversationId) {
            setErrorMessage(
                'This run cannot be reset because it is missing its conversation.',
            );
            return;
        }
        if (!organizationId) {
            return;
        }
        try {
            setSuppressedPhoebeRunId(phoebeAgentRunId);
            setIsAwaitingRunCompletion(true);
            const response = await resetLegacyAgentConversation({
                organizationId,
                agentConversationId: resetAgentConversationId,
            });
            if (
                isRecord(response) &&
                typeof response['phoebe_run_id'] === 'string'
            ) {
                setPhoebeRunIdOverride(response['phoebe_run_id']);
                if (shouldUseRunNativeConversation) {
                    setSelectedConversationId(response['phoebe_run_id']);
                    await mutateOutreachTargetRun();
                }
            }
            await refreshConversation();
        } catch (error) {
            setIsAwaitingRunCompletion(false);
            if (!isTransparentRateLimitError(error)) {
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : 'Failed to reset analysis.',
                );
            }
        } finally {
            // A response without a phoebe_run_id (or a failure) must not
            // leave the suppression latched — that would strand the chat
            // with a permanently disabled event stream.
            setSuppressedPhoebeRunId(null);
        }
    }

    async function handleRestartAnalysis(): Promise<boolean> {
        if (!organizationId) {
            return false;
        }
        const resetAgentConversationId =
            shouldUseRunNativeConversation && isOutreachTarget
                ? selectedCompatAgentConversationId
                : selectedConversationId;
        if (!resetAgentConversationId) {
            return false;
        }
        setErrorMessage(null);
        setHasRunError(false);
        setHasRunCompletionSignal(false);
        setOptimisticThinkingMarker(null);
        resetStreamState();
        try {
            setSuppressedPhoebeRunId(phoebeAgentRunId);
            setIsAwaitingRunCompletion(true);
            const response = await resetLegacyAgentConversation({
                organizationId,
                agentConversationId: resetAgentConversationId,
            });
            if (
                isRecord(response) &&
                typeof response['phoebe_run_id'] === 'string'
            ) {
                setPhoebeRunIdOverride(response['phoebe_run_id']);
                if (shouldUseRunNativeConversation) {
                    setSelectedConversationId(response['phoebe_run_id']);
                    await mutateOutreachTargetRun();
                }
            }
            await refreshConversation();
            void mutateTargetOutreach();
            return true;
        } catch {
            setIsAwaitingRunCompletion(false);
            return false;
        } finally {
            // A response without a phoebe_run_id (or a failure) must not
            // leave the suppression latched — that would strand the chat
            // with a permanently disabled event stream.
            setSuppressedPhoebeRunId(null);
        }
    }

    async function handleResetOutreachRunner(): Promise<boolean> {
        // Claim 5: never fence on a guess. The affordance is disabled until
        // the runner id has loaded, and this is the matching guard.
        if (
            !organizationId ||
            target.kind !== 'outreach' ||
            !outreachRunnerRun?.id
        ) {
            return false;
        }
        setErrorMessage(null);
        setHasRunError(false);
        setHasRunCompletionSignal(false);
        setOptimisticThinkingMarker(null);
        resetStreamState();
        try {
            setSuppressedPhoebeRunId(phoebeAgentRunId);
            setIsAwaitingRunCompletion(true);
            const run = await resetOutreachRunnerRun({
                organizationId,
                outreachId: target.outreachId,
                expectedRunnerRunId: outreachRunnerRun.id,
            });
            await mutateOutreachRunnerRun(run, { revalidate: false });
            if (run?.id) {
                setPhoebeRunIdOverride(run.id);
                setSelectedConversationId(run.id);
            }
            await refreshConversation();
            void mutateTargetOutreach();
            toast.success('Phoebe restarted on this outreach', {
                persistNotification: true,
                notificationType: 'app_feedback',
            });
            return true;
        } catch (error) {
            setIsAwaitingRunCompletion(false);
            // Someone else already restarted Phoebe on this outreach. The
            // runner we were about to fence is gone, so adopt the winner
            // rather than stranding the chat on a dead run — from the
            // coordinator's seat a fresh chat is now on screen, which is
            // the outcome they asked for.
            if (isOutreachRunnerLinkChangedError(error)) {
                const winner = await mutateOutreachRunnerRun();
                if (winner?.id) {
                    setPhoebeRunIdOverride(winner.id);
                    setSelectedConversationId(winner.id);
                }
                await refreshConversation();
                void mutateTargetOutreach();
                toast.info(
                    'Someone else already started a fresh Phoebe chat on ' +
                        'this outreach — showing that one.',
                    { persistNotification: false },
                );
                return true;
            }
            if (!isTransparentRateLimitError(error)) {
                toast.error('Could not restart Phoebe on this outreach', {
                    persistNotification: true,
                    notificationType: 'app_feedback',
                });
            }
            return false;
        } finally {
            setSuppressedPhoebeRunId(null);
        }
    }

    function getPendingMessageOperationId(text: string) {
        if (pendingMessageOperationTextRef.current !== text) {
            pendingMessageOperationIdRef.current = generateUuid();
            pendingMessageOperationTextRef.current = text;
        }
        pendingMessageOperationIdRef.current ??= generateUuid();
        return pendingMessageOperationIdRef.current;
    }

    async function sendMessage(
        text: string,
        targetConversationId?: string | null,
        activityEventIds: ReadonlyArray<string> = [],
        widgetAction?: DynamicWidgetActionRequest,
        widgetDiagnostic?: DynamicWidgetDiagnosticRequest,
        messageOrigin?: ChatMessageOrigin,
        uploadIds: ReadonlyArray<string> = [],
    ) {
        const isWidgetSubmission =
            widgetAction !== undefined || widgetDiagnostic !== undefined;
        const activeConversationId =
            targetConversationId ?? selectedConversationId;
        if (!activeConversationId) {
            toast.error('No conversation selected', {
                persistNotification: false,
            });
            return;
        }
        if (!organizationId && !isAdminConversationSource) {
            return;
        }
        if (isSending) {
            return;
        }

        // A diagnostic send reuses the "Help me understand and fix this card"
        // text every time, so it must not share the composer's text-keyed
        // idempotency key — the second Ask Phoebe would be dropped as a retry.
        const operationId =
            widgetAction?.id ??
            (widgetDiagnostic === undefined
                ? getPendingMessageOperationId(text)
                : generateUuid());

        setIsSending(true);
        try {
            const primaryRequest = isAdminConversationSource
                ? sendChatBackendMessage({
                      backendKind: effectiveChatBackendKind,
                      organizationId,
                      adminTraceConversationId: activeConversationId,
                      content: text,
                      operationId,
                      messageOrigin,
                  })
                : sendChatBackendMessage({
                      backendKind: effectiveChatBackendKind,
                      organizationId,
                      runId: shouldUseRunNativeConversation
                          ? activeConversationId
                          : null,
                      agentConversationId: shouldUseRunNativeConversation
                          ? null
                          : activeConversationId,
                      content: text,
                      operationId,
                      activityEventIds,
                      uploadIds,
                      messageOrigin,
                      widgetAction,
                      widgetDiagnostic,
                  });
            const response = await primaryRequest;
            if (!isWidgetSubmission) {
                pendingMessageOperationIdRef.current = null;
                pendingMessageOperationTextRef.current = null;
            }
            return response;
        } catch (error) {
            if (!isWidgetSubmission && !isTransparentRateLimitError(error)) {
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : 'Failed to send message.',
                );
            }
            throw error;
        } finally {
            setIsSending(false);
        }
    }

    /**
     * "Ask Phoebe" on a dynamic widget error: a normal user message with the
     * bounded diagnostic attached. Admin traces never carry widget metadata,
     * so this refuses there and the control stays hidden.
     */
    async function askPhoebeAboutDynamicWidget(
        diagnostic: DynamicWidgetDiagnosticRequest,
    ): Promise<boolean> {
        if (
            isAdminConversationSource ||
            !canSendInSelectedConversation ||
            selectedConversationId === null ||
            isInputBlockedByPendingApproval
        ) {
            return false;
        }
        setIsAwaitingRunCompletion(true);
        setHasRunCompletionSignal(false);
        try {
            await sendMessage(
                ASK_PHOEBE_DIAGNOSTIC_MESSAGE,
                selectedConversationId,
                [],
                undefined,
                diagnostic,
            );
        } catch {
            setIsAwaitingRunCompletion(false);
            setHasRunCompletionSignal(false);
            return false;
        }
        if (shouldUsePhoebeEventRead) {
            refreshPhoebeRunEvents();
        }
        return true;
    }

    function showDynamicWidgetSource(
        widgetId: string,
        bindingId: string | null,
    ) {
        dynamicWidgetInspectorStore.select(widgetId, bindingId);
        if (!debugPanelOpen) {
            onDebugPanelToggle?.();
        }
    }

    async function decideToolCall(
        target: ToolApprovalDecisionTarget,
        decision: ToolApprovalDecision,
        alwaysAllowScope?: ToolApprovalScope,
        argumentsOverride?: Record<string, unknown>,
    ) {
        if (
            !selectedConversationId ||
            (!organizationId && !isAdminConversationSource)
        ) {
            return;
        }
        const callIds = Array.isArray(target) ? target : [target];
        if (
            callIds.length === 0 ||
            callIds.some((callId) => decidingCallIds[callId])
        ) {
            return;
        }

        const operationKey = `${callIds.join(',')}:${decision}`;
        pendingToolDecisionOperationIdsRef.current[operationKey] ??=
            generateUuid();
        const operationId =
            pendingToolDecisionOperationIdsRef.current[operationKey];

        setDecidingCallIds((current) => ({
            ...current,
            ...Object.fromEntries(callIds.map((callId) => [callId, true])),
        }));
        try {
            const result = await decideChatBackendToolCalls({
                backendKind: effectiveChatBackendKind,
                organizationId,
                runId: shouldUseRunNativeConversation
                    ? selectedConversationId
                    : null,
                agentConversationId:
                    !isAdminConversationSource &&
                    !shouldUseRunNativeConversation
                        ? selectedConversationId
                        : null,
                adminTraceConversationId: isAdminConversationSource
                    ? selectedConversationId
                    : null,
                target,
                decision,
                alwaysAllowScope,
                argumentsOverride,
                operationId,
            });
            delete pendingToolDecisionOperationIdsRef.current[operationKey];
            setErrorMessage(null);
            if (result.resumed) {
                setIsAwaitingRunCompletion(true);
                setHasRunCompletionSignal(false);
            }
            if (shouldUsePhoebeEventRead) {
                refreshPhoebeRunEvents();
            }
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Failed to submit tool approval decision.';
                setErrorMessage(message);
                toast.error(message, { persistNotification: false });
            }
            throw error;
        } finally {
            setDecidingCallIds((current) => {
                const next = { ...current };
                for (const callId of callIds) {
                    delete next[callId];
                }
                return next;
            });
        }
    }

    async function submitQuestionAnswer(
        callId: string,
        payload: QuestionAnswerPayload,
    ) {
        if (!selectedConversationId || !organizationId) {
            return;
        }
        if (!shouldUseRunNativeConversation) {
            return;
        }
        if (answeringQuestionCallIds[callId]) {
            return;
        }

        const operationKey = `${callId}:${payload.cancelled === true ? 'cancel' : JSON.stringify(payload.answers ?? payload.answer ?? '')}`;
        pendingQuestionAnswerOperationIdsRef.current[operationKey] ??=
            generateUuid();
        const operationId =
            pendingQuestionAnswerOperationIdsRef.current[operationKey];

        setAnsweringQuestionCallIds((current) => ({
            ...current,
            [callId]: true,
        }));
        try {
            const result = await submitRunQuestionAnswer({
                organizationId,
                runId: selectedConversationId,
                callId,
                payload,
                operationId,
            });
            delete pendingQuestionAnswerOperationIdsRef.current[operationKey];
            if (
                result &&
                typeof result === 'object' &&
                'resumed' in result &&
                Boolean(result.resumed)
            ) {
                setIsAwaitingRunCompletion(true);
                setHasRunCompletionSignal(false);
                setAwaitingQuestionAnswerCallId(callId);
            }
            if (shouldUsePhoebeEventRead) {
                refreshPhoebeRunEvents();
            }
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Failed to submit answer.';
                setErrorMessage(message);
                toast.error(message, { persistNotification: false });
            }
            // Re-throw so the question card's own catch can stop the
            // rejection from propagating unhandled.
            throw error;
        } finally {
            setAnsweringQuestionCallIds((current) => {
                const next = { ...current };
                delete next[callId];
                return next;
            });
        }
    }

    async function stopRun() {
        if (
            !selectedConversationId ||
            (!organizationId && !isAdminConversationSource)
        ) {
            return;
        }
        if (isStopping) {
            return;
        }

        setIsStopping(true);
        pendingStopOperationIdRef.current ??= generateUuid();
        const operationId = pendingStopOperationIdRef.current;
        posthog?.capture(EVENTS.CONVERSATION_RUN_INTERRUPTED, {
            conversation_id: selectedConversationId,
            conversation_source: isAdminConversationSource
                ? 'admin'
                : 'organization',
            mode: modePreference,
            organization_id: organizationId ?? undefined,
        });
        try {
            await interruptChatBackendRun({
                backendKind: effectiveChatBackendKind,
                organizationId,
                runId: shouldUseRunNativeConversation
                    ? selectedConversationId
                    : null,
                agentConversationId:
                    !isAdminConversationSource &&
                    !shouldUseRunNativeConversation
                        ? selectedConversationId
                        : null,
                adminTraceConversationId: isAdminConversationSource
                    ? selectedConversationId
                    : null,
                operationId,
            });
            pendingStopOperationIdRef.current = null;
            setOptimisticThinkingMarker(null);
            setIsAwaitingRunCompletion(false);
            setHasRunCompletionSignal(true);
            if (shouldUsePhoebeEventRead) {
                refreshPhoebeRunEvents();
            } else {
                resetStreamState();
            }
            void refreshConversation();
            void mutateConversations();
            void mutateOutreachTargetRun();
            void mutateTargetOutreach();
            setErrorMessage(null);
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Failed to stop run.';
                setErrorMessage(message);
                toast.error(message, { persistNotification: false });
            }
        } finally {
            setIsStopping(false);
        }
    }

    async function configureOutreachMonitoring(
        payload: MonitoringConfigPayload,
    ): Promise<boolean> {
        if (
            !organizationId ||
            !selectedConversationId ||
            !shouldUseRunNativeConversation
        ) {
            return false;
        }
        if (isConfiguringMonitoring) {
            return false;
        }

        setIsConfiguringMonitoring(true);
        const operationId = generateUuid();
        try {
            await configureRunOutreachMonitoring({
                organizationId,
                runId: selectedConversationId,
                conditions: payload.conditions,
                operationId,
            });
            setErrorMessage(null);
            refreshPhoebeRunEvents();
            void refreshConversation();
            void mutateConversations();
            void mutateOutreachTargetRun();
            void mutateTargetOutreach();
            toast.success('Monitoring updated', {
                persistNotification: false,
            });
            return true;
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Failed to update monitoring.';
                setErrorMessage(message);
                toast.error(message, { persistNotification: false });
            }
            return false;
        } finally {
            setIsConfiguringMonitoring(false);
        }
    }

    async function stopOutreachMonitoring() {
        if (
            !organizationId ||
            !selectedConversationId ||
            !shouldUseRunNativeConversation
        ) {
            return;
        }
        if (isStopping) {
            return;
        }

        setIsStopping(true);
        const operationId = generateUuid();
        try {
            await stopRunOutreachMonitoring({
                organizationId,
                runId: selectedConversationId,
                operationId,
            });
            setErrorMessage(null);
            refreshPhoebeRunEvents();
            void refreshConversation();
            void mutateConversations();
            void mutateOutreachTargetRun();
            void mutateTargetOutreach();
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Failed to stop monitoring.';
                setErrorMessage(message);
                toast.error(message, { persistNotification: false });
            }
        } finally {
            setIsStopping(false);
        }
    }

    const {
        voiceInputError,
        setVoiceInputError,
        canRecordAudio,
        isRecording,
        isTranscribing,
        startRecording,
        stopRecording,
        draftIncludesVoiceInputRef,
        transcriptionGenerationRef,
    } = useVoiceRecording({
        organizationId,
        chatInputRef,
        isAgentBusy,
        isLoading,
        isCreating,
        isInputBlockedByPendingApproval,
        isCalloutMode,
        selectedConversationId,
        canCreateOutreachRunOnSubmit,
    });

    const handleSubmit = async (
        submission?: string | DynamicWidgetActionRequest,
        messageOrigin?: ChatMessageOrigin,
    ) => {
        if (!canSendInSelectedConversation) {
            return false;
        }
        const widgetAction =
            typeof submission === 'object' ? submission : undefined;
        if (widgetAction !== undefined && isAdminConversationSource) {
            return false;
        }
        const typedValue =
            (typeof submission === 'string'
                ? submission
                : widgetAction?.display_text) ??
            chatInputRef.current?.getSerializedContent() ??
            '';
        // Guard on the typed text OR a ready upload attachment: staged
        // references or activity items alone must not make an empty
        // message sendable, but a coordinator dropping a CSV without
        // any text should still submit.
        if (
            !typedValue.trim() &&
            uploadAttachments.readyUploadIds.length === 0
        ) {
            return false;
        }
        if (isInputBlockedByPendingApproval) {
            return false;
        }
        const isAnsweringUserQuestion =
            currentRunEventStreamIsWaitingForUserInput &&
            !!pendingUserQuestionEntry?.callId &&
            shouldUseRunNativeConversation;
        if (widgetAction !== undefined && isAnsweringUserQuestion) {
            return false;
        }
        // Capture mention display names from the composer pills so the
        // pending bubble can render named badges instead of raw tokens
        // while the worker resolves references.
        const mentionReferences: Array<ReferenceData> =
            widgetAction === undefined
                ? (chatInputRef.current?.getReferences() ?? [])
                : [];
        let currentValue = typedValue;
        // References staged from other pages (e.g. calendar shifts) ride along
        // as reference tokens on general-chat sends. Question answers resume a
        // paused tool call, so they never take them.
        const referenceState = chatReferenceStore.getState();
        const stagedReferences =
            !isAnsweringUserQuestion &&
            widgetAction === undefined &&
            !isOutreachTarget &&
            !isAdminConversationSource &&
            referenceState.orderedReferences.length > 0 &&
            referenceState.contextKey ===
                buildChatReferenceContextKey(
                    organizationId,
                    modePreference ?? 'live',
                )
                ? referenceState.orderedReferences
                : [];
        const stagedReferenceIds = stagedReferences.map(
            (reference) => reference.id,
        );
        for (const reference of stagedReferences) {
            const token = `[[${reference.type}:${reference.id}]]`;
            // The user may already have @-mentioned this entity.
            if (!currentValue.includes(token)) {
                currentValue += ` ${token}`;
            }
            if (!mentionReferences.some((ref) => ref.id === reference.id)) {
                mentionReferences.push(
                    stagedReferenceToReferenceData(reference, contextTimezone),
                );
            }
        }
        // Activity-board cards attached in the tray ride along as entity
        // reference tokens plus activity_event_ids on general-chat sends.
        // Question answers resume a paused tool call, so they never take
        // them.
        const handoffSelection = activityHandoffStore.getState();
        const handoffApplies =
            !isAnsweringUserQuestion &&
            widgetAction === undefined &&
            !isOutreachTarget &&
            !isAdminConversationSource &&
            handoffSelection.orderedItems.length > 0 &&
            handoffSelection.contextKey ===
                buildActivityHandoffContextKey(
                    organizationId,
                    modePreference ?? 'live',
                );
        let outgoingActivityEventIds: ReadonlyArray<string> =
            widgetAction === undefined
                ? pendingActivityEventIdsRef.current
                : [];
        let referencedHandoffIds: ReadonlyArray<string> = [];
        if (handoffApplies) {
            for (const item of handoffSelection.orderedItems) {
                for (const reference of item.references) {
                    const token = `[[${reference.type}:${reference.id}]]`;
                    // The user may already have @-mentioned this entity.
                    if (!currentValue.includes(token)) {
                        currentValue += ` ${token}`;
                    }
                    if (
                        !mentionReferences.some(
                            (ref) => ref.id === reference.id,
                        )
                    ) {
                        mentionReferences.push({
                            type: reference.type,
                            id: reference.id,
                            display_name: reference.displayName,
                        });
                    }
                }
            }
            outgoingActivityEventIds = handoffSelection.orderedItems.map(
                (item) => item.activityEventId,
            );
            referencedHandoffIds = outgoingActivityEventIds;
        }
        if (mentionReferences.length > 0) {
            sentMentionReferencesRef.current.set(
                currentValue.trim(),
                mentionReferences,
            );
            if (sentMentionReferencesRef.current.size > 20) {
                const oldestKey = sentMentionReferencesRef.current
                    .keys()
                    .next().value;
                if (oldestKey !== undefined) {
                    sentMentionReferencesRef.current.delete(oldestKey);
                }
            }
        }
        transcriptionGenerationRef.current += 1;
        setErrorMessage(null);
        setHasRunError(false);
        setVoiceInputError(null);

        let activeConversationId = selectedConversationId;
        if (activeConversationId === null) {
            if (isOutreachTarget && shouldUseRunNativeConversation) {
                let run;
                try {
                    run = await ensureOutreachRunConversation();
                } catch (error) {
                    // Reset the drafting flag so the auto-resolve effect can
                    // recover; otherwise it stays suppressed forever.
                    isDraftingNewChatRef.current = false;
                    const message = getSendErrorMessage(
                        error,
                        CREATE_CONVERSATION_ERROR_MESSAGE,
                    );
                    if (message !== null) {
                        setErrorMessage(message);
                        toast.error(message, { persistNotification: false });
                    }
                    return false;
                }
                activeConversationId = run?.id ?? null;
                setSelectedConversationId(activeConversationId);
                if (activeConversationId === null) {
                    setIsAwaitingRunCompletion(false);
                    setHasRunCompletionSignal(false);
                    setErrorMessage(CREATE_CONVERSATION_ERROR_MESSAGE);
                    toast.error(CREATE_CONVERSATION_ERROR_MESSAGE, {
                        persistNotification: false,
                    });
                    return false;
                }
            } else if (isCalloutMode) {
                toast.error('Open shift conversation is not ready yet.', {
                    persistNotification: false,
                });
                return false;
            } else {
                let conversation;
                try {
                    conversation = await createConversation();
                } catch (error) {
                    // Reset the drafting flag so the auto-resolve effect can
                    // recover; otherwise it stays suppressed forever.
                    isDraftingNewChatRef.current = false;
                    const message = getSendErrorMessage(
                        error,
                        CREATE_CONVERSATION_ERROR_MESSAGE,
                    );
                    if (message !== null) {
                        setErrorMessage(message);
                        toast.error(message, { persistNotification: false });
                    }
                    return false;
                }
                void mutateConversations();
                activeConversationId = conversation?.id ?? null;
                // Hold the drafting flag until the new conversation lands in
                // the SWR list. Without this, the auto-resolve effect runs
                // before the refetch completes, sees the new id missing from a
                // stale list, and snaps the selection back to the previously
                // newest row.
                setSelectedConversationId(activeConversationId);
                if (activeConversationId === null) {
                    isDraftingNewChatRef.current = false;
                    setIsAwaitingRunCompletion(false);
                    setHasRunCompletionSignal(false);
                    setErrorMessage(CREATE_CONVERSATION_ERROR_MESSAGE);
                    toast.error(CREATE_CONVERSATION_ERROR_MESSAGE, {
                        persistNotification: false,
                    });
                    return false;
                }
            }
        }

        if (isAnsweringUserQuestion && pendingUserQuestionEntry?.callId) {
            try {
                advanceThinkingTipIndex();
                await submitQuestionAnswer(pendingUserQuestionEntry.callId, {
                    answer: currentValue.trim(),
                });
            } catch {
                setOptimisticThinkingTipIndex(null);
                // submitQuestionAnswer owns the error toast; keep the draft
                // in the input so the user can retry.
                return false;
            }
            draftIncludesVoiceInputRef.current = false;
            chatInputRef.current?.clear();
            return true;
        }

        try {
            if (!shouldUsePhoebeEventRead) {
                resetStreamState();
            }
            const outgoingValue = currentValue;
            const operationId =
                widgetAction?.id ?? getPendingMessageOperationId(outgoingValue);
            advanceThinkingTipIndex();
            startOptimisticThinking(
                outgoingValue,
                operationId,
                widgetAction !== undefined || messageOrigin !== undefined,
            );
            setIsAwaitingRunCompletion(true);
            setHasRunCompletionSignal(false);
            const uploadIdsForThisSend =
                widgetAction === undefined
                    ? uploadAttachments.readyUploadIds
                    : [];
            const turn = await sendMessage(
                outgoingValue,
                activeConversationId,
                outgoingActivityEventIds,
                widgetAction,
                undefined,
                messageOrigin,
                uploadIdsForThisSend,
            );
            if (uploadIdsForThisSend.length > 0) {
                // Only remove the ids we actually attached to this
                // message; a file still uploading stays in the composer
                // and lands on the next send instead of being lost.
                uploadAttachments.clearAttachmentsByUploadIds(
                    uploadIdsForThisSend,
                );
            }
            if (widgetAction === undefined) {
                pendingActivityEventIdsRef.current = [];
            }
            if (handoffApplies) {
                activityHandoffStore.removeItems(referencedHandoffIds);
            }
            if (shouldUsePhoebeEventRead) {
                refreshPhoebeRunEvents();
            }
            // Track message volume per surface so we can compare the general
            // sidebar vs the Chrome extension (mirrors the backend
            // `client_surface` run tag).
            if (widgetAction === undefined && messageOrigin === undefined) {
                captureConversationMessageSent(posthog, {
                    clientSurface,
                    conversationId: activeConversationId,
                    draftLength: currentValue.trim().length,
                    mode: modePreference,
                    organizationId,
                });
            }
            if (
                widgetAction === undefined &&
                draftIncludesVoiceInputRef.current
            ) {
                posthog?.capture(EVENTS.VOICE_INPUT_MESSAGE_SENT, {
                    composer: 'phoebe_chat_view',
                    conversation_id: activeConversationId ?? undefined,
                    draft_length: currentValue.trim().length,
                    mode: modePreference,
                    organization_id: organizationId,
                    voice_input_flow: voiceInputFlow,
                });
            }
            if (widgetAction === undefined) {
                draftIncludesVoiceInputRef.current = false;
                chatInputRef.current?.clear();
            }
            // The sent references are now part of the conversation, so drop
            // just those from the chat reference tray and the calendar;
            // references staged during the in-flight send are preserved.
            if (stagedReferenceIds.length > 0) {
                chatReferenceStore.removeMany(stagedReferenceIds);
            }
            appendTurnOptimistically(
                turn,
                widgetAction === undefined && messageOrigin === undefined
                    ? outgoingValue
                    : undefined,
            );
            // First message in a default-titled conversation triggers a
            // server-side auto-title rename. Refresh the list always; refresh
            // the open chat only while the title is still a placeholder.
            void mutateConversations();
            if (
                isDefaultChatTitle(conversation?.title) ||
                isDefaultChatTitle(selectedConversation?.title)
            ) {
                void refreshConversation();
            }
            window.requestAnimationFrame(() => {
                stickTranscriptToBottomRef.current = true;
                scrollTranscriptToBottom('smooth');
            });
            return true;
        } catch (error) {
            setOptimisticThinkingMarker(null);
            setOptimisticThinkingTipIndex(null);
            setIsAwaitingRunCompletion(false);
            setHasRunCompletionSignal(false);
            if (widgetAction !== undefined) {
                throw error;
            }
            const message = getSendErrorMessage(
                error,
                SEND_MESSAGE_ERROR_MESSAGE,
            );
            if (message !== null) {
                setErrorMessage(message);
            }
            return false;
        }
    };

    const assistantPromptActionHandler: AssistantPromptAction | undefined =
        canSendInSelectedConversation && !isInputBlockedByPendingApproval
            ? (content) =>
                  handleSubmit(
                      content,
                      isAdminConversationSource ? undefined : 'chat_ui_action',
                  )
            : undefined;
    // Support controls on dynamic widget errors. "Ask Phoebe" is offered only
    // where widget metadata may be sent (never an admin trace); "Show source"
    // and inspector registration follow the debug panel being open.
    const dynamicWidgetSupportActions: DynamicWidgetSupportActions = {
        isInspectionOpen: debugPanelOpen === true,
        ...(isAdminConversationSource || !canSendInSelectedConversation
            ? {}
            : { onAskPhoebe: askPhoebeAboutDynamicWidget }),
        ...(debugPanelOpen === true
            ? { onShowSource: showDynamicWidgetSource }
            : {}),
    };
    const retryAfterRunErrorHandler: (() => void) | undefined =
        canSendInSelectedConversation
            ? () => {
                  void handleRetryAfterRunError();
              }
            : undefined;
    const isRetryAfterRunErrorDisabled =
        isCreating || !canSendInSelectedConversation;

    const derivedLiveStatus = useMemo<LiveRunStatus | null>(
        () => deriveLiveStatusFromRun(streamOverlayRun),
        [streamOverlayRun],
    );
    const shouldRenderCompletedCurrentRunWrapper = useMemo(
        () => shouldRenderCompletedRunWrapper(streamOverlayRun),
        [streamOverlayRun],
    );
    const completedRunStatus = useMemo<LiveRunStatus | null>(() => {
        if (
            !streamOverlayRun ||
            streamOverlayRun.status === 'running' ||
            !shouldRenderCompletedCurrentRunWrapper
        ) {
            return null;
        }
        return {
            phase: 'thinking',
            label: 'Thought',
            detail: null,
            toolName: null,
        };
    }, [shouldRenderCompletedCurrentRunWrapper, streamOverlayRun]);
    const hasLiveRunTreeContent = useMemo(
        () => hasRenderableLiveRunTreeContent(streamOverlayRun),
        [streamOverlayRun],
    );
    const isLiveRunExpanded =
        streamOverlayRun?.runId !== undefined &&
        (expandedLiveRunId === streamOverlayRun.runId ||
            subagentDraftProgress !== null);
    const isLiveRunExpandable =
        streamOverlayRun !== null &&
        (hasLiveRunTreeContent || subagentDraftProgress !== null) &&
        (streamOverlayRun.status === 'running' ||
            shouldRenderCompletedCurrentRunWrapper);
    const lastTimelineEntry =
        timelineEntriesBeforeQueuedUserMessages[
            timelineEntriesBeforeQueuedUserMessages.length - 1
        ];
    const hasActiveCompletedRunInTimeline =
        timelineEntriesBeforeQueuedUserMessages.length > 0 &&
        ((shouldUsePhoebeEventRead
            ? currentRunEventStreamIsGenerating
            : isActiveRunWithoutStream) ||
            streamOverlayRun?.status === 'running') &&
        lastTimelineEntry?.kind === 'completed-run' &&
        (lastTimelineEntry.suppressLiveStatus === true ||
            hasRenderableLiveRunTreeContent(lastTimelineEntry.run));
    // The "Thinking" status row is driven purely by run state now that pending
    // user messages render as pills above the composer. The pill carries the
    // "sent / queued" feedback; the transcript spinner reflects the actual
    // agent activity (generating tokens, claimed lease, pending work to drain).
    const shouldForceGeneratingOnlyLiveStatus =
        effectiveIsAwaitingRunCompletion || isOutreachAnalysisActive;
    const shouldShowGeneratingOnlyLiveStatus = shouldUsePhoebeEventRead
        ? currentRunEventStreamIsGenerating ||
          currentRunEventStreamIsBusy ||
          shouldForceGeneratingOnlyLiveStatus
        : isAgentBusy;
    const generatingOnlyLiveStatus: LiveRunStatus | null =
        shouldShowGeneratingOnlyLiveStatus &&
        !currentStreamRun &&
        !hasActiveCompletedRunInTimeline
            ? {
                  phase: 'thinking',
                  label: 'Thinking',
                  detail: null,
                  toolName: null,
              }
            : null;
    // Suppress any "Thinking" status while a prior run's final assistant
    // message is still typewriter-animating. We narrow on `!isGenerating` so
    // an actively streaming current-run message keeps its "Thinking" header.
    const isPriorAssistantMessageAnimating =
        latestAnimatedAssistantMessageId !== null &&
        !currentRunEventStreamIsGenerating;
    const effectiveLiveStatus: LiveRunStatus | null =
        isAgentBusy || shouldForceGeneratingOnlyLiveStatus
            ? (manualLiveStatus ??
              derivedLiveStatus ??
              generatingOnlyLiveStatus)
            : completedRunStatus;
    const shouldShowLiveArtifactsInline =
        (streamOverlayArtifactEntries.length > 0 ||
            streamOverlayReportArtifactEntries.length > 0) &&
        streamOverlayAssistantMessages.length > 0;
    const streamOverlayInlineEntries = useMemo<
        Array<StreamOverlayInlineEntry>
    >(() => {
        if (!shouldRenderStreamOverlay) {
            return [];
        }
        return sortTimelineEntriesChronologically([
            ...liveArtifactEntries,
            ...liveReportArtifactEntries,
            ...streamedAssistantMessages,
        ]);
    }, [
        liveArtifactEntries,
        liveReportArtifactEntries,
        shouldRenderStreamOverlay,
        streamedAssistantMessages,
    ]);
    const liveStatusForDisplay: LiveRunStatus | null =
        isPriorAssistantMessageAnimating &&
        effectiveLiveStatus?.label === 'Thinking'
            ? null
            : effectiveLiveStatus;
    const shouldShowLiveStatusSpinner =
        streamOverlayRun?.status === 'running' || !streamOverlayRun;
    const currentStreamRunId = streamOverlayRun?.runId ?? null;
    const currentStreamRunStatus = streamOverlayRun?.status ?? null;
    useEffect(() => {
        if (!currentStreamRunId || !currentStreamRunStatus) {
            return;
        }

        if (currentStreamRunStatus === 'running') {
            if (!hasLiveRunTreeContent && subagentDraftProgress === null) {
                return;
            }

            // In callout mode, always auto-expand when there is content.
            // In general mode, auto-expand only when the run was seeded
            // from persisted data (negative sequences) so the thinking
            // trace transitions smoothly from the completed-run wrapper
            // to the live run tree after refresh.
            const shouldAutoExpand =
                isCalloutMode ||
                subagentDraftProgress !== null ||
                streamOverlayRun?.orderedNodeIds.some((nodeId) => {
                    const node = streamOverlayRun.nodesById[nodeId];
                    return node !== undefined && node.sequence < 0;
                });
            if (
                shouldAutoExpand &&
                autoExpandDismissedRunId !== currentStreamRunId
            ) {
                setExpandedLiveRunId((current) =>
                    current === currentStreamRunId
                        ? current
                        : currentStreamRunId,
                );
            }
            return;
        }

        if (isCalloutMode) {
            setExpandedLiveRunId((current) =>
                current === currentStreamRunId ? null : current,
            );
        }
    }, [
        autoExpandDismissedRunId,
        currentStreamRunId,
        currentStreamRunStatus,
        hasLiveRunTreeContent,
        isCalloutMode,
        streamOverlayRun,
        subagentDraftProgress,
    ]);

    useEffect(() => {
        if (!streamOverlayRun) {
            setExpandedLiveRunId(null);
            setAutoExpandDismissedRunId(null);
        }
    }, [streamOverlayRun]);

    const inputDisabled =
        isSending ||
        (!canQueueMessageWhileAgentBusy && isAgentBusy) ||
        isInputBlockedByPendingApproval ||
        isLoading ||
        isCreating ||
        (isCalloutMode &&
            !selectedConversationId &&
            !canCreateOutreachRunOnSubmit) ||
        !canSendInSelectedConversation;
    const textInputDisabled = inputDisabled || isRecording || isTranscribing;
    const isVoiceInputDisabled = inputDisabled || isTranscribing;
    const lastTurnHasRunError = useMemo(() => {
        const turns = legacyTurns;
        if (!turns || turns.length === 0) {
            return false;
        }
        const lastTurn = turns.reduce((latest, turn) =>
            turn.sequence > latest.sequence ? turn : latest,
        );
        for (const item of lastTurn.items) {
            const rawJson = (item as ToolAwareItem).raw_json;
            if (
                rawJson !== null &&
                typeof rawJson === 'object' &&
                'type' in rawJson &&
                (rawJson as { readonly type?: unknown }).type === 'run_error'
            ) {
                return true;
            }
        }
        return false;
    }, [legacyTurns]);
    const runErrorPromptIsInline =
        (hasRunError || lastTurnHasRunError) && !voiceInputError;
    const displayErrorMessage =
        voiceInputError ??
        uploadAttachments.errorMessage ??
        (runErrorPromptIsInline ? null : errorMessage);
    // A new general chat carries the pick in its create request; an outreach
    // chat has its run ensured on send and the pick is applied right after.
    const canSetPendingModelForNewChat =
        !isAdminConversationSource &&
        ((isOutreachTarget && shouldUseRunNativeConversation) ||
            (!isCalloutMode &&
                MODEL_PICKABLE_NEW_CHAT_RUN_TYPES.includes(pendingRunType)));
    const canShowModelPicker =
        userIsAdmin &&
        !selectedRunIsOrchestrator &&
        (selectedConversationId !== null || canSetPendingModelForNewChat);
    const modelPicker = canShowModelPicker ? (
        <AgentModelPicker
            pinnedModel={pinnedAgentModel}
            resolvedModel={resolvedAgentModel}
            options={agentModelOptions}
            disabled={
                isUpdatingModel ||
                isAgentBusy ||
                isConversationLoading ||
                textInputDisabled
            }
            onModelChange={(activeModel) => {
                void updateActiveAgentModel(activeModel);
            }}
        />
    ) : null;
    const handleNewChatRunTypeChange = (runType: NewChatRunType) => {
        setPendingRunType(runType);
        window.requestAnimationFrame(() => {
            const activeElement = document.activeElement;
            if (
                activeElement === null ||
                activeElement === document.body ||
                (activeElement instanceof HTMLElement &&
                    activeElement.dataset['newChatRunTypeTrigger'] ===
                        'true') ||
                composerFormRef.current?.contains(activeElement) === true
            ) {
                chatInputRef.current?.focus();
            }
        });
    };
    const newChatRunTypeOptions: ReadonlyArray<NewChatRunType> = [
        'general',
        ...(orchestratorProductChatEnabled ? (['orchestrator'] as const) : []),
    ];
    const canChooseNewChatRunType =
        userIsAdmin &&
        orchestratorProductChatEnabled &&
        selectedConversationId === null &&
        effectiveChatBackendKind === 'run_native' &&
        clientSurface !== 'extension' &&
        !isCalloutMode;
    const showInternalReadonlyBadge =
        organizationRun?.is_agent_v3_internal === true;
    const runTypePicker = canChooseNewChatRunType ? (
        <NewChatRunTypePicker
            runType={pendingRunType}
            runTypeOptions={newChatRunTypeOptions}
            disabled={isCreating || isSending}
            // The picker's label is the widest thing in the header, so it
            // yields early when the read-only badge is also competing for
            // the row; the badge already names the mode.
            className={cn(
                '@max-[360px]/chat-header:gap-0.5 @max-[360px]/chat-header:px-1.5 @max-[360px]/chat-header:[&_[data-new-chat-run-type-label]]:sr-only',
                showInternalReadonlyBadge &&
                    '@max-[560px]/chat-header:gap-0.5 @max-[560px]/chat-header:px-1.5 @max-[560px]/chat-header:[&_[data-new-chat-run-type-label]]:sr-only',
            )}
            onRunTypeChange={handleNewChatRunTypeChange}
        />
    ) : null;
    const composerLeadingControl = modelPicker;

    const selectedConversation = conversations?.find(
        (conversation) => conversation.id === selectedConversationId,
    );
    const canShareSelectedConversation = selectedConversationId !== null;
    const selectedAgentTraceConversationId = shouldUseRunNativeConversation
        ? selectedCompatAgentConversationId
        : selectedConversationId;
    useEffect(() => {
        onAgentTraceConversationIdChange?.(selectedAgentTraceConversationId);
    }, [selectedAgentTraceConversationId, onAgentTraceConversationIdChange]);
    const canOpenSelectedAgentTrace =
        canOpenAgentTrace && selectedAgentTraceConversationId !== null;
    const selectedConversationTitle = visibleChatTitle(
        conversation?.title || selectedConversation?.title,
    );
    // In expanded mode, surface conversation history as a ChatGPT-style
    // left rail and drop the title-chevron dropdown (the rail covers it).
    // Callout chat keeps its single-conversation layout.
    const isExpandedConversationMode = isExpanded && !isCalloutMode;
    // Fullscreen surfaces the centered hero for general chat. The docked
    // sidebar uses its own compact empty-state card with starter rows above
    // the composer, including a context-specific variant for callouts.
    const isEmptyConversationState =
        !readOnly &&
        !shouldShowConversationLoader &&
        timelineEntriesBeforeQueuedUserMessages.length === 0 &&
        pendingMessagePillItems.length === 0 &&
        !streamOverlayRun &&
        !isAgentBusy;
    const showCenteredEmptyComposer =
        isExpandedConversationMode && isEmptyConversationState;
    const showDockedEmptyState =
        !isExpandedConversationMode && isEmptyConversationState;
    // Toggled by the docked sidebar's "Review my chats" prompt; renders
    // ReviewMyRunsCard in place of AgentSidebarEmptyState, in the same footer
    // slot above the composer. Reset on conversation switch so a fresh empty
    // conversation always starts on the prompt list, not a stale card. Also
    // reset on an organization switch: a null-selection-to-null-selection
    // org change never touches selectedConversationId, so without this the
    // card could stay open into a non-training org (belt: the render below
    // also gates on showTrainingModeBadge) or silently reopen on return to a
    // training org.
    const [isReviewingRuns, setIsReviewingRuns] = useState(false);
    useEffect(() => {
        setIsReviewingRuns(false);
    }, [selectedConversationId, organizationId]);
    const debugSnapshot = useMemo<PhoebeChatDebugSnapshot | null>(() => {
        if (!onDebugSnapshotChange) {
            return null;
        }

        return buildPhoebeChatDebugSnapshot({
            conversationId: selectedConversationId,
            conversationTitle: selectedConversationTitle,
            phoebeRunId: phoebeAgentRunId,
            agentGeneration: savedRunVersionFailedToLoad ? null : 'v2',
            eventReadEnabled: shouldUsePhoebeEventRead,
            connectionStatus: phoebeRunEventStream.connectionStatus,
            runState: phoebeRunEventStream.runState,
            events: shouldUsePhoebeEventRead ? phoebeRunEventStream.events : [],
            pendingEvents: shouldUsePhoebeEventRead
                ? phoebeRunEventStream.pendingEvents
                : [],
        });
    }, [
        onDebugSnapshotChange,
        phoebeAgentRunId,
        phoebeRunEventStream.connectionStatus,
        phoebeRunEventStream.events,
        phoebeRunEventStream.pendingEvents,
        phoebeRunEventStream.runState,
        selectedConversationId,
        selectedConversationTitle,
        shouldUsePhoebeEventRead,
        savedRunVersionFailedToLoad,
    ]);
    useEffect(() => {
        onDebugSnapshotChange?.(debugSnapshot);
    }, [debugSnapshot, onDebugSnapshotChange]);
    useEffect(() => {
        return () => {
            onDebugSnapshotChange?.(null);
        };
    }, [onDebugSnapshotChange]);
    const groupedConversations = useMemo(() => {
        // Skip conversations that never received a turn — they clutter the
        // list with empty "New AI chat" stubs from accidental opens.
        const conversationSummaries: Array<OrganizationConversationSummary> = (
            conversations ?? []
        ).filter((summary) => {
            if (
                'turn_count' in summary &&
                typeof summary.turn_count === 'number' &&
                summary.turn_count === 0
            ) {
                return false;
            }
            if ('last_turn_at' in summary && summary.last_turn_at === null) {
                return false;
            }
            return true;
        });
        if (conversationSummaries.length === 0) {
            return [];
        }

        const now = new Date();
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);

        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);

        const startOfSevenDaysAgo = new Date(startOfToday);
        startOfSevenDaysAgo.setDate(startOfSevenDaysAgo.getDate() - 7);

        const startOfThirtyDaysAgo = new Date(startOfToday);
        startOfThirtyDaysAgo.setDate(startOfThirtyDaysAgo.getDate() - 30);

        const buckets: Record<
            'today' | 'yesterday' | 'previous7' | 'previous30' | 'older',
            Array<OrganizationConversationSummary>
        > = {
            today: [],
            yesterday: [],
            previous7: [],
            previous30: [],
            older: [],
        };

        for (const conversationSummary of conversationSummaries) {
            const activityAt =
                ('last_activity_at' in conversationSummary
                    ? conversationSummary.last_activity_at
                    : null) ??
                ('last_turn_at' in conversationSummary
                    ? conversationSummary.last_turn_at
                    : null) ??
                conversationSummary.updated_at ??
                conversationSummary.created_at;
            const activityDate = new Date(activityAt);
            if (Number.isNaN(activityDate.getTime())) {
                buckets.older.push(conversationSummary);
                continue;
            }

            if (activityDate >= startOfToday) {
                buckets.today.push(conversationSummary);
                continue;
            }
            if (activityDate >= startOfYesterday) {
                buckets.yesterday.push(conversationSummary);
                continue;
            }
            if (activityDate >= startOfSevenDaysAgo) {
                buckets.previous7.push(conversationSummary);
                continue;
            }
            if (activityDate >= startOfThirtyDaysAgo) {
                buckets.previous30.push(conversationSummary);
                continue;
            }
            buckets.older.push(conversationSummary);
        }

        return [
            { label: 'Today', items: buckets.today },
            { label: 'Yesterday', items: buckets.yesterday },
            { label: 'Previous 7 days', items: buckets.previous7 },
            { label: 'Previous 30 days', items: buckets.previous30 },
            { label: 'Older', items: buckets.older },
        ].filter((group) => group.items.length > 0);
    }, [conversations]);

    const toggleLiveRunTree = () => {
        if (!isLiveRunExpandable || !streamOverlayRun) {
            return;
        }
        const isCollapsingCurrentRun = isLiveRunExpanded;
        setExpandedLiveRunId(
            isCollapsingCurrentRun ? null : streamOverlayRun.runId,
        );
        setAutoExpandDismissedRunId(
            isCollapsingCurrentRun ? streamOverlayRun.runId : null,
        );
    };

    const handleStarterPromptClick = (prompt: string) => {
        if (!canSendInSelectedConversation || isInputBlockedByPendingApproval) {
            return false;
        }
        return handleSubmit(prompt);
    };

    const handleDockedSeedPrompt = (seedText: string) => {
        if (!canSendInSelectedConversation || isInputBlockedByPendingApproval) {
            return;
        }
        chatInputRef.current?.clear();
        chatInputRef.current?.appendText(seedText);
        chatInputRef.current?.focus();
    };

    // Reset to the lazy new-chat draft; the ref gates the resolver.
    const startNewDraftChat = useCallback(() => {
        setPendingRunType('general');
        isDraftingNewChatRef.current = true;
        requestedConversationIdRef.current = null;
        explicitRequestedSelectionRef.current = null;
        chatInputRef.current?.clear();
        setSelectedConversationId(null);
        onSelectedConversationIdChange?.(null);
        setErrorMessage(null);
        setHasRunError(false);
        setVoiceInputError(null);
    }, [onSelectedConversationIdChange, setVoiceInputError]);

    // A surface outside the chat (for example the sidebar checklist)
    // requested a composer draft via useUI. These drafts are general-agent
    // prompts, so an outreach-scoped view must never take one — on a callout
    // detail page the visible composer is the outreach thread. Only the
    // view whose composer is actually laid out on screen consumes it, so a
    // mounted-but-hidden view never swallows the draft. Fill the composer
    // once it can accept input; never auto-send.
    const [prefillRetryTick, setPrefillRetryTick] = useState(0);
    useEffect(() => {
        if (requestedComposerPrefill === null) {
            return undefined;
        }
        // Only the general product chat takes the draft: the outreach thread is
        // a different conversation, and the admin trace playground is another
        // organization's.
        if (isOutreachTarget || isAdminConversationSource) {
            return undefined;
        }
        if (requestedComposerPrefill.newConversation === true) {
            if (selectedConversationId !== null) {
                startNewDraftChat();
                return undefined;
            }
            if (!isDraftingNewChatRef.current) {
                isDraftingNewChatRef.current = true;
                setPendingRunType('general');
            }
        }
        if (!canSendInSelectedConversation || isInputBlockedByPendingApproval) {
            return undefined;
        }
        if (chatInputRef.current?.isVisible() !== true) {
            // On a fresh panel open the composer often isn't rendered yet
            // (conversation view still loading) and no dependency changes
            // when it appears, so poll until it does.
            const retryTimeout = window.setTimeout(
                () => setPrefillRetryTick((tick) => tick + 1),
                150,
            );
            return () => window.clearTimeout(retryTimeout);
        }
        // Replace, don't append: "Try in chat" is an explicit request for THIS
        // prompt, and the composer usually still holds the last one that was
        // seeded. Appending would concatenate two prompts, and skipping a
        // non-empty composer made every click after the first do nothing.
        chatInputRef.current.clear();
        chatInputRef.current.appendText(requestedComposerPrefill.text);
        chatInputRef.current.focus();
        // The draft actually landed. The guards above defer the seed rather
        // than dropping it, so this trails the click event instead of
        // filtering it — it separates "never got a composer" (reloaded away, or
        // never opened chat) from "saw the prompt and bailed".
        try {
            // Only the two tracked prefill surfaces emit an event this way;
            // other sources (e.g. training_coverage) seed silently here, but
            // may still supply their own onDelivered below.
            if (requestedComposerPrefill.source === 'learn_phoebe') {
                posthog?.capture(EVENTS.LEARN_PHOEBE_PROMPT_SEEDED, {
                    organization_id: organizationId,
                });
            } else if (
                requestedComposerPrefill.source === 'calendar_selection'
            ) {
                posthog?.capture(EVENTS.CALENDAR_OUTREACH_PROMPT_SEEDED, {
                    organization_id: organizationId,
                });
            }
        } catch {
            // Best-effort.
        }
        // Let the caller know the draft actually reached a visible,
        // sendable composer — not just that a seed was requested. Guarded
        // the same way as the events above (never breaks the seed itself).
        try {
            requestedComposerPrefill.onDelivered?.();
        } catch {
            // Best-effort.
        }
        clearRequestedComposerPrefill();
        return undefined;
    }, [
        requestedComposerPrefill,
        isOutreachTarget,
        isAdminConversationSource,
        canSendInSelectedConversation,
        isInputBlockedByPendingApproval,
        clearRequestedComposerPrefill,
        organizationId,
        posthog,
        selectedConversationId,
        startNewDraftChat,
        // Not read directly, but the composer's layout visibility flips
        // with the docked panel; re-run the visibility check when it opens.
        isDockedChatVisible,
        prefillRetryTick,
    ]);

    // Activity-board drafts include entity-pill display data and the source
    // event IDs to bind to the run when the user sends. Like plain drafts,
    // they are reviewable and are never auto-sent.
    useEffect(() => {
        if (requestedComposerDraft === null) {
            return;
        }
        if (isOutreachTarget || isAdminConversationSource) {
            return;
        }
        if (!canSendInSelectedConversation || isInputBlockedByPendingApproval) {
            return;
        }
        // Opening the docked panel can remount the composer in the same React
        // commit. Seed on the next frame so the final visible editor receives
        // the draft rather than the outgoing hidden instance.
        const frame = window.requestAnimationFrame(() => {
            if (chatInputRef.current?.isVisible() !== true) {
                return;
            }
            chatInputRef.current.replaceDraft(requestedComposerDraft);
            pendingActivityEventIdsRef.current =
                requestedComposerDraft.activityEventIds;
            clearRequestedComposerDraft();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [
        requestedComposerDraft,
        isOutreachTarget,
        isAdminConversationSource,
        canSendInSelectedConversation,
        isInputBlockedByPendingApproval,
        clearRequestedComposerDraft,
        isDockedChatVisible,
    ]);

    // Picking a chat out of the history dropdown is as explicit as opening
    // one by deep link, so it pins too: the chat stays put if it ages out of
    // the 12h list while the user is sitting on it.
    const handleSelectConversationFromHistory = (conversationId: string) => {
        explicitRequestedSelectionRef.current = {
            conversationId,
            organizationId,
        };
        setSelectedConversationId(conversationId);
    };

    const handleCreateConversationClick = useCallback(() => {
        startNewDraftChat();
        chatInputRef.current?.focus();
    }, [startNewDraftChat]);

    // A surface outside the chat asked for a fresh draft chat via useUI
    // (openNewChat). Only the general product chat honors it — an
    // outreach-scoped or admin view must not swallow the request while the
    // general panel is what the user asked to open.
    useEffect(() => {
        if (!newChatRequested) {
            return;
        }
        if (isOutreachTarget || isAdminConversationSource) {
            return;
        }
        clearNewChatRequest();
        handleCreateConversationClick();
    }, [
        newChatRequested,
        isOutreachTarget,
        isAdminConversationSource,
        clearNewChatRequest,
        handleCreateConversationClick,
    ]);

    const composerForm = readOnly ? null : (
        <React.Fragment>
            {/* Keyed per conversation: the dock's receipts are instance
                state, and a receipt must never outlive the chat it came
                from. */}
            {pendingMessagePillItems.length > 0 ? (
                <div className="mb-2 flex flex-col gap-1.5">
                    {pendingMessagePillItems.map((item) => (
                        <PendingMessagePill
                            key={item.id}
                            content={item.content}
                            label={item.label}
                            references={item.references}
                        />
                    ))}
                </div>
            ) : null}
            {firstTypingUser ? (
                <div className="text-muted-foreground mb-1 flex items-center gap-1.5 px-1 text-xs">
                    <UserAvatar
                        displayName={firstTypingUser.displayName}
                        colorKey={firstTypingUser.userId}
                    />
                    <span>{formatChatTypingLabel(chatTypingUsers)}</span>
                </div>
            ) : null}
            {!isOutreachTarget && !isAdminConversationSource ? (
                <Fragment>
                    <ReferenceTray />
                    <ActivityReferenceTray />
                </Fragment>
            ) : null}
            <form
                ref={composerFormRef}
                onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                }}
            >
                <ChatInput
                    ref={chatInputRef}
                    onSubmit={(content) => {
                        void handleSubmit(content);
                    }}
                    disabled={textInputDisabled}
                    isAgentBusy={isAgentBusy}
                    canSubmitWhileAgentBusy={canQueueMessageWhileAgentBusy}
                    isStopping={isStopping}
                    canStopRun={canSendInSelectedConversation}
                    imageAttachments={
                        canUploadRunAttachments
                            ? uploadAttachments.attachments
                            : []
                    }
                    onImageFilesSelected={
                        canUploadRunAttachments
                            ? uploadAttachments.onFilesSelected
                            : undefined
                    }
                    onRemoveImageAttachment={
                        canUploadRunAttachments
                            ? uploadAttachments.removeAttachment
                            : undefined
                    }
                    imageUploadDisabled={
                        !canSendInSelectedConversation ||
                        !canUploadRunAttachments
                    }
                    acceptedUploadMimeTypes={
                        canUploadRunAttachments
                            ? uploadAttachments.acceptedMimeTypes
                            : undefined
                    }
                    onStopRun={() => {
                        void stopRun();
                    }}
                    placeholder={
                        isSelectedConversationCostCapped
                            ? 'Conversation closed'
                            : !canSendInSelectedConversation
                              ? 'View-only conversation'
                              : isRecording
                                ? 'Listening…'
                                : isCalloutMode
                                  ? 'Ask about this open shift…'
                                  : 'Find shifts, callouts, or caregivers…'
                    }
                    organizationId={organizationId}
                    canRecordAudio={canRecordAudio}
                    isRecording={isRecording}
                    isTranscribing={isTranscribing}
                    isVoiceInputDisabled={isVoiceInputDisabled}
                    onStartRecording={() => {
                        void startRecording();
                    }}
                    onStopRecording={stopRecording}
                    onEmptyChange={(nowEmpty) => {
                        if (nowEmpty) {
                            draftIncludesVoiceInputRef.current = false;
                            pendingActivityEventIdsRef.current = [];
                        }
                    }}
                    onTyping={notifyChatTyping}
                    leadingControl={composerLeadingControl}
                    presentation={onHide ? 'mason' : 'default'}
                />
                {displayErrorMessage && (
                    <div className="mt-1 flex items-center gap-2">
                        <p className="text-destructive text-xs">
                            {displayErrorMessage}
                        </p>
                        {displayErrorMessage ===
                            VOICE_INPUT_PERMISSION_ERROR_MESSAGE &&
                            onRequestMicrophonePermission && (
                                <button
                                    type="button"
                                    className="text-xs font-medium underline underline-offset-2"
                                    onClick={onRequestMicrophonePermission}
                                >
                                    Enable microphone
                                </button>
                            )}
                    </div>
                )}
                {isInputBlockedByPendingApproval && (
                    <div className="mt-1">
                        <p className="text-muted-foreground text-xs">
                            {PENDING_APPROVAL_INPUT_BLOCKED_MESSAGE}
                        </p>
                    </div>
                )}
                {isSelectedConversationCostCapped ? (
                    <div className="mt-1">
                        <p className="text-muted-foreground text-xs">
                            This conversation reached its cost limit and is
                            closed. Start a new conversation.
                        </p>
                    </div>
                ) : !canSendInSelectedConversation ? (
                    <div className="mt-1">
                        <p className="text-muted-foreground text-xs">
                            View-only conversation. You don’t have permission to
                            send messages.
                        </p>
                    </div>
                ) : null}
            </form>
        </React.Fragment>
    );
    // Only training orgs resolve note destinations, so only they have a
    // Learned tab to look at.
    const changesTab = trainingMode.isInTrainingMode ? (
        <SegmentedControl
            options={(['chat', 'changes'] as const).map((tab) => ({
                value: tab,
                label: CHAT_TAB_LABELS[tab],
            }))}
            value={activeChatTab}
            onValueChange={setActiveChatTab}
            layout="auto"
            size="xs"
            className="shrink-0"
            aria-label="Chat view"
        />
    ) : null;

    const chatBody = (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <div className="@container/chat-header py-2 pr-1 pl-3">
                <div className="flex items-center justify-between gap-1">
                    <div className="flex min-w-0 flex-1 items-center gap-2 @max-[420px]/chat-header:gap-1.5">
                        {changesTab}
                        {isExpandedConversationMode || isCalloutMode ? (
                            <span
                                className="h-8 max-w-[480px] min-w-0 flex-[0_1_auto] truncate px-2 py-1 text-sm leading-6 font-medium"
                                title={selectedConversationTitle}
                            >
                                {selectedConversationTitle}
                            </span>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild={true}>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        className="-ml-2 h-8 max-w-[480px] min-w-0 flex-[0_1_auto] justify-start gap-1 px-2 text-xs font-medium"
                                        title={selectedConversationTitle}
                                    >
                                        <span className="truncate">
                                            {selectedConversationTitle}
                                        </span>
                                        <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    className="max-h-[70vh] w-[340px] overflow-y-auto rounded-xl p-2"
                                >
                                    {groupedConversations.length === 0 ? (
                                        <p className="text-muted-foreground px-2 py-1 text-xs">
                                            Start a conversation to chat with
                                            Phoebe.
                                        </p>
                                    ) : (
                                        groupedConversations.map(
                                            (group, index) => (
                                                <div key={group.label}>
                                                    {index > 0 && (
                                                        <DropdownMenuSeparator className="my-2" />
                                                    )}
                                                    <p className="text-muted-foreground px-2 py-1 text-xs font-medium">
                                                        {group.label}
                                                    </p>
                                                    {group.items.map(
                                                        (
                                                            conversationSummary,
                                                        ) => (
                                                            <ContextMenu
                                                                key={
                                                                    conversationSummary.id
                                                                }
                                                            >
                                                                <ContextMenuTrigger
                                                                    asChild={
                                                                        true
                                                                    }
                                                                >
                                                                    <DropdownMenuItem
                                                                        className="justify-between py-2"
                                                                        onSelect={() =>
                                                                            handleSelectConversationFromHistory(
                                                                                conversationSummary.id,
                                                                            )
                                                                        }
                                                                    >
                                                                        <span className="flex min-w-0 items-center gap-1.5">
                                                                            <span className="truncate">
                                                                                {conversationSummary.title ??
                                                                                    'Untitled conversation'}
                                                                            </span>
                                                                            {'is_agent_v3_internal' in
                                                                                conversationSummary &&
                                                                                conversationSummary.is_agent_v3_internal ===
                                                                                    true && (
                                                                                    <InternalReadonlyBadge />
                                                                                )}
                                                                        </span>
                                                                        {conversationSummary.id ===
                                                                            selectedConversationId && (
                                                                            <Check className="h-4 w-4 shrink-0 opacity-80" />
                                                                        )}
                                                                    </DropdownMenuItem>
                                                                </ContextMenuTrigger>
                                                                <ContextMenuContent>
                                                                    {'conversation_type' in
                                                                        conversationSummary &&
                                                                        conversationSummary.conversation_type ===
                                                                            'outreach_analysis' && (
                                                                            <React.Fragment>
                                                                                <ContextMenuItem
                                                                                    onClick={() => {
                                                                                        void navigator.clipboard.writeText(
                                                                                            conversationSummary.id,
                                                                                        );
                                                                                        toast.success(
                                                                                            'Chat ID copied',
                                                                                            {
                                                                                                persistNotification: true,
                                                                                                notificationType:
                                                                                                    'app_feedback',
                                                                                            },
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <Hash className="mr-2 h-4 w-4" />
                                                                                    Copy
                                                                                    chat
                                                                                    ID
                                                                                </ContextMenuItem>
                                                                                <ContextMenuItem
                                                                                    onClick={() => {
                                                                                        const url =
                                                                                            buildPanelChatLink(
                                                                                                conversationSummary.id,
                                                                                            );
                                                                                        void navigator.clipboard.writeText(
                                                                                            url,
                                                                                        );
                                                                                        toast.success(
                                                                                            'Link copied',
                                                                                            {
                                                                                                persistNotification: true,
                                                                                                notificationType:
                                                                                                    'app_feedback',
                                                                                            },
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <Link2 className="mr-2 h-4 w-4" />
                                                                                    Copy
                                                                                    link
                                                                                </ContextMenuItem>
                                                                            </React.Fragment>
                                                                        )}
                                                                </ContextMenuContent>
                                                            </ContextMenu>
                                                        ),
                                                    )}
                                                </div>
                                            ),
                                        )
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        <ChatViewerPresence
                            viewers={chatViewers}
                            currentUserId={user?.id ?? null}
                        />
                        {showTrainingModeBadge && (
                            <TrainingModeBadge className="@max-[360px]/chat-header:gap-0 @max-[360px]/chat-header:px-1 @max-[360px]/chat-header:[&_[data-training-mode-badge-label]]:sr-only">
                                In training
                            </TrainingModeBadge>
                        )}
                        {showInternalReadonlyBadge && (
                            <InternalReadonlyBadge className="@max-[360px]/chat-header:gap-0 @max-[360px]/chat-header:px-1 @max-[360px]/chat-header:[&_[data-internal-readonly-badge-label]]:sr-only" />
                        )}
                        {!isCalloutMode && providerErrorBadge ? (
                            <React.Fragment>
                                <Tooltip delayDuration={300}>
                                    <TooltipTrigger asChild={true}>
                                        <Badge
                                            variant="outline"
                                            className="max-w-[180px] min-w-0 shrink truncate rounded-md border-red-300 bg-red-50 text-red-700 @max-[560px]/chat-header:max-w-[120px] @max-[460px]/chat-header:hidden dark:border-red-900 dark:bg-red-950/50 dark:text-red-300"
                                        >
                                            {providerErrorBadge.label}
                                        </Badge>
                                    </TooltipTrigger>
                                    {providerErrorBadge.detail ? (
                                        <TooltipContent>
                                            <p className="max-w-xs">
                                                {providerErrorBadge.detail}
                                            </p>
                                        </TooltipContent>
                                    ) : null}
                                </Tooltip>
                                {providerErrorBadge.kind === 'irrecoverable' &&
                                retryAfterRunErrorHandler ? (
                                    <button
                                        type="button"
                                        disabled={isRetryAfterRunErrorDisabled}
                                        onClick={retryAfterRunErrorHandler}
                                        className="text-destructive shrink-0 cursor-pointer rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 @max-[620px]/chat-header:hidden dark:border-red-900 dark:bg-red-950/50 dark:hover:bg-red-900/40"
                                    >
                                        Click here to retry
                                    </button>
                                ) : null}
                            </React.Fragment>
                        ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-0.5 @max-[360px]/chat-header:gap-0">
                        {runTypePicker}
                        {/* "New chat" is the primary action — always
                                surface it as a standalone button, never
                                collapse it into the kebab. */}
                        {!isCalloutMode && (
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={handleCreateConversationClick}
                                disabled={isCreating || isSending}
                                aria-label="Create new chat"
                                className="size-7"
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        )}
                        <div className="flex items-center gap-0.5 @max-[360px]/chat-header:hidden">
                            {onDebugPanelToggle && (
                                <PhoebeChatDebugPanelToggle
                                    isOpen={debugPanelOpen}
                                    onToggle={onDebugPanelToggle}
                                />
                            )}
                        </div>
                        {chatMode && onChangeChatMode && (
                            <ChatModeSelector
                                chatMode={chatMode}
                                onChangeChatMode={onChangeChatMode}
                            />
                        )}
                        {((selectedConversationId !== null &&
                            canShareSelectedConversation) ||
                            onDebugPanelToggle) && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild={true}>
                                    <Button
                                        type="button"
                                        size="icon"
                                        variant="ghost"
                                        aria-label="Chat actions"
                                        className="size-7"
                                    >
                                        <MoreVertical className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {selectedConversationId &&
                                        canShareSelectedConversation && (
                                            <React.Fragment>
                                                <DropdownMenuItem
                                                    onClick={() => {
                                                        void navigator.clipboard
                                                            .writeText(
                                                                selectedConversationId,
                                                            )
                                                            .then(() => {
                                                                toast.success(
                                                                    'Chat ID copied',
                                                                    {
                                                                        persistNotification: true,
                                                                        notificationType:
                                                                            'app_feedback',
                                                                    },
                                                                );
                                                            })
                                                            .catch(() => {
                                                                toast.error(
                                                                    'Could not copy chat ID',
                                                                    {
                                                                        persistNotification: true,
                                                                        notificationType:
                                                                            'app_feedback',
                                                                    },
                                                                );
                                                            });
                                                    }}
                                                >
                                                    <Hash className="mr-2 h-4 w-4" />
                                                    Copy chat ID
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() => {
                                                        // The extension iframe runs on the
                                                        // /extension route, which does not
                                                        // handle ?chatId=. Link to the
                                                        // canonical web-app conversation view,
                                                        // carrying org + mode so the copied
                                                        // link reopens the chat in the right
                                                        // tenant and live/sandbox context.
                                                        const extensionLinkParams =
                                                            new URLSearchParams(
                                                                {
                                                                    conversationId:
                                                                        selectedConversationId ??
                                                                        '',
                                                                },
                                                            );
                                                        if (
                                                            organizationId !==
                                                            null
                                                        ) {
                                                            extensionLinkParams.set(
                                                                'organizationId',
                                                                organizationId,
                                                            );
                                                        }
                                                        if (
                                                            modePreference !==
                                                            undefined
                                                        ) {
                                                            extensionLinkParams.set(
                                                                'mode',
                                                                modePreference,
                                                            );
                                                        }
                                                        const url =
                                                            clientSurface ===
                                                            'extension'
                                                                ? `${window.location.origin}/conversations?${extensionLinkParams.toString()}`
                                                                : buildPanelChatLink(
                                                                      selectedConversationId ??
                                                                          '',
                                                                  );
                                                        void navigator.clipboard
                                                            .writeText(url)
                                                            .then(() => {
                                                                toast.success(
                                                                    'Link copied',
                                                                    {
                                                                        persistNotification: true,
                                                                        notificationType:
                                                                            'app_feedback',
                                                                    },
                                                                );
                                                            })
                                                            .catch(() => {
                                                                toast.error(
                                                                    'Could not copy link',
                                                                    {
                                                                        persistNotification: true,
                                                                        notificationType:
                                                                            'app_feedback',
                                                                    },
                                                                );
                                                            });
                                                    }}
                                                >
                                                    <Link2 className="mr-2 h-4 w-4" />
                                                    Copy link
                                                </DropdownMenuItem>
                                                {canOpenSelectedAgentTrace &&
                                                    (clientSurface ===
                                                        'extension' &&
                                                    onOpenPhoebePathInNewTab ? (
                                                        <DropdownMenuItem
                                                            onClick={() => {
                                                                if (
                                                                    selectedAgentTraceConversationId !==
                                                                    null
                                                                ) {
                                                                    onOpenPhoebePathInNewTab(
                                                                        `/admin/agent-traces/${selectedAgentTraceConversationId}`,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <Wrench className="mr-2 h-4 w-4" />
                                                            Go to agent trace
                                                        </DropdownMenuItem>
                                                    ) : (
                                                        <DropdownMenuItem
                                                            asChild={true}
                                                        >
                                                            <Link
                                                                to="/admin/agent-traces/$conversationId"
                                                                params={{
                                                                    conversationId:
                                                                        selectedAgentTraceConversationId,
                                                                }}
                                                            >
                                                                <Wrench className="mr-2 h-4 w-4" />
                                                                Go to agent
                                                                trace
                                                            </Link>
                                                        </DropdownMenuItem>
                                                    ))}
                                                {canOpenAgentTrace &&
                                                    selectedRunIsOrchestrator &&
                                                    selectedPhoebeRunId !==
                                                        null &&
                                                    (clientSurface ===
                                                        'extension' &&
                                                    onOpenPhoebePathInNewTab ? (
                                                        <DropdownMenuItem
                                                            onClick={() => {
                                                                if (
                                                                    selectedPhoebeRunId !==
                                                                    null
                                                                ) {
                                                                    onOpenPhoebePathInNewTab(
                                                                        `/admin/agent-traces/run/${selectedPhoebeRunId}`,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <List className="mr-2 h-4 w-4" />
                                                            View trace
                                                        </DropdownMenuItem>
                                                    ) : (
                                                        <DropdownMenuItem
                                                            asChild={true}
                                                        >
                                                            <Link
                                                                to="/admin/agent-traces/run/$runId"
                                                                params={{
                                                                    runId: selectedPhoebeRunId,
                                                                }}
                                                            >
                                                                <List className="mr-2 h-4 w-4" />
                                                                View trace
                                                            </Link>
                                                        </DropdownMenuItem>
                                                    ))}
                                            </React.Fragment>
                                        )}
                                    {isCalloutMode &&
                                        isOutreachTarget &&
                                        !isV3BornOutreachTarget &&
                                        targetOutreach?.status ===
                                            'pending' && (
                                            <React.Fragment>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={() =>
                                                        setIsRestartModalOpen(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    <RotateCcw className="mr-2 h-4 w-4" />
                                                    Restart analysis
                                                </DropdownMenuItem>
                                            </React.Fragment>
                                        )}
                                    {isCalloutMode &&
                                        isV3BornOutreachTarget && (
                                            <React.Fragment>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    disabled={
                                                        !outreachRunnerRun?.id
                                                    }
                                                    onClick={() =>
                                                        setIsRunnerResetModalOpen(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    <RotateCcw className="mr-2 h-4 w-4" />
                                                    Start a fresh Phoebe chat
                                                </DropdownMenuItem>
                                            </React.Fragment>
                                        )}
                                    {onDebugPanelToggle && (
                                        <DropdownMenuSeparator />
                                    )}
                                    {onDebugPanelToggle && (
                                        <DropdownMenuItem
                                            onClick={onDebugPanelToggle}
                                        >
                                            <Bug className="mr-2 h-4 w-4" />
                                            {debugPanelOpen
                                                ? 'Hide debug'
                                                : 'Show debug'}
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        {onHide && (
                            <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild={true}>
                                    <Button
                                        type="button"
                                        size="icon"
                                        variant="ghost"
                                        onClick={onHide}
                                        aria-label="Hide chat"
                                        className="size-7"
                                    >
                                        <ChevronsRight className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Hide chat</p>
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
            <div className="@container/chat-pane relative flex min-h-0 flex-1 flex-col overflow-hidden">
                {isSelectedConversationExpired ? (
                    <div className="text-muted-foreground flex shrink-0 items-center gap-2 border-b bg-[#F0F5F5]/40 px-4 py-2 text-xs">
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        <span>
                            This chat has expired. Send a message to pick it
                            back up.
                        </span>
                    </div>
                ) : null}
                {shouldShowConversationLoader ? (
                    <div
                        className="flex flex-1 items-center justify-center"
                        role="status"
                    >
                        <Spinner size="lg" tone="muted" />
                        <span className="sr-only">Loading conversation…</span>
                    </div>
                ) : savedRunVersionFailedToLoad ? (
                    <div
                        className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center"
                        role="alert"
                    >
                        <p className="text-muted-foreground text-sm">
                            Unable to load this chat.
                        </p>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                void refreshConversation();
                            }}
                        >
                            Try again
                        </Button>
                    </div>
                ) : showDockedEmptyState && !isCalloutMode ? (
                    <div className="min-h-0 flex-1">
                        <AgentSidebarEmptyStage />
                    </div>
                ) : showCenteredEmptyComposer ? (
                    <div className="flex flex-1 flex-col items-center justify-center bg-[#F0F5F5]/40 px-6">
                        <div className="mb-6 flex flex-col items-center text-center">
                            <img
                                src="/icon.svg"
                                alt=""
                                aria-hidden="true"
                                className="mb-3 h-8 w-8"
                            />
                            <h2 className="text-[28px] font-semibold text-[#002429]">
                                {getTimeOfDayGreeting(new Date())}
                                {user?.first_name
                                    ? `, ${user.first_name}.`
                                    : '.'}
                            </h2>
                            <p className="mt-1 text-sm text-[#829494]">
                                Ask about shifts, callouts, or caregivers.
                            </p>
                        </div>
                        <div className={CHAT_COLUMN_CLASS}>{composerForm}</div>
                        {canSendInSelectedConversation ? (
                            <div
                                className={cn(
                                    'mt-4 flex flex-wrap justify-center gap-2',
                                    CHAT_COLUMN_CLASS,
                                )}
                            >
                                {STARTER_PROMPTS.map((prompt) => (
                                    <button
                                        key={prompt}
                                        type="button"
                                        onClick={() =>
                                            handleStarterPromptClick(prompt)
                                        }
                                        className="cursor-pointer rounded-full border border-[#DBE3E3] bg-white px-3 py-1.5 text-xs text-[#42595E] transition-colors hover:bg-[#F0F5F5] hover:text-[#002429]"
                                    >
                                        {prompt}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <Fragment>
                        {activeChatTab === 'changes' ? (
                            <ScrollArea className="h-full min-h-0 flex-1">
                                <ChangesPanel />
                            </ScrollArea>
                        ) : null}
                        <ScrollArea
                            ref={transcriptScrollAreaRef}
                            viewportClassName="[&>div]:!block"
                            className={cn(
                                'flex h-full min-h-0 flex-1 flex-col',
                                activeChatTab === 'changes' && 'hidden',
                            )}
                        >
                            <div
                                ref={transcriptContentRef}
                                className={cn(
                                    'space-y-4 overflow-x-hidden p-4',
                                    CHAT_COLUMN_CLASS,
                                    // Grey the history out so an expired chat
                                    // reads as inactive until it is picked back up.
                                    isSelectedConversationExpired &&
                                        'opacity-60',
                                )}
                            >
                                <PhoebeAgentTimelineEntries
                                    entries={
                                        timelineEntriesWithRunDebriefAcknowledgement
                                    }
                                    actionStateEntries={
                                        legacyDisplayedTranscript
                                    }
                                    showThinkingTips={thinkingTipsEnabled}
                                    renderMessageFeedback={
                                        renderMessageFeedback
                                    }
                                    debugPanelOpen={debugPanelOpen}
                                    latestAnimatedAssistantMessageId={
                                        latestAnimatedAssistantMessageId
                                    }
                                    latestAnimatedThoughtId={
                                        latestAnimatedThoughtId
                                    }
                                    onTypewriterProgress={
                                        handleTypewriterProgress
                                    }
                                    onCaregiverClick={setSelectedCaregiverId}
                                    onClientClick={setSelectedClientId}
                                    onAssistantPromptAction={
                                        assistantPromptActionHandler
                                    }
                                    onRetryAfterRunError={
                                        retryAfterRunErrorHandler
                                    }
                                    isRetryDisabled={
                                        isRetryAfterRunErrorDisabled
                                    }
                                    shiftSuggestionTabs={shiftSuggestionTabs}
                                    decidingCallIds={decidingCallIds}
                                    answeringQuestionCallIds={
                                        answeringQuestionCallIds
                                    }
                                    onToolDecision={
                                        canSendInSelectedConversation
                                            ? decideToolCall
                                            : undefined
                                    }
                                    onQuestionAnswer={
                                        canSendInSelectedConversation
                                            ? submitQuestionAnswer
                                            : undefined
                                    }
                                    agentThinking={
                                        !!conversation?.agent_thinking &&
                                        streamOverlayAssistantMessages.length ===
                                            0
                                    }
                                    streamOverlayRun={streamOverlayRun}
                                    shouldUsePhoebeEventRead={
                                        shouldUsePhoebeEventRead
                                    }
                                    currentRunEventStreamIsGenerating={
                                        currentRunEventStreamIsGenerating
                                    }
                                    isActiveRunWithoutStream={
                                        isActiveRunWithoutStream
                                    }
                                    manualLiveStatus={manualLiveStatus}
                                    isPriorAssistantMessageAnimating={
                                        isPriorAssistantMessageAnimating
                                    }
                                    shouldShowWorkflowActivitySurface={
                                        shouldShowWorkflowActivitySurface
                                    }
                                    timelineNowMs={timelineNowMs}
                                    expandedLiveRunId={expandedLiveRunId}
                                    onExpandedLiveRunIdChange={
                                        setExpandedLiveRunId
                                    }
                                    autoExpandDismissedRunId={
                                        autoExpandDismissedRunId
                                    }
                                    onAutoExpandDismissedRunIdChange={
                                        setAutoExpandDismissedRunId
                                    }
                                    showGeneralOutreachProgressCards={
                                        showGeneralOutreachProgressCards
                                    }
                                    showInlineWorkflowActivitySurface={
                                        showInlineWorkflowActivitySurface
                                    }
                                    showCaregiverDecisionProgress={false}
                                    subagentDraftProgress={
                                        subagentDraftProgress
                                    }
                                    shiftActionPolicy={shiftActionPolicy}
                                    showTrainingModeQuestionContext={
                                        showTrainingModeQuestionContext
                                    }
                                    trainingModeContextReady={
                                        trainingModeContextReady
                                    }
                                />
                                {pendingOutreachProgressSeed !== null &&
                                !currentRunEventStreamIsGenerating &&
                                clientSurface === 'extension' ? (
                                    <GeneralOutreachProgressCard
                                        seed={pendingOutreachProgressSeed}
                                        nested={false}
                                        onCaregiverClick={
                                            setSelectedCaregiverId
                                        }
                                    />
                                ) : null}
                                {!shouldShowLiveArtifactsInline &&
                                liveStatusForDisplay &&
                                !hasActiveCompletedRunInTimeline ? (
                                    showGeneralOutreachProgressCards ||
                                    showInlineWorkflowActivitySurface ? (
                                        streamOverlayRun ? (
                                            <div className="space-y-2">
                                                {derivePhoebeShimmerLabel(
                                                    streamOverlayRun,
                                                    false,
                                                ) ? (
                                                    <PhoebeShimmerStatus
                                                        label={derivePhoebeShimmerLabel(
                                                            streamOverlayRun,
                                                            false,
                                                        )!}
                                                        tip={derivePhoebeThinkingTip(
                                                            {
                                                                enabled:
                                                                    thinkingTipsEnabled,
                                                                isWorkflow:
                                                                    shouldShowWorkflowActivitySurface,
                                                                run: streamOverlayRun,
                                                                nowMs: timelineNowMs,
                                                                tipIndex:
                                                                    thinkingTipIndex,
                                                            },
                                                        )}
                                                    />
                                                ) : null}
                                                <LiveRunTree
                                                    run={streamOverlayRun}
                                                    showDurationLabels={
                                                        shouldShowWorkflowActivitySurface
                                                    }
                                                    animatedReasoningNodeId={
                                                        latestAnimatedThoughtId
                                                    }
                                                    animatedResponseNodeId={
                                                        latestAnimatedAssistantMessageId
                                                    }
                                                    onTypewriterProgress={
                                                        handleTypewriterProgress
                                                    }
                                                    onCaregiverClick={
                                                        setSelectedCaregiverId
                                                    }
                                                    onClientClick={
                                                        setSelectedClientId
                                                    }
                                                    showGeneralOutreachProgressCards={
                                                        showGeneralOutreachProgressCards
                                                    }
                                                    hideTraceTitles={
                                                        showGeneralOutreachProgressCards ||
                                                        showInlineWorkflowActivitySurface
                                                    }
                                                    subagentDraftProgress={
                                                        subagentDraftProgress
                                                    }
                                                    navigateOnDraftedOutreach={
                                                        true
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <PhoebeShimmerStatus
                                                label="Thinking"
                                                tip={derivePhoebeThinkingTip({
                                                    enabled:
                                                        thinkingTipsEnabled,
                                                    isWorkflow:
                                                        shouldShowWorkflowActivitySurface,
                                                    seed:
                                                        selectedConversationId ??
                                                        target.kind,
                                                    nowMs: timelineNowMs,
                                                    tipIndex: thinkingTipIndex,
                                                })}
                                            />
                                        )
                                    ) : (
                                        <div className="space-y-2">
                                            <LiveStatusRow
                                                status={liveStatusForDisplay}
                                                isExpandable={
                                                    isLiveRunExpandable
                                                }
                                                isExpanded={isLiveRunExpanded}
                                                onToggle={toggleLiveRunTree}
                                                showSpinner={
                                                    shouldShowLiveStatusSpinner
                                                }
                                                tip={derivePhoebeThinkingTip({
                                                    enabled:
                                                        thinkingTipsEnabled,
                                                    isWorkflow:
                                                        shouldShowWorkflowActivitySurface,
                                                    run: streamOverlayRun,
                                                    seed:
                                                        selectedConversationId ??
                                                        target.kind,
                                                    nowMs: timelineNowMs,
                                                    tipIndex: thinkingTipIndex,
                                                })}
                                            />
                                            {isLiveRunExpanded &&
                                            streamOverlayRun ? (
                                                <React.Fragment>
                                                    <LiveRunTree
                                                        run={streamOverlayRun}
                                                        showDurationLabels={
                                                            shouldShowWorkflowActivitySurface
                                                        }
                                                        animatedReasoningNodeId={
                                                            latestAnimatedThoughtId
                                                        }
                                                        animatedResponseNodeId={
                                                            latestAnimatedAssistantMessageId
                                                        }
                                                        onTypewriterProgress={
                                                            handleTypewriterProgress
                                                        }
                                                        onCaregiverClick={
                                                            setSelectedCaregiverId
                                                        }
                                                        onClientClick={
                                                            setSelectedClientId
                                                        }
                                                        showGeneralOutreachProgressCards={
                                                            showGeneralOutreachProgressCards
                                                        }
                                                        hideTraceTitles={
                                                            showGeneralOutreachProgressCards ||
                                                            showInlineWorkflowActivitySurface
                                                        }
                                                        subagentDraftProgress={
                                                            subagentDraftProgress
                                                        }
                                                        navigateOnDraftedOutreach={
                                                            true
                                                        }
                                                    />
                                                </React.Fragment>
                                            ) : null}
                                        </div>
                                    )
                                ) : null}
                                {shouldShowLiveArtifactsInline
                                    ? streamOverlayInlineEntries.map(
                                          (entry) => {
                                              switch (entry.kind) {
                                                  case 'message':
                                                      return (
                                                          <MessageBubble
                                                              key={`stream-response-${entry.id}`}
                                                              message={entry}
                                                              renderMessageFeedback={
                                                                  renderMessageFeedback
                                                              }
                                                              onCaregiverClick={
                                                                  setSelectedCaregiverId
                                                              }
                                                              onClientClick={
                                                                  setSelectedClientId
                                                              }
                                                              onAssistantPromptAction={
                                                                  assistantPromptActionHandler
                                                              }
                                                              onRetryAfterRunError={
                                                                  retryAfterRunErrorHandler
                                                              }
                                                              isRetryDisabled={
                                                                  isRetryAfterRunErrorDisabled
                                                              }
                                                              shiftSuggestionTabs={
                                                                  tabsRenderedInTranscript
                                                                      ? null
                                                                      : shiftSuggestionTabs
                                                              }
                                                              shiftActionPolicy={
                                                                  shiftActionPolicy
                                                              }
                                                          />
                                                      );
                                                  case 'update-playbook-artifact':
                                                      return (
                                                          <UpdatePlaybookArtifactCard
                                                              key={entry.id}
                                                              artifact={
                                                                  entry.artifact
                                                              }
                                                          />
                                                      );
                                                  case 'save-note-artifact':
                                                      return (
                                                          <SaveNoteArtifactCard
                                                              key={entry.id}
                                                              artifact={
                                                                  entry.artifact
                                                              }
                                                              staggerIndex={
                                                                  entry.staggerIndex ??
                                                                  0
                                                              }
                                                          />
                                                      );
                                                  case 'structured-report-artifact':
                                                      return (
                                                          <StructuredReportArtifactPill
                                                              key={entry.id}
                                                              artifact={{
                                                                  id: entry.id,
                                                                  toolName:
                                                                      entry.toolName,
                                                                  title: humanizeReportToolName(
                                                                      entry.toolName,
                                                                  ),
                                                                  columns:
                                                                      entry.columns,
                                                                  rows: entry.rows,
                                                                  summary:
                                                                      entry.summary,
                                                              }}
                                                          />
                                                      );
                                                  default:
                                                      return null;
                                              }
                                          },
                                      )
                                    : streamOverlayAssistantMessages.map(
                                          (message) => (
                                              <MessageBubble
                                                  key={`stream-response-${message.id}`}
                                                  message={message}
                                                  renderMessageFeedback={
                                                      renderMessageFeedback
                                                  }
                                                  onCaregiverClick={
                                                      setSelectedCaregiverId
                                                  }
                                                  onClientClick={
                                                      setSelectedClientId
                                                  }
                                                  onAssistantPromptAction={
                                                      assistantPromptActionHandler
                                                  }
                                                  onRetryAfterRunError={
                                                      retryAfterRunErrorHandler
                                                  }
                                                  isRetryDisabled={
                                                      isRetryAfterRunErrorDisabled
                                                  }
                                                  shiftSuggestionTabs={
                                                      tabsRenderedInTranscript
                                                          ? null
                                                          : shiftSuggestionTabs
                                                  }
                                                  shiftActionPolicy={
                                                      shiftActionPolicy
                                                  }
                                              />
                                          ),
                                      )}
                                {shouldShowLiveArtifactsInline &&
                                liveStatusForDisplay &&
                                !hasActiveCompletedRunInTimeline ? (
                                    showGeneralOutreachProgressCards ||
                                    showInlineWorkflowActivitySurface ? (
                                        streamOverlayRun ? (
                                            <div className="space-y-2">
                                                {derivePhoebeShimmerLabel(
                                                    streamOverlayRun,
                                                    false,
                                                ) ? (
                                                    <PhoebeShimmerStatus
                                                        label={derivePhoebeShimmerLabel(
                                                            streamOverlayRun,
                                                            false,
                                                        )!}
                                                        tip={derivePhoebeThinkingTip(
                                                            {
                                                                enabled:
                                                                    thinkingTipsEnabled,
                                                                isWorkflow:
                                                                    shouldShowWorkflowActivitySurface,
                                                                run: streamOverlayRun,
                                                                nowMs: timelineNowMs,
                                                                tipIndex:
                                                                    thinkingTipIndex,
                                                            },
                                                        )}
                                                    />
                                                ) : null}
                                                <LiveRunTree
                                                    run={streamOverlayRun}
                                                    showDurationLabels={
                                                        shouldShowWorkflowActivitySurface
                                                    }
                                                    animatedReasoningNodeId={
                                                        latestAnimatedThoughtId
                                                    }
                                                    animatedResponseNodeId={
                                                        latestAnimatedAssistantMessageId
                                                    }
                                                    onTypewriterProgress={
                                                        handleTypewriterProgress
                                                    }
                                                    onCaregiverClick={
                                                        setSelectedCaregiverId
                                                    }
                                                    onClientClick={
                                                        setSelectedClientId
                                                    }
                                                    showGeneralOutreachProgressCards={
                                                        showGeneralOutreachProgressCards
                                                    }
                                                    hideTraceTitles={
                                                        showGeneralOutreachProgressCards ||
                                                        showInlineWorkflowActivitySurface
                                                    }
                                                    subagentDraftProgress={
                                                        subagentDraftProgress
                                                    }
                                                    navigateOnDraftedOutreach={
                                                        true
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <PhoebeShimmerStatus
                                                label="Thinking"
                                                tip={derivePhoebeThinkingTip({
                                                    enabled:
                                                        thinkingTipsEnabled,
                                                    isWorkflow:
                                                        shouldShowWorkflowActivitySurface,
                                                    seed:
                                                        selectedConversationId ??
                                                        target.kind,
                                                    nowMs: timelineNowMs,
                                                    tipIndex: thinkingTipIndex,
                                                })}
                                            />
                                        )
                                    ) : (
                                        <div className="space-y-2">
                                            <LiveStatusRow
                                                status={liveStatusForDisplay}
                                                isExpandable={
                                                    isLiveRunExpandable
                                                }
                                                isExpanded={isLiveRunExpanded}
                                                onToggle={toggleLiveRunTree}
                                                showSpinner={
                                                    shouldShowLiveStatusSpinner
                                                }
                                                tip={derivePhoebeThinkingTip({
                                                    enabled:
                                                        thinkingTipsEnabled,
                                                    isWorkflow:
                                                        shouldShowWorkflowActivitySurface,
                                                    run: streamOverlayRun,
                                                    seed:
                                                        selectedConversationId ??
                                                        target.kind,
                                                    nowMs: timelineNowMs,
                                                    tipIndex: thinkingTipIndex,
                                                })}
                                            />
                                            {isLiveRunExpanded &&
                                            streamOverlayRun ? (
                                                <React.Fragment>
                                                    <LiveRunTree
                                                        run={streamOverlayRun}
                                                        showDurationLabels={
                                                            shouldShowWorkflowActivitySurface
                                                        }
                                                        animatedReasoningNodeId={
                                                            latestAnimatedThoughtId
                                                        }
                                                        animatedResponseNodeId={
                                                            latestAnimatedAssistantMessageId
                                                        }
                                                        onTypewriterProgress={
                                                            handleTypewriterProgress
                                                        }
                                                        onCaregiverClick={
                                                            setSelectedCaregiverId
                                                        }
                                                        onClientClick={
                                                            setSelectedClientId
                                                        }
                                                        showGeneralOutreachProgressCards={
                                                            showGeneralOutreachProgressCards
                                                        }
                                                        hideTraceTitles={
                                                            showGeneralOutreachProgressCards ||
                                                            showInlineWorkflowActivitySurface
                                                        }
                                                        subagentDraftProgress={
                                                            subagentDraftProgress
                                                        }
                                                        navigateOnDraftedOutreach={
                                                            true
                                                        }
                                                    />
                                                </React.Fragment>
                                            ) : null}
                                        </div>
                                    )
                                ) : null}
                                <MonitoringConfigurationCardForRun
                                    run={activeMonitoringRun}
                                    controls={monitoringControls}
                                    monitoringScheduleMetadata={
                                        monitoringScheduleMetadata
                                    }
                                />
                                {shouldShowPhoebeFooterStatus ? (
                                    <PhoebeFooterStatusRow
                                        status={phoebeFooterStatus}
                                        onRetryAfterRunError={
                                            retryAfterRunErrorHandler
                                        }
                                        onStopMonitoring={
                                            showGeneralOutreachProgressCards ||
                                            !canSendInSelectedConversation
                                                ? undefined
                                                : () => {
                                                      void stopRun();
                                                  }
                                        }
                                        isRetryDisabled={
                                            isRetryAfterRunErrorDisabled
                                        }
                                        isStopMonitoringDisabled={isStopping}
                                    />
                                ) : null}
                                {showRunDebrief ? (
                                    <RunDebriefCard
                                        decisions={runDebrief?.decisions ?? []}
                                        disabled={runDebriefSaving}
                                        promptContent={
                                            recommendationReviewPromptContent ??
                                            undefined
                                        }
                                        submitted={
                                            runDebriefOutcome === 'submitted'
                                        }
                                        onSubmit={(submission) => {
                                            void handleRunDebriefSubmit(
                                                submission,
                                            );
                                        }}
                                        onSkip={() => {
                                            void handleRunDebriefSkip();
                                        }}
                                    />
                                ) : null}
                            </div>
                        </ScrollArea>
                        {activeChatTab === 'chat' && showScrollToBottom ? (
                            <ScrollToBottomButton
                                onClick={() =>
                                    scrollTranscriptToBottom('smooth')
                                }
                            />
                        ) : null}
                    </Fragment>
                )}
            </div>
            {!readOnly &&
                !savedRunVersionFailedToLoad &&
                !showCenteredEmptyComposer && (
                    <div className="flex flex-col">
                        {showDockedEmptyState &&
                        isReviewingRuns &&
                        showTrainingModeBadge ? (
                            // The review card holds the thumbs-down comment box, so
                            // it must stay mounted while the mobile keyboard is
                            // open — hiding it would tear down an in-progress note.
                            // The starter prompts have no input, so they still hide
                            // to give the keyboard room. Gated on
                            // showTrainingModeBadge too: this is training-only, and
                            // isReviewingRuns alone can outlive an org switch.
                            <ReviewMyRunsCard
                                organizationId={organizationId}
                                onDismiss={() => setIsReviewingRuns(false)}
                            />
                        ) : showDockedEmptyState &&
                          !isCalloutMode &&
                          !mobileKeyboardOpen ? (
                            <div className={cn('px-4', CHAT_COLUMN_CLASS)}>
                                <AgentSidebarStarterRows
                                    onSeedPrompt={handleDockedSeedPrompt}
                                    showReviewMyRuns={showTrainingModeBadge}
                                    onReviewMyRuns={() =>
                                        setIsReviewingRuns(true)
                                    }
                                />
                            </div>
                        ) : showDockedEmptyState && !mobileKeyboardOpen ? (
                            <AgentSidebarEmptyState
                                variant={isCalloutMode ? 'callout' : 'general'}
                                onSeedPrompt={handleDockedSeedPrompt}
                                showReviewMyRuns={showTrainingModeBadge}
                                onReviewMyRuns={() => setIsReviewingRuns(true)}
                            />
                        ) : null}
                        <div
                            className={cn('px-4 pt-2 pb-3', CHAT_COLUMN_CLASS)}
                        >
                            {composerForm}
                        </div>
                    </div>
                )}
            {organizationId ? (
                <CaregiverDetailModal
                    isOpen={selectedCaregiverId !== null}
                    onClose={() => setSelectedCaregiverId(null)}
                    organizationId={organizationId}
                    caregiverId={selectedCaregiverId ?? undefined}
                />
            ) : null}
            {organizationId && selectedClientId !== null ? (
                <ClientDetailModal
                    organizationId={organizationId}
                    client={selectedClient ?? null}
                    isLoading={
                        selectedClientIsLoading || selectedClient === undefined
                    }
                    open={selectedClientId !== null}
                    onOpenChange={(open) => {
                        if (!open) {
                            setSelectedClientId(null);
                        }
                    }}
                    onClientUpdated={() => void mutateSelectedClient()}
                />
            ) : null}
            <CaregiverShiftDetailsDialog
                shift={
                    selectedShiftId !== null ? (selectedShift ?? null) : null
                }
                open={selectedShiftId !== null}
                isLoading={
                    selectedShiftIsLoading ||
                    (selectedShiftId !== null &&
                        selectedShift === undefined &&
                        !selectedShiftError)
                }
                loadFailed={Boolean(selectedShiftError)}
                onRetry={() => void mutateSelectedShift()}
                viewShiftInNewTab={clientSurface === 'extension'}
                timezone={timezone}
                onClose={() => setSelectedShiftId(null)}
            />
            <RestartAnalysisConfirmModal
                isOpen={isRestartModalOpen}
                onClose={() => setIsRestartModalOpen(false)}
                onConfirm={handleRestartAnalysis}
            />
            <RestartAnalysisConfirmModal
                isOpen={isRunnerResetModalOpen}
                onClose={() => setIsRunnerResetModalOpen(false)}
                onConfirm={handleResetOutreachRunner}
                title="Start a fresh Phoebe chat?"
                description="Phoebe stops what she’s doing on this outreach and picks it up again in a new chat. This chat stays readable, and the outreach itself is untouched."
                confirmLabel="Start fresh"
                reportsOwnOutcome={true}
            />
        </div>
    );

    // In fullscreen mode the chat panel renders only its own content;
    // the conversation list lives in the main app sidebar's Sessions
    // section, matching the homepage layout. This removes the previous
    // in-chat rail that created a duplicate sidebar.
    return (
        <DisplayNamesLastFirstContext.Provider value={displayNamesLastFirst}>
            <PhoebeChatOrganizationScopeContext.Provider
                value={chatOrganizationScope}
            >
                <DynamicWidgetSupportActionsContext.Provider
                    value={dynamicWidgetSupportActions}
                >
                    <DynamicWidgetBuildContextContext.Provider value={null}>
                        <ExtensionSurfaceProvider
                            value={clientSurface === 'extension'}
                        >
                            <EntityDetailClickProvider
                                value={entityDetailClickHandlers}
                            >
                                <SavedNoteOutcomesProvider
                                    organizationId={organizationId}
                                    agentConversationId={
                                        shouldUseRunNativeConversation
                                            ? null
                                            : (conversation?.id ?? null)
                                    }
                                    agentRunId={
                                        shouldUseRunNativeConversation
                                            ? (conversation?.id ?? null)
                                            : null
                                    }
                                    isRunActive={Boolean(
                                        conversation?.agent_thinking,
                                    )}
                                    isInTrainingMode={
                                        trainingMode.isInTrainingMode
                                    }
                                    isAgentNotesArmed={agentV3Enabled}
                                >
                                    {chatBody}
                                </SavedNoteOutcomesProvider>
                            </EntityDetailClickProvider>
                        </ExtensionSurfaceProvider>
                    </DynamicWidgetBuildContextContext.Provider>
                </DynamicWidgetSupportActionsContext.Provider>
            </PhoebeChatOrganizationScopeContext.Provider>
        </DisplayNamesLastFirstContext.Provider>
    );
}
